import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Heart, Plus, Eye, Loader2, CheckCircle, XCircle, Upload, FileText, ClipboardCheck, Trash2, Search, Edit, Pencil, FilePlus, MoreHorizontal, Settings, X, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Tag, Undo2, RotateCw, RotateCcw, Download, FileDown, Check, Crop, Link2, BookOpen, GraduationCap, Sparkles, Banknote, Wallet, PercentSquare, HandHelping, BarChart3, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import * as XLSX from 'xlsx';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { TreatmentSupportRequest, TreatmentSupportStatus, MedicalReport, DonationEntry, CaseUpdate, TreatmentIdType, TreatmentGender, TreatmentMaritalStatus, TreatmentEducationLevel, TreatmentWorkSector, TreatmentHousingType } from '@/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';
import { backfillCaseNumbers, assertCategoryMatch } from '@/lib/firebase/queries/treatmentSupport';
import { MedicalReportUploader, PendingReport } from '@/components/shared/MedicalReportUploader';
import { InvoiceUploader, PendingInvoice } from '@/components/shared/InvoiceUploader';
import { ProgressReportUploader, type PendingProgressReport } from '@/components/shared/ProgressReportUploader';
import { canStartTreatment, shouldOfferStartTreatment } from '@/lib/services/treatmentWorkflow';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { HijriDatePicker } from '@/components/shared/HijriDatePicker';
import { CategorySelect } from '@/components/shared/CategorySelect';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { useTreatmentCategories, addTreatmentCategory, updateTreatmentCategory, deleteTreatmentCategory } from '@/lib/firebase/queries/treatmentCategories';
import { useLinks, addLink, updateLink, deleteLink } from '@/lib/firebase/queries/links';
import { useDonations, addDonation, updateDonation, deleteDonation } from '@/lib/firebase/queries/donations';
import { DonationTransaction } from '@/types';
import { useCashbacks, updateCashback } from '@/lib/firebase/queries/cashbacks';
import { useMarketingPayments, addMarketingPayment, updateMarketingPayment, deleteMarketingPayment } from '@/lib/firebase/queries/marketingPayments';
import { useVatRefunds, addVatRefund, updateVatRefund, deleteVatRefund } from '@/lib/firebase/queries/vatRefunds';
import { toast } from 'sonner';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db, functions } from '@/lib/firebase/config';
import { doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { verifyLinkData, migrateLinkData } from '@/lib/firebase/scripts/verifyLinkData';
import { canReduceReceived, computeAggregateDonationWrite, findAggregateDonation } from '@/lib/linkFinancials';

const STATUS_COLORS: Record<TreatmentSupportStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-orange-100 text-orange-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  approved_treatment: 'bg-teal-100 text-teal-800',
  treatment_started: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  pending_invoices: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

const CATEGORY_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
  'bg-orange-100 text-orange-800',
  'bg-cyan-100 text-cyan-800',
  'bg-pink-100 text-pink-800',
  'bg-lime-100 text-lime-800',
  'bg-fuchsia-100 text-fuchsia-800',
  'bg-sky-100 text-sky-800',
];

interface AdminHelpManagementProps {
  titleOverride?: string;
  descriptionOverride?: string;
  readOnly?: boolean;
}

export function AdminHelpManagement({ titleOverride, descriptionOverride, readOnly = false }: AdminHelpManagementProps = {}) {
  const { t, dir, language } = useLanguage();
  const { currentUser } = useAuth();
  const ALLOWED_TOP_TABS = ['pending', 'approval', 'approved', 'approvedTreatment', 'treatmentStarted', 'treated', 'completed', 'rejected'] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const topTab = (ALLOWED_TOP_TABS as readonly string[]).includes(urlTab ?? '') ? (urlTab as string) : 'pending';
  const handleTopTabChange = (newTab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', newTab);
    setSearchParams(next, { replace: false });
  };
  const { requests, loading, addRequest, updateRequest, deleteRequest, refreshRequests } = useTreatmentSupportRequests();
  const { categories } = useTreatmentCategories();
  const { providers } = useMedicalProviders();
  const { links } = useLinks();
  const { donations } = useDonations();
  console.log(`[perf] AdminHelpManagement render — donations.length=${donations.length}`);
  const donationSumByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, number> = {};
    for (const d of donations) m[d.linkId] = (m[d.linkId] || 0) + (d.amount || 0);
    console.log(`[perf] donationSumByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const donationsByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, DonationTransaction[]> = {};
    for (const d of donations) (m[d.linkId] = m[d.linkId] || []).push(d);
    for (const k in m) m[k].sort((a, b) => b.date.getTime() - a.date.getTime());
    console.log(`[perf] donationsByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const donationStartByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, Date> = {};
    for (const d of donations) if (!m[d.linkId] || d.date < m[d.linkId]) m[d.linkId] = d.date;
    console.log(`[perf] donationStartByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const activeProviders = providers.filter(p => p.isActive);
  const activeCategories = categories.filter(c => c.isActive);

  // Cashback state
  const { cashbacks, loading: cashbacksLoading } = useCashbacks();
  const { marketingPayments } = useMarketingPayments();
  const { vatRefunds } = useVatRefunds();
  // Marketing/VAT ledger dialog state
  const [ledgerDialog, setLedgerDialog] = useState<{ kind: 'marketing' | 'vat'; id: string | null } | null>(null);
  const [ledgerForm, setLedgerForm] = useState({ amount: '', date: '', notes: '', status: 'pending' });
  const [ledgerReceiptFile, setLedgerReceiptFile] = useState<File | null>(null);
  const [isSubmittingLedger, setIsSubmittingLedger] = useState(false);
  const [isCashbackReviewOpen, setIsCashbackReviewOpen] = useState(false);
  const [selectedCashback, setSelectedCashback] = useState<any>(null);
  const [cashbackDecision, setCashbackDecision] = useState<{ decision: 'confirm' | 'reject' | ''; adminNotes: string }>({ decision: '', adminNotes: '' });
  const [isSubmittingCashback, setIsSubmittingCashback] = useState(false);

  // Usage map keyed by linkId: actualReceived seeded from links collection,
  // allocations accumulated from case donationEntries.
  const linkUsageMap = useMemo(() => {
    const t0 = performance.now();
    const map: Record<string, { actualReceived: number; count: number; totalAllocated: number; cases: { id: string; allocated: number }[] }> = {};
    for (const l of links) {
      map[l.id] = { actualReceived: donationSumByLink[l.id] || 0, count: 0, totalAllocated: 0, cases: [] };
    }
    for (const req of requests) {
      if (!req.donationEntries) continue;
      for (const entry of req.donationEntries) {
        if (!entry.linkId || !map[entry.linkId]) continue;
        map[entry.linkId].totalAllocated += entry.amount || 0;
        map[entry.linkId].count += 1;
        map[entry.linkId].cases.push({ id: req.id, allocated: entry.amount || 0 });
      }
    }
    console.log(`[perf] linkUsageMap: ${requests.length} requests × ${links.length} links in ${(performance.now() - t0).toFixed(1)}ms`);
    return map;
  }, [requests, links, donationSumByLink]);

  const dateLocale = language === 'ar' ? ar : enUS;
  const formatLocalDate = (date: Date, fmt: string = 'PPP') => {
    if (!date || isNaN(date.getTime())) return '—';
    const formatted = format(date, fmt, { locale: dateLocale });
    return formatted.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  };

  const translateRole = (role: string) => {
    const key = `common.roles.${role}` as const;
    const translated = t(key);
    return translated !== key ? translated : role;
  };
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Settings
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMonthlyCostOpen, setIsMonthlyCostOpen] = useState(false);
  const [monthlyReportYear, setMonthlyReportYear] = useState<number>(() => new Date().getFullYear());
  const [settingsTab, setSettingsTab] = useState('general');
  const [autoApproveEnabled, setAutoApproveEnabled] = useState(false);
  const [coordCanDeleteAssignments, setCoordCanDeleteAssignments] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [marketingX, setMarketingX] = useState(0.4);
  const [marketingY, setMarketingY] = useState(1);
  const [migrationLog, setMigrationLog] = useState<string[]>([]);
  const [isMigrating, setIsMigrating] = useState(false);

  // Category management in settings
  const [isCategoryEditOpen, setIsCategoryEditOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{ id: string; name: string; nameAr: string; description: string; isActive: boolean } | null>(null);
  const [isCategorySubmitting, setIsCategorySubmitting] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', nameAr: '', description: '', isActive: true });
  
  // View switcher
  const [activeView, setActiveView] = useState<'cases' | 'links' | 'awareness' | 'training' | 'marketing' | 'vat' | 'cashbacks'>('cases');
  const [linksSearchQuery, setLinksSearchQuery] = useState('');
  const [linksSortColumn, setLinksSortColumn] = useState<string>('caseNumber');
  const [linksSortDirection, setLinksSortDirection] = useState<'asc' | 'desc'>('asc');

  const calcLinkAvailable = (received: number) => {
    // VAT on the marketing share is refundable — it doesn't reduce what
    // the case can spend. Mirrors the allLinks.map formula used by the
    // Projects List display.
    const ms = received * marketingX / ((marketingX + marketingY) || 1);
    return Math.floor(received - Math.ceil(ms));
  };

  const getNetMedicalCost = (req: any): number => {
    const invoiceTotal = Array.isArray(req.invoices)
      ? req.invoices.reduce(
          (s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0),
          0,
        )
      : 0;
    return invoiceTotal > 0 ? invoiceTotal : (req.cost || 0);
  };

  const getEffectiveCost = (req: any) => {
    // Effective cost = gross donations needed so that, after the donation
    // link's marketing share is deducted, the NET available for the case
    // equals the medical cost. Available-share per donated riyal is
    // Y/(X+Y), so the gross multiplier is its inverse: (X+Y)/Y.
    // For X=46 Y=54 this is ×1.852 (40,000 → 74,074). Applied to all
    // cases regardless of nationality.
    const grossMult = (marketingX + marketingY) / (marketingY || 1);
    const base = (req.invoices && req.invoices.length > 0)
      ? req.invoices.reduce((s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0) || (req.cost || 0)
      : (req.cost || 0);
    return Math.ceil(base * grossMult);
  };

  const monthlyCostRows = useMemo(() => {
    // Pure medical cost (no marketing margin) — invoice totals if any,
    // else the case's declared cost.
    const pureCost = (r: any): number => {
      if (r.invoices && r.invoices.length > 0) {
        const sum = r.invoices.reduce(
          (s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0),
          0,
        );
        if (sum > 0) return sum;
      }
      return r.cost || 0;
    };
    const buckets = new Map<string, { ts: number; count: number; total: number; invoices: number }>();
    for (const r of requests) {
      const created = r.createdAt instanceof Date ? r.createdAt : (r.createdAt ? new Date(r.createdAt as any) : null);
      if (!created || Number.isNaN(created.getTime())) continue;
      const y = created.getFullYear();
      const m = created.getMonth();
      const key = `${y}-${String(m + 1).padStart(2, '0')}`;
      const existing = buckets.get(key) || { ts: new Date(y, m, 1).getTime(), count: 0, total: 0, invoices: 0 };
      existing.count += 1;
      existing.total += pureCost(r);
      existing.invoices += Array.isArray((r as any).invoices)
        ? (r as any).invoices.reduce(
            (s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0),
            0,
          )
        : 0;
      buckets.set(key, existing);
    }
    const monthName = (ts: number) => formatLocalDate(new Date(ts), 'MMM yyyy');
    return Array.from(buckets.entries())
      .map(([key, v]) => ({ key, label: monthName(v.ts), ts: v.ts, count: v.count, total: v.total, invoices: v.invoices }))
      .sort((a, b) => b.ts - a.ts);
  }, [requests]);
  const monthlyGrandTotal = useMemo(
    () => monthlyCostRows.reduce((s, r) => s + r.total, 0),
    [monthlyCostRows],
  );
  const monthlyCaseGrandTotal = useMemo(
    () => monthlyCostRows.reduce((s, r) => s + r.count, 0),
    [monthlyCostRows],
  );
  const monthlyReportYears = useMemo(() => {
    const set = new Set<number>();
    for (const r of monthlyCostRows) set.add(new Date(r.ts).getFullYear());
    return Array.from(set).sort((a, b) => a - b);
  }, [monthlyCostRows]);
  const filteredMonthlyRows = useMemo(() => {
    // Always emit 12 rows (Jan–Dec) for the selected year, filling months
    // with no cases as zeros so the year reads as a complete calendar.
    type Row = { key: string; label: string; ts: number; count: number; total: number; invoices: number };
    const haveByKey = new Map<string, Row>();
    for (const r of monthlyCostRows) {
      if (new Date(r.ts).getFullYear() === monthlyReportYear) haveByKey.set(r.key, r);
    }
    const out: Row[] = [];
    for (let m = 0; m < 12; m++) {
      const key = `${monthlyReportYear}-${String(m + 1).padStart(2, '0')}`;
      const existing = haveByKey.get(key);
      if (existing) {
        out.push(existing);
      } else {
        const ts = new Date(monthlyReportYear, m, 1).getTime();
        out.push({
          key,
          label: formatLocalDate(new Date(ts), 'MMM yyyy'),
          ts,
          count: 0,
          total: 0,
          invoices: 0,
        });
      }
    }
    return out.sort((a, b) => a.ts - b.ts);
  }, [monthlyCostRows, monthlyReportYear]);
  const filteredMonthlyTotalCost = useMemo(
    () => filteredMonthlyRows.reduce((s, r) => s + r.total, 0),
    [filteredMonthlyRows],
  );
  const filteredMonthlyTotalCount = useMemo(
    () => filteredMonthlyRows.reduce((s, r) => s + r.count, 0),
    [filteredMonthlyRows],
  );
  const filteredMonthlyTotalInvoices = useMemo(
    () => filteredMonthlyRows.reduce((s, r) => s + r.invoices, 0),
    [filteredMonthlyRows],
  );

  const allLinks = useMemo(() => {
    const t0 = performance.now();
    const grouped: Record<string, {
      _key: string;
      id: string;
      platform: string;
      platformCaseNumber: string;
      url?: string;
      donorName?: string;
      treatmentCategoryId?: string;
      linkTitle?: string;
      actualReceived: number;
      marketingShare: number;
      refundableVAT: number;
      nonSaudiAllocated: number;
      caseInvoiceVAT: number;
      cashbackAmount: number;
      availableForCases: number;
      totalAllocated: number;
      remaining: number;
      addedAt: any;
      cases: { caseId: string; caseNumber?: number; nameAr: string; nameEn: string; idNumber: string; allocated: number; status: string }[];
    }> = {};

    // Seed one group per link (keyed by link.id) from the links collection.
    for (const l of links) {
      grouped[l.id] = {
        _key: l.id,
        id: l.id,
        platform: l.platform,
        platformCaseNumber: l.caseNumber,
        url: l.url,
        donorName: l.donorName,
        treatmentCategoryId: l.treatmentCategoryId,
        linkTitle: l.linkTitle,
        actualReceived: donationSumByLink[l.id] || 0,
        marketingShare: 0,
        refundableVAT: 0,
        nonSaudiAllocated: 0,
        caseInvoiceVAT: 0,
        cashbackAmount: 0,
        availableForCases: 0,
        totalAllocated: 0,
        remaining: 0,
        addedAt: l.addedAt,
        cases: [],
      };
    }

    for (const r of (requests || [])) {
      if (!r.donationEntries) continue;
      for (const entry of r.donationEntries) {
        if (!entry.linkId) continue;
        const g = grouped[entry.linkId];
        if (!g) continue;
        const amt = entry.amount || 0;
        g.totalAllocated += amt;
        if (r.idType !== 'national_id') g.nonSaudiAllocated += amt;
        // Proportional invoice VAT and cashback for this allocation
        if (r.invoices && r.invoices.length > 0) {
          const invoicesTotal = r.invoices.reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
          const invoiceVAT = r.invoices.reduce((s, inv) => s + (inv.taxAmount || 0), 0);
          if (invoicesTotal > 0 && invoiceVAT > 0) {
            g.caseInvoiceVAT += Math.ceil(invoiceVAT * amt / invoicesTotal);
          }
          // Cashback from provider percentage
          for (const inv of r.invoices) {
            const prov = providers.find(p => p.id === inv.providerId);
            const pct = prov?.cashbackPercentage || 0;
            if (pct > 0) {
              const invAmt = (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0);
              if (invoicesTotal > 0) g.cashbackAmount += (invAmt * pct / 100) * amt / invoicesTotal;
            }
          }
        }
        g.cases.push({
          caseId: r.id,
          caseNumber: r.caseNumber,
          nameAr: r.nameArabic || r.applicantName || '',
          nameEn: r.nameEnglish || r.applicantName || '',
          idNumber: r.idNumber || r.nationalId || '',
          allocated: amt,
          status: r.status,
        });
      }
    }

    const result = Object.values(grouped).map(g => {
      const ms = g.actualReceived * marketingX / ((marketingX + marketingY) || 1);
      g.marketingShare = Math.ceil(ms);
      g.refundableVAT = Math.ceil(ms * 0.15) + g.caseInvoiceVAT;
      // VAT on the marketing share is refundable, so it doesn't reduce the
      // amount available to the cases — only the marketing share itself does.
      g.availableForCases = Math.floor(g.actualReceived - g.marketingShare);
      g.remaining = g.availableForCases - g.totalAllocated;
      return g;
    });
    console.log(`[perf] allLinks: ${requests.length} requests × ${links.length} links in ${(performance.now() - t0).toFixed(1)}ms`);
    return result;
  }, [requests, links, marketingX, marketingY, providers, donationSumByLink]);

  const filteredLinks = useMemo(() => {
    if (!linksSearchQuery.trim()) return allLinks;
    const q = linksSearchQuery.toLowerCase();
    return allLinks.filter(l =>
      l.platformCaseNumber?.toLowerCase().includes(q) ||
      l.platform?.toLowerCase().includes(q) ||
      l.donorName?.toLowerCase().includes(q) ||
      l.cases.some(c =>
        String(c.caseNumber ?? '').includes(q) ||
        c.nameAr?.toLowerCase().includes(q) ||
        c.nameEn?.toLowerCase().includes(q) ||
        c.idNumber?.includes(q)
      )
    );
  }, [allLinks, linksSearchQuery]);

  const sortedLinks = useMemo(() => {
    const sorted = [...filteredLinks];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (linksSortColumn) {
        case 'platform': cmp = (a.platform || '').localeCompare(b.platform || ''); break;
        case 'platformCaseNo': cmp = (a.platformCaseNumber || '').localeCompare(b.platformCaseNumber || ''); break;
        case 'actualReceived': cmp = (a.actualReceived || 0) - (b.actualReceived || 0); break;
        case 'marketingShare': cmp = (a.marketingShare || 0) - (b.marketingShare || 0); break;
        case 'refundableVAT': cmp = (a.refundableVAT || 0) - (b.refundableVAT || 0); break;
        case 'cashback': cmp = (a.cashbackAmount || 0) - (b.cashbackAmount || 0); break;
        case 'availableForCases': cmp = (a.availableForCases || 0) - (b.availableForCases || 0); break;
        case 'totalAllocated': cmp = (a.totalAllocated || 0) - (b.totalAllocated || 0); break;
        case 'remaining': cmp = (a.remaining || 0) - (b.remaining || 0); break;
        case 'addedAt': {
          const da = a.addedAt ? new Date(a.addedAt).getTime() : 0;
          const db = b.addedAt ? new Date(b.addedAt).getTime() : 0;
          cmp = da - db;
          break;
        }
        default: cmp = (a.platformCaseNumber || '').localeCompare(b.platformCaseNumber || '');
      }
      return linksSortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredLinks, linksSortColumn, linksSortDirection]);

  const handleLinksSort = (column: string) => {
    if (linksSortColumn === column) {
      setLinksSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setLinksSortColumn(column);
      setLinksSortDirection('asc');
    }
  };

  const LinksSortIcon = ({ column }: { column: string }) => {
    if (linksSortColumn !== column) return <ArrowUpDown className="h-3 w-3 ms-1 opacity-50" />;
    return linksSortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ms-1" /> : <ArrowDown className="h-3 w-3 ms-1" />;
  };

  // Links tab management (add/edit/delete from links view)
  const [showAddLinkFromTab, setShowAddLinkFromTab] = useState(false);
  const [addLinkTabForm, setAddLinkTabForm] = useState({
    caseId: '',
    platform: '' as '' | 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
    caseNumber: '',
    url: '',
    actualReceived: '',
    treatmentCategoryId: '',
    linkTitle: ''
  });
  const resetAddLinkTabForm = () => setAddLinkTabForm({ caseId: '', platform: '', caseNumber: '', url: '', actualReceived: '', treatmentCategoryId: '', linkTitle: '' });

  const [editingLink, setEditingLink] = useState<typeof allLinks[0] | null>(null);
  const [editLinkForm, setEditLinkForm] = useState({ url: '', actualReceived: '', treatmentCategoryId: '', linkTitle: '' });

  const [showWeeklyMarketing, setShowWeeklyMarketing] = useState(false);

  // Per-project donation transactions manager
  const [managingDonationsLink, setManagingDonationsLink] = useState<typeof allLinks[0] | null>(null);
  const emptyDonationForm = { id: '', amount: '', date: '', donorName: '', donorPhone: '', paymentMethod: '' };
  const [donationForm, setDonationForm] = useState(emptyDonationForm);
  const [showDonationForm, setShowDonationForm] = useState(false);

  const toDateInput = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const startAddDonation = () => {
    setDonationForm({ ...emptyDonationForm, date: toDateInput(new Date()) });
    setShowDonationForm(true);
  };
  const startEditDonation = (d: DonationTransaction) => {
    setDonationForm({
      id: d.id,
      amount: String(d.amount ?? ''),
      date: toDateInput(d.date),
      donorName: d.donorName || '',
      donorPhone: d.donorPhone || '',
      paymentMethod: d.paymentMethod || '',
    });
    setShowDonationForm(true);
  };
  const handleSaveDonation = async () => {
    if (!managingDonationsLink) return;
    const amount = Number(donationForm.amount);
    if (!donationForm.amount || isNaN(amount) || amount < 0 || !donationForm.date) {
      toast.error(t('dashboard.admin.helps.donationError'));
      return;
    }
    setIsSubmitting(true);
    try {
      const [y, mo, da] = donationForm.date.split('-').map(Number);
      const date = new Date(y, (mo || 1) - 1, da || 1, 12, 0, 0);
      if (donationForm.id) {
        await updateDonation(donationForm.id, {
          amount,
          date,
          donorName: donationForm.donorName || undefined,
          donorPhone: donationForm.donorPhone || undefined,
          paymentMethod: donationForm.paymentMethod || undefined,
        });
      } else {
        await addDonation({
          linkId: managingDonationsLink._key,
          platform: managingDonationsLink.platform,
          caseNumber: managingDonationsLink.platformCaseNumber,
          amount,
          date,
          donorName: donationForm.donorName || undefined,
          donorPhone: donationForm.donorPhone || undefined,
          paymentMethod: donationForm.paymentMethod || undefined,
          source: 'manual',
          importKey: `man_${managingDonationsLink._key}_${Date.now()}`,
        });
      }
      setShowDonationForm(false);
      setDonationForm(emptyDonationForm);
      toast.success(t('dashboard.admin.helps.donationUpdated'));
    } catch {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteDonation = async (d: DonationTransaction) => {
    if (!confirm(t('dashboard.admin.helps.linksView.confirmDeleteDonation'))) return;
    setIsSubmitting(true);
    try {
      await deleteDonation(d.id);
      toast.success(t('dashboard.admin.helps.donationRemoved'));
    } catch {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddLinkFromTab = async () => {
    if (!addLinkTabForm.platform || !addLinkTabForm.caseNumber) return;

    // Check if this link already exists (logical key = platform + caseNumber)
    const existingLink = links.find(l => l.platform === addLinkTabForm.platform && l.caseNumber === addLinkTabForm.caseNumber);
    if (existingLink) {
      toast.error(t('dashboard.admin.helps.linksView.duplicateLink'));
      return;
    }

    setIsSubmitting(true);
    try {
      const newLinkId = await addLink({
        platform: addLinkTabForm.platform,
        caseNumber: addLinkTabForm.caseNumber,
        actualReceived: 0,
        url: addLinkTabForm.url || undefined,
        treatmentCategoryId: addLinkTabForm.treatmentCategoryId || undefined,
        linkTitle: addLinkTabForm.linkTitle || undefined,
        addedAt: new Date(),
        addedBy: currentUser?.uid,
      });

      const amt = addLinkTabForm.actualReceived ? Number(addLinkTabForm.actualReceived) : 0;
      if (amt > 0) {
        await addDonation({
          linkId: newLinkId,
          platform: addLinkTabForm.platform,
          caseNumber: addLinkTabForm.caseNumber,
          amount: amt,
          date: new Date(),
          source: 'manual',
          importKey: 'agg_' + newLinkId,
        });
      }

      setShowAddLinkFromTab(false);
      resetAddLinkTabForm();
      toast.success(t('dashboard.admin.helps.donationAdded'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditLink = (link: typeof allLinks[0]) => {
    setEditingLink(link);
    setEditLinkForm({
      url: link.url || '',
      actualReceived: link.actualReceived ? String(link.actualReceived) : '',
      treatmentCategoryId: link.treatmentCategoryId || '',
      linkTitle: link.linkTitle || ''
    });
  };

  const handleSaveEditLink = async () => {
    if (!editingLink) return;
    const linkDonations = donations.filter(d => d.linkId === editingLink._key);
    const isDerivedFromJood = linkDonations.some(d => d.source === 'jood');
    const aggDonation = findAggregateDonation(linkDonations);
    const newReceived = editLinkForm.actualReceived ? Number(editLinkForm.actualReceived) : 0;

    if (!isDerivedFromJood && !canReduceReceived(newReceived, editingLink.totalAllocated, marketingX, marketingY)) {
      toast.error(t('dashboard.admin.helps.linkBudget.cannotReduce'));
      return;
    }
    // CRITICAL fix (same edit-project inflation bug fixed on the marketer surface — see
    // MarketerProjectDialogs.handleSaveEditProject): the "received" field is prefilled from the
    // COMPUTED total (sum of every donation on the link), so the write must target that same
    // total — never the aggregate donation alone — or itemised donations get double-counted.
    // See computeAggregateDonationWrite.
    const nonAggregateTotal = linkDonations
      .filter(d => d.id !== aggDonation?.id)
      .reduce((s, d) => s + (d.amount || 0), 0);
    const writeDecision = isDerivedFromJood
      ? null
      : computeAggregateDonationWrite(newReceived, editingLink.actualReceived, aggDonation, nonAggregateTotal);
    if (writeDecision?.op === 'refuse') {
      toast.error(t('dashboard.admin.helps.linkBudget.receivedBelowItemised'));
      return;
    }
    setIsSubmitting(true);
    try {
      // Link metadata lives on the link doc; received drives a manual aggregate donation.
      await updateLink(editingLink._key, {
        url: editLinkForm.url || undefined,
        treatmentCategoryId: editLinkForm.treatmentCategoryId || undefined,
        linkTitle: editLinkForm.linkTitle || undefined,
      });
      if (writeDecision?.op === 'update' && aggDonation) {
        await updateDonation(aggDonation.id, { amount: writeDecision.amount });
      } else if (writeDecision?.op === 'create') {
        await addDonation({
          linkId: editingLink._key,
          platform: editingLink.platform,
          caseNumber: editingLink.platformCaseNumber,
          amount: writeDecision.amount,
          date: new Date(),
          source: 'manual',
          importKey: 'agg_' + editingLink._key,
        });
      }
      setEditingLink(null);
      toast.success(t('dashboard.admin.helps.donationUpdated'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assign unassigned link to case
  const [assigningLink, setAssigningLink] = useState<typeof allLinks[0] | null>(null);
  const [assignCaseId, setAssignCaseId] = useState('');

  const handleAssignLink = async () => {
    if (!assigningLink || !assignCaseId) return;
    const targetCase = requests.find(r => r.id === assignCaseId);
    if (!targetCase) return;

    try {
      assertCategoryMatch(assigningLink, targetCase);
    } catch (e: any) {
      toast.error(t(e.code === 'CATEGORY_MISSING'
        ? 'dashboard.admin.helps.alloc.categoryMissing'
        : 'dashboard.admin.helps.alloc.categoryMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Assigning = adding an allocation ref (linkId) to the target case.
      const newEntry: DonationEntry = {
        linkId: assigningLink._key,
        platform: assigningLink.platform as 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
        caseNumber: assigningLink.platformCaseNumber,
        addedAt: new Date(),
        addedBy: currentUser?.uid,
      };
      const targetEntries = [...(targetCase.donationEntries || []), newEntry];
      await updateRequest(targetCase.id, { donationEntries: targetEntries, updatedAt: new Date() });

      setAssigningLink(null);
      setAssignCaseId('');
      toast.success(t('dashboard.admin.helps.linksView.linkAssigned'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLink = async (link: typeof allLinks[0]) => {
    const hasCases = link.cases.length > 0;
    const msg = hasCases
      ? t('dashboard.admin.helps.linksView.confirmDeleteLinked', { count: link.cases.length })
      : t('dashboard.admin.helps.linksView.confirmDelete');
    if (!confirm(msg)) return;

    setIsSubmitting(true);
    try {
      // Remove allocation refs from any assigned cases
      for (const c of link.cases) {
        const req = requests.find(r => r.id === c.caseId);
        if (!req?.donationEntries) continue;
        const updatedEntries = req.donationEntries.filter(entry => entry.linkId !== link._key);
        await updateRequest(req.id, { donationEntries: updatedEntries, updatedAt: new Date() });
      }
      // Delete the link doc itself
      await deleteLink(link._key);
      toast.success(t('dashboard.admin.helps.donationRemoved'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Backfill case numbers
  const [isBackfilling, setIsBackfilling] = useState(false);
  const hasMissingCaseNumbers = requests.some(r => typeof r.caseNumber !== 'number');

  const handleBackfillCaseNumbers = async () => {
    setIsBackfilling(true);
    try {
      const updated = await backfillCaseNumbers();
      toast.success(`Assigned case numbers to ${updated} cases`);
      await refreshRequests();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error('Failed to assign case numbers');
    } finally {
      setIsBackfilling(false);
    }
  };

  // AI Allocation
  const [showAiAllocation, setShowAiAllocation] = useState(false);
  const [aiAllocations, setAiAllocations] = useState<{ linkKey: string; linkTitle: string; caseId: string; caseName: string; amount: number; treatmentCategoryId?: string; }[]>([]);
  const [isApplyingAi, setIsApplyingAi] = useState(false);

  const [isAiLoading, setIsAiLoading] = useState(false);

  const handleAiAllocate = async () => {
    // Helper: compute age from dateOfBirth
    const getAge = (r: any): number | null => {
      const dob = r.dateOfBirth;
      if (!dob) return r.age ?? null;
      const d = dob instanceof Date ? dob : dob.toDate ? dob.toDate() : new Date(dob);
      if (isNaN(d.getTime())) return r.age ?? null;
      const now = new Date();
      let age = now.getFullYear() - d.getFullYear();
      if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
      return age;
    };

    // Build link budgets (remaining > 0)
    const linkBudgets = allLinks
      .filter(l => l.remaining > 0)
      .map(l => ({
        key: l._key,
        title: l.linkTitle || '',
        platform: l.platform,
        caseNumber: l.platformCaseNumber,
        category: l.treatmentCategoryId || '',
        remaining: l.remaining,
      }));

    // Build case needs (remaining > 0, not link holders).
    // NET medical cost vs NET allocations — matches the case-side
    // semantics enforced by getCaseRemainingBudget.
    const caseNeeds = requests
      .map(r => {
        const caseCost = getCaseRemainingBudget(r.id).caseCost;
        let totalFunded = 0;
        for (const entry of (r.donationEntries || [])) {
          totalFunded += entry.amount || 0;
        }
        const remaining = caseCost - totalFunded;
        if (remaining <= 0) return null;
        return {
          id: r.id,
          name: r.nameArabic || r.applicantName || r.nameEnglish || '',
          caseNumber: r.caseNumber,
          category: r.categoryId || '',
          categoryName: r.categoryName || '',
          cost: caseCost,
          funded: totalFunded,
          remaining,
          age: getAge(r),
          gender: r.gender || '',
          existingLinkKeys: (r.donationEntries || []).map(e => e.linkId),
        };
      })
      .filter(Boolean);

    if (linkBudgets.length === 0 || caseNeeds.length === 0) {
      setAiAllocations([]);
      setShowAiAllocation(true);
      return;
    }

    setIsAiLoading(true);
    setShowAiAllocation(true);

    const prompt = `You are a smart allocation engine for a medical NGO (Namat). Your job is to allocate donation links to treatment cases optimally.

RULES (STRICT):
1. Never allocate more than a donation's "remaining" budget
2. Never allocate more than a case's "remaining" need
3. Try to FULLY fund each case before moving to the next
4. Match donation category to case category — if a donation's category does not equal the case's category, DO NOT propose that pair. Drop it entirely.
5. Consider donation title keywords for age/gender matching:
   - Child keywords: طفل, أطفال, children, kids → prefer cases with age < 18
   - Female keywords: نساء, سيدات, women → prefer female cases
   - Male keywords: رجال, men → prefer male cases
6. A donation can be split across multiple cases
7. A case can receive from multiple donations
8. Do NOT allocate a donation to a case if the donation key is in the case's existingLinkKeys
9. Maximize total allocation amount while respecting all constraints

AVAILABLE DONATIONS:
${JSON.stringify(linkBudgets, null, 2)}

CASES NEEDING FUNDING:
${JSON.stringify(caseNeeds, null, 2)}

Respond with ONLY valid JSON array, no markdown, no explanation.
Use the donation's "key" value verbatim as "linkKey":
[
  { "linkKey": "<donation key>", "caseId": "firestore_doc_id", "amount": number }
]

Each amount must be a whole number (no decimals). Return empty array [] if no allocations possible.`;

    try {
      const aiChat = httpsCallable(functions, 'aiChat');
      const res: any = await aiChat({
        system: 'You are an optimal resource allocation engine. Respond with valid JSON only. No markdown code fences.',
        prompt,
      });

      const content = String(res?.data?.content || '').trim() || '[]';
      // Strip markdown fences if present
      const jsonStr = content.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
      const rawAllocations: { linkKey: string; caseId: string; amount: number }[] = JSON.parse(jsonStr);

      // Validate and build allocations with display info
      const linkMap = new Map(allLinks.map(l => [l._key, l]));
      const caseMap = new Map(requests.map(r => [r.id, r]));
      const linkUsed: Record<string, number> = {};
      const caseUsed: Record<string, number> = {};

      const validAllocations: typeof aiAllocations = [];
      for (const raw of rawAllocations) {
        const link = linkMap.get(raw.linkKey);
        const caseReq = caseMap.get(raw.caseId);
        if (!link || !caseReq || raw.amount <= 0) continue;
        if (!link.treatmentCategoryId || !caseReq.categoryId) continue;
        if (link.treatmentCategoryId !== caseReq.categoryId) continue;

        const linkRemaining = link.remaining - (linkUsed[raw.linkKey] || 0);
        const caseBudget = getCaseRemainingBudget(raw.caseId);
        const caseRemaining = caseBudget.remaining - (caseUsed[raw.caseId] || 0);
        const amount = Math.min(Math.floor(raw.amount), linkRemaining, caseRemaining);
        if (amount <= 0) continue;

        validAllocations.push({
          linkKey: raw.linkKey,
          linkTitle: link.linkTitle || link.platformCaseNumber,
          caseId: raw.caseId,
          caseName: caseReq.nameArabic || caseReq.applicantName || caseReq.nameEnglish || '',
          amount,
          treatmentCategoryId: link.treatmentCategoryId,
        });
        linkUsed[raw.linkKey] = (linkUsed[raw.linkKey] || 0) + amount;
        caseUsed[raw.caseId] = (caseUsed[raw.caseId] || 0) + amount;
      }

      setAiAllocations(validAllocations);
    } catch (error: any) {
      console.error('AI allocation error:', error);
      const code = error?.code || '';
      const message = String(error?.message || '');
      if (code === 'functions/failed-precondition' || message.includes('ai-key-missing')) {
        toast.error(t('dashboard.admin.helps.aiAllocateNoKey'));
      } else {
        toast.error(t('dashboard.admin.helps.aiAllocateError'));
      }
      setAiAllocations([]);
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleApplyAiAllocations = async () => {
    if (aiAllocations.length === 0) return;
    setIsApplyingAi(true);
    try {
      // Group allocations by caseId
      const byCaseId: Record<string, typeof aiAllocations> = {};
      for (const alloc of aiAllocations) {
        if (!byCaseId[alloc.caseId]) byCaseId[alloc.caseId] = [];
        byCaseId[alloc.caseId].push(alloc);
      }

      for (const [caseId, allocs] of Object.entries(byCaseId)) {
        const req = requests.find(r => r.id === caseId);
        if (!req) continue;
        const existingEntries = [...(req.donationEntries || [])];
        for (const alloc of allocs) {
          const link = allLinks.find(l => l._key === alloc.linkKey);
          if (!link) continue;
          const caseDocForGate = requests.find(r => r.id === alloc.caseId);
          if (!caseDocForGate) continue;
          try {
            assertCategoryMatch(link, caseDocForGate);
          } catch (e: any) {
            toast.error(t(e.code === 'CATEGORY_MISSING'
              ? 'dashboard.admin.helps.alloc.categoryMissing'
              : 'dashboard.admin.helps.alloc.categoryMismatch'));
            continue;
          }
          const newEntry: DonationEntry = {
            linkId: link._key,
            platform: link.platform,
            caseNumber: link.platformCaseNumber,
            amount: alloc.amount,
            addedAt: new Date(),
            addedBy: currentUser?.uid,
          };
          existingEntries.push(newEntry);
        }
        await updateRequest(caseId, { donationEntries: existingEntries, updatedAt: new Date() });
      }

      toast.success(`تم تخصيص ${aiAllocations.length} تبرع بنجاح`);
      setShowAiAllocation(false);
      setAiAllocations([]);
    } catch (error) {
      console.error('AI allocation error:', error);
      toast.error('فشل في تطبيق التخصيص');
    } finally {
      setIsApplyingAi(false);
    }
  };

  // Sort state
  const [sortColumn, setSortColumn] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const calcCostWithMarketing = (cost: number) => Math.ceil(cost + (cost * marketingX / (marketingY || 1)));

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [autoApproveDoc, marketingDoc, coordPermsDoc] = await Promise.all([
          getDoc(doc(db, 'settings', 'treatmentAutoApprove')),
          getDoc(doc(db, 'settings', 'treatmentMarketing')),
          getDoc(doc(db, 'settings', 'coordPermissions')),
        ]);
        if (autoApproveDoc.exists()) {
          setAutoApproveEnabled(autoApproveDoc.data().enabled === true);
        }
        if (marketingDoc.exists()) {
          const data = marketingDoc.data();
          if (typeof data.x === 'number') setMarketingX(data.x);
          if (typeof data.y === 'number') setMarketingY(data.y);
        }
        if (coordPermsDoc.exists()) {
          setCoordCanDeleteAssignments(coordPermsDoc.data().canDeleteAssignments === true);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();
  }, []);
  
  // Handle auto-approve toggle
  const handleAutoApproveToggle = async (checked: boolean) => {
    try {
      await setDoc(doc(db, 'settings', 'treatmentAutoApprove'), {
        enabled: checked,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid
      });
      setAutoApproveEnabled(checked);
      toast.success(checked 
        ? t('dashboard.admin.helps.settings.autoApproveEnabled')
        : t('dashboard.admin.helps.settings.autoApproveDisabled')
      );
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(t('dashboard.admin.helps.settings.updateError'));
    }
  };

  const handleCoordCanDeleteAssignmentsToggle = async (checked: boolean) => {
    try {
      await setDoc(doc(db, 'settings', 'coordPermissions'), {
        canDeleteAssignments: checked,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid,
      }, { merge: true });
      setCoordCanDeleteAssignments(checked);
      toast.success(checked
        ? t('dashboard.admin.helps.settings.coordCanDeleteAssignmentsEnabled')
        : t('dashboard.admin.helps.settings.coordCanDeleteAssignmentsDisabled'));
    } catch (error) {
      console.error('Error updating coord permissions:', error);
      toast.error(t('dashboard.admin.helps.settings.updateError'));
    }
  };

  const handleMarketingSave = async (x: number, y: number) => {
    try {
      await setDoc(doc(db, 'settings', 'treatmentMarketing'), {
        x,
        y,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid
      });
      setMarketingX(x);
      setMarketingY(y);
      toast.success(t('dashboard.admin.helps.settings.marketingUpdated'));
    } catch (error) {
      console.error('Error updating marketing settings:', error);
      toast.error(t('dashboard.admin.helps.settings.updateError'));
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({ name: '', nameAr: '', description: '', isActive: true });
    setEditingCategory(null);
  };

  const openAddCategoryDialog = () => {
    resetCategoryForm();
    setIsCategoryEditOpen(true);
  };

  const openEditCategoryDialog = (category: any) => {
    setEditingCategory(category);
    setCategoryFormData({ name: category.name, nameAr: category.nameAr || '', description: category.description || '', isActive: category.isActive });
    setIsCategoryEditOpen(true);
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryFormData.name || !categoryFormData.nameAr) {
      toast.error(t('dashboard.admin.helps.settings.categories.validation.required'));
      return;
    }
    setIsCategorySubmitting(true);
    try {
      const data = { name: categoryFormData.name, nameAr: categoryFormData.nameAr, description: categoryFormData.description, isActive: categoryFormData.isActive };
      if (editingCategory) {
        await updateTreatmentCategory(editingCategory.id, data);
        toast.success(t('dashboard.admin.helps.settings.categories.updateSuccess'));
      } else {
        await addTreatmentCategory(data);
        toast.success(t('dashboard.admin.helps.settings.categories.addSuccess'));
      }
      setIsCategoryEditOpen(false);
      resetCategoryForm();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(t('dashboard.admin.helps.settings.categories.saveError'));
    } finally {
      setIsCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = async (category: any) => {
    if (!confirm(t('dashboard.admin.helps.settings.categories.confirmDelete'))) return;
    try {
      await deleteTreatmentCategory(category.id);
      toast.success(t('dashboard.admin.helps.settings.categories.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('dashboard.admin.helps.settings.categories.deleteError'));
    }
  };

  // Modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addFormStep, setAddFormStep] = useState(1);
  const ADD_TOTAL_STEPS = 5;
  const addStepLabels = [
    t('common.stepLabels.personalInfo'),
    t('common.stepLabels.demographics'),
    t('common.stepLabels.socioeconomic'),
    t('common.stepLabels.medical'),
    t('common.stepLabels.caseStatus'),
  ];
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isApprovalOpen, setIsApprovalOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [isPaymentSlipOpen, setIsPaymentSlipOpen] = useState(false);
  const [isAddReportsOpen, setIsAddReportsOpen] = useState(false);
  
  // Selected request
  const [selectedRequest, setSelectedRequest] = useState<TreatmentSupportRequest | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [idPreviewUrl, setIdPreviewUrl] = useState<string | null>(null);
  const [idPreviewRotation, setIdPreviewRotation] = useState(0);
  const [isSavingRotation, setIsSavingRotation] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropDragStart, setCropDragStart] = useState<{ x: number; y: number } | null>(null);
  const [cropDragHandle, setCropDragHandle] = useState<string | null>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [editingFileType, setEditingFileType] = useState<'report' | 'invoice' | 'progressReport' | null>(null);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editingInvoiceData, setEditingInvoiceData] = useState({
    providerId: '',
    providerName: '',
    vatNumber: '',
    invoiceDate: '',
    invoiceNumber: '',
    totalBeforeDiscount: '',
    totalAfterDiscount: '',
    taxableAmount: '',
    nonTaxableAmount: '',
    taxAmount: '',
  });
  const [isEditingReport, setIsEditingReport] = useState(false);
  const [editingReportData, setEditingReportData] = useState({
    providerId: '',
    department: '',
    reportDate: '',
  });
  const [isEditingProgressReport, setIsEditingProgressReport] = useState(false);
  const [editingProgressReportData, setEditingProgressReportData] = useState({
    providerId: '',
    department: '',
    reportDate: '',
    notes: '',
  });


  const isPdfUrl = (url: string | undefined) => !!url && url.toLowerCase().includes('.pdf');
  const isImageUrl = (url: string | undefined) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].some(ext => lower.includes(ext)) && !lower.includes('.pdf');
  };

  const handleSaveRotation = async () => {
    if (!idPreviewUrl || !selectedRequest || idPreviewRotation === 0) return;
    setIsSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = idPreviewUrl;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const isVertical = idPreviewRotation === 90 || idPreviewRotation === 270;
      canvas.width = isVertical ? img.height : img.width;
      canvas.height = isVertical ? img.width : img.height;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((idPreviewRotation * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
      });

      const timestamp = Date.now();
      const file = new File([blob], `rotated_${timestamp}.png`, { type: 'image/png' });
      const url = await uploadFile(file, `id-documents/${selectedRequest.id}/${timestamp}_rotated.png`);

      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, idDocumentUrl: url } : null);
      setIdPreviewUrl(url);
      setIdPreviewRotation(0);
      toast.success(t('common.rotationSaved'));
    } catch {
      toast.error(t('common.rotationSaveError'));
    } finally {
      setIsSavingRotation(false);
    }
  };

  const handleSaveCrop = async () => {
    if (!idPreviewUrl || !selectedRequest || !cropRect) return;
    setIsSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = idPreviewUrl;
      });

      const sx = (cropRect.x / 100) * img.width;
      const sy = (cropRect.y / 100) * img.height;
      const sw = (cropRect.w / 100) * img.width;
      const sh = (cropRect.h / 100) * img.height;

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw));
      canvas.height = Math.max(1, Math.round(sh));
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
      });

      const timestamp = Date.now();
      const file = new File([blob], `cropped_${timestamp}.png`, { type: 'image/png' });
      const url = await uploadFile(file, `id-documents/${selectedRequest.id}/${timestamp}_cropped.png`);

      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, idDocumentUrl: url } : null);
      setIdPreviewUrl(url);
      setCropRect(null);
      setIsCropping(false);
      toast.success(t('common.cropSaved'));
    } catch {
      toast.error(t('common.cropSaveError'));
    } finally {
      setIsSavingRotation(false);
    }
  };

  const getCropRelativePos = (clientX: number, clientY: number) => {
    const container = cropContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const onCropPointerDown = (e: React.PointerEvent) => {
    if (!isCropping) return;
    e.preventDefault(); e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setCropDragStart(getCropRelativePos(e.clientX, e.clientY));
    setCropDragHandle(null); setCropRect(null);
  };

  const onCropPointerMove = (e: React.PointerEvent) => {
    if (!isCropping || !cropDragStart) return;
    e.preventDefault(); e.stopPropagation();
    const pos = getCropRelativePos(e.clientX, e.clientY);
    if (cropDragHandle && cropRect) {
      const newRect = { ...cropRect };
      if (cropDragHandle.includes('t')) { const b = cropRect.y + cropRect.h; newRect.y = Math.min(pos.y, b - 1); newRect.h = b - newRect.y; }
      if (cropDragHandle.includes('b')) { newRect.h = Math.max(1, pos.y - cropRect.y); }
      if (cropDragHandle.includes('l')) { const r = cropRect.x + cropRect.w; newRect.x = Math.min(pos.x, r - 1); newRect.w = r - newRect.x; }
      if (cropDragHandle.includes('r')) { newRect.w = Math.max(1, pos.x - cropRect.x); }
      newRect.x = Math.max(0, newRect.x); newRect.y = Math.max(0, newRect.y);
      newRect.w = Math.min(100 - newRect.x, newRect.w); newRect.h = Math.min(100 - newRect.y, newRect.h);
      setCropRect(newRect);
    } else {
      setCropRect({ x: Math.min(cropDragStart.x, pos.x), y: Math.min(cropDragStart.y, pos.y), w: Math.abs(pos.x - cropDragStart.x), h: Math.abs(pos.y - cropDragStart.y) });
    }
  };

  const onCropPointerUp = (e: React.PointerEvent) => {
    if (!isCropping) return;
    e.stopPropagation();
    setCropDragStart(null); setCropDragHandle(null);
    if (cropRect && (cropRect.w < 1 || cropRect.h < 1)) setCropRect(null);
  };

  const onCropHandleDown = (handle: string, e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setCropDragStart(getCropRelativePos(e.clientX, e.clientY));
    setCropDragHandle(handle);
  };

  const getCropHandleStyle = (handle: string, c: { x: number; y: number; w: number; h: number }): React.CSSProperties => {
    const s = 12, h2 = s / 2;
    const base: React.CSSProperties = { position: 'absolute', width: s, height: s };
    const positions: Record<string, React.CSSProperties> = {
      tl: { left: `${c.x}%`, top: `${c.y}%`, cursor: 'nwse-resize' },
      tr: { left: `${c.x + c.w}%`, top: `${c.y}%`, cursor: 'nesw-resize' },
      bl: { left: `${c.x}%`, top: `${c.y + c.h}%`, cursor: 'nesw-resize' },
      br: { left: `${c.x + c.w}%`, top: `${c.y + c.h}%`, cursor: 'nwse-resize' },
      t: { left: `${c.x + c.w / 2}%`, top: `${c.y}%`, cursor: 'ns-resize' },
      b: { left: `${c.x + c.w / 2}%`, top: `${c.y + c.h}%`, cursor: 'ns-resize' },
      l: { left: `${c.x}%`, top: `${c.y + c.h / 2}%`, cursor: 'ew-resize' },
      r: { left: `${c.x + c.w}%`, top: `${c.y + c.h / 2}%`, cursor: 'ew-resize' },
    };
    return { ...base, ...positions[handle], transform: `translate(-${h2}px, -${h2}px)` };
  };

  const getCategoryColor = (categoryId: string | undefined) => {
    if (!categoryId) return 'bg-gray-100 text-gray-800';
    const idx = activeCategories.findIndex(c => c.id === categoryId);
    return CATEGORY_COLORS[idx >= 0 ? idx % CATEGORY_COLORS.length : 0];
  };

  const getCategoryLabel = (request: TreatmentSupportRequest) => {
    if (request.categoryId) {
      const cat = activeCategories.find(c => c.id === request.categoryId);
      return dir === 'rtl'
        ? (cat?.nameAr || cat?.name || request.categoryName || '-')
        : (cat?.name || request.categoryName || '-');
    }
    return request.categoryName || '-';
  };

  const updateRequestCategory = async (request: TreatmentSupportRequest, newId: string | undefined) => {
    if (newId === request.categoryId) return;
    const conflicting = (request.donationEntries || []).filter(e => {
      const l = allLinks.find(x => x._key === e.linkId);
      return l && l.treatmentCategoryId && newId && l.treatmentCategoryId !== newId;
    });
    if (conflicting.length > 0) {
      const ok = window.confirm(t('dashboard.admin.helps.linksView.cascadeConfirm'));
      if (!ok) return;
    }
    const remainingEntries = (request.donationEntries || []).filter(e => !conflicting.includes(e));
    try {
      const cat = newId ? activeCategories.find(c => c.id === newId) : null;
      await updateRequest(request.id, {
        categoryId: newId,
        categoryName: cat ? (dir === 'rtl' ? (cat.nameAr || cat.name) : cat.name) : '',
        donationEntries: remainingEntries,
        updatedAt: new Date(),
      } as any);
    } catch (err) {
      console.error('Error updating case category:', err);
      toast.error(t('common.saveError'));
    }
  };

  const changeLinkCategoryWithCascade = async (link: typeof allLinks[number], newId: string | undefined) => {
    if (newId === link.treatmentCategoryId) return;
    const affected = link.cases || [];
    if (affected.length > 0) {
      const ok = window.confirm(t('dashboard.admin.helps.linksView.cascadeConfirm'));
      if (!ok) return;
    }
    try {
      const batch = writeBatch(db);
      for (const c of affected) {
        const req = requests.find(r => r.id === c.caseId);
        if (!req) continue;
        const updated = (req.donationEntries || []).filter(e => e.linkId !== link._key);
        batch.update(doc(db, 'treatmentSupportRequests', req.id), { donationEntries: updated, updatedAt: new Date() });
      }
      batch.update(doc(db, 'links', link._key), { treatmentCategoryId: newId ?? null, updatedAt: new Date() });
      await batch.commit();
    } catch (err) {
      console.error('Error updating link category:', err);
      toast.error(t('common.saveError'));
    }
  };

  // Sync selectedRequest with requests array when data is refreshed
  useEffect(() => {
    if (selectedRequest && requests.length > 0) {
      const updated = requests.find(r => r.id === selectedRequest.id);
      if (updated) {
        setSelectedRequest(updated);
      }
    }
  }, [requests]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nameArabic: '',
    nameEnglish: '',
    idNumber: '',
    idType: 'national_id' as TreatmentIdType,
    dateOfBirth: '',
    idExpiryDate: '',
    nationality: '',
    gender: 'male' as TreatmentGender,
    maritalStatus: 'single' as TreatmentMaritalStatus,
    familyMembersCount: '',
    email: '',
    educationLevel: '' as TreatmentEducationLevel | '',
    workSector: '' as TreatmentWorkSector | '',
    profession: '',
    monthlyIncome: '',
    housingType: '' as TreatmentHousingType | '',
    rentAmount: '',
    city: '',
    neighborhood: '',
    phoneNumber: '',
    relativeMobileNumber: '',
    isCritical: false,
    isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    medicalCondition: '',
    requiredTreatment: '',
    cost: '',
    coordinatorNotes: '',
  });
  const [newPaymentSlipFile, setNewPaymentSlipFile] = useState<File | null>(null);
  
  // Donation entries state
  const [showAllocateLink, setShowAllocateLink] = useState(false);
  const [openAllocateLinkPicker, setOpenAllocateLinkPicker] = useState(false);
  const [allocateLinkKey, setAllocateLinkKey] = useState('');
  const [allocateAmount, setAllocateAmount] = useState('');
  const [editingDonationIndex, setEditingDonationIndex] = useState<number | null>(null);
  const [editingDonation, setEditingDonation] = useState({
    linkId: '',
    platform: '' as '' | 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
    caseNumber: '',
    amount: '',
  });

  // Update state
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [newUpdateText, setNewUpdateText] = useState('');
  
  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Applicant information
    nameArabic: '',
    nameEnglish: '',
    idNumber: '',
    idType: 'national_id' as TreatmentIdType,
    dateOfBirth: '',
    idExpiryDate: '',
    nationality: '',
    gender: 'male' as TreatmentGender,
    maritalStatus: 'single' as TreatmentMaritalStatus,
    familyMembersCount: '',
    email: '',
    educationLevel: '' as TreatmentEducationLevel | '',
    workSector: '' as TreatmentWorkSector | '',
    profession: '',
    monthlyIncome: '',
    housingType: '' as TreatmentHousingType | '',
    rentAmount: '',
    city: '',
    neighborhood: '',
    phoneNumber: '',
    relativeMobileNumber: '',
    // Case status
    isCritical: false,
    isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    // Medical information
    medicalCondition: '',
    requiredTreatment: '',
    cost: '',
    coordinatorNotes: '',
  });

  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);
  const [pendingIdDocument, setPendingIdDocument] = useState<File | null>(null);

  // Review form
  const [reviewData, setReviewData] = useState({
    socialStatusFits: false,
    medicalConditionFits: false,
    coordinatorNotes: '',
  });

  // Approval form
  const [approvalData, setApprovalData] = useState({
    decision: '' as 'approve' | 'reject' | '',
    approvedAmount: '',
    adminNotes: '',
  });

  // Invoice upload
  const [pendingInvoices, setPendingInvoices] = useState<PendingInvoice[]>([]);

  // Progress report upload
  const [pendingProgressReports, setPendingProgressReports] = useState<PendingProgressReport[]>([]);
  const [startPromptRequest, setStartPromptRequest] = useState<TreatmentSupportRequest | null>(null);

  // Payment slip upload
  const [paymentSlipFiles, setPaymentSlipFiles] = useState<File[]>([]);

  // New medical reports
  const [newReportFiles, setNewReportFiles] = useState<File[]>([]);

  const resetForm = () => {
    setFormData({
      nameArabic: '',
      nameEnglish: '',
      idNumber: '',
      idType: 'national_id',
      dateOfBirth: '',
      idExpiryDate: '',
      nationality: '',
      gender: 'male',
      maritalStatus: 'single',
      familyMembersCount: '',
      email: '',
      educationLevel: '',
      workSector: '',
      profession: '',
      monthlyIncome: '',
      housingType: '',
      rentAmount: '',
      city: '',
      neighborhood: '',
      phoneNumber: '',
      relativeMobileNumber: '',
      isCritical: false,
      isHospitalized: false,
      hasInsurance: false,
      insuranceProvider: '',
      insuranceCategory: '',
      categoryId: '',
      medicalCondition: '',
      requiredTreatment: '',
      cost: '',
      coordinatorNotes: '',
    });
    setPendingReports([]);
    setPendingIdDocument(null);
  };

  const uploadFile = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Filter requests by search
  const filteredRequests = requests.filter(r => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      r.applicantName?.toLowerCase().includes(query) ||
      r.nationalId?.toLowerCase().includes(query) ||
      r.medicalCondition?.toLowerCase().includes(query)
    );
  });

  // Filter requests by status
  const pendingReview = filteredRequests.filter(r => r.status === 'pending' || r.status === 'under_review');
  const pendingApproval = filteredRequests.filter(r => r.status === 'pending_approval');
  const approved = filteredRequests.filter(r => r.status === 'approved');
  const approvedTreatment = filteredRequests.filter(r => r.status === 'approved_treatment');
  const treatmentStarted = filteredRequests.filter(r => r.status === 'treatment_started');
  const treated = filteredRequests.filter(r => r.status === 'pending_invoices');
  const completed = filteredRequests.filter(r => r.status === 'completed');
  const rejected = filteredRequests.filter(r => r.status === 'rejected');

  // Add new case manually
  const handleAddCase = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation - only phone and ID number are required
    if (!formData.idNumber || !formData.phoneNumber) {
      toast.error(t('dashboard.admin.helps.validation.required'));
      return;
    }

    // Validate ID format based on type
    if (formData.idType === 'national_id' && !/^[12]\d{9}$/.test(formData.idNumber)) {
      toast.error(t('dashboard.admin.helps.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload all pending reports
      const uploadedReports = [];
      for (const report of pendingReports) {
        const timestamp = Date.now();
        const url = await uploadFile(
          report.file,
          `treatment-reports/admin/${timestamp}_${report.file.name}`
        );
        uploadedReports.push({
          url,
          name: report.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          reportDate: report.reportDate ? new Date(report.reportDate) : new Date(),
          providerId: report.providerId,
          providerName: report.providerName,
          department: report.department,
        });
      }
      const medicalReportUrl = uploadedReports.length > 0 ? uploadedReports[0].url : '';

      // Upload ID document if provided
      let idDocumentUrl = '';
      let idDocumentName = '';
      if (pendingIdDocument) {
        const timestamp = Date.now();
        idDocumentUrl = await uploadFile(
          pendingIdDocument,
          `id-documents/admin/${timestamp}_${pendingIdDocument.name}`
        );
        idDocumentName = pendingIdDocument.name;
      }

      const requestData: Parameters<typeof addRequest>[0] = {
        userId: `admin_${currentUser?.uid}`,
        // New applicant fields
        nameArabic: formData.nameArabic,
        nameEnglish: formData.nameEnglish,
        idNumber: formData.idNumber,
        idType: formData.idType,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined as any,
        idExpiryDate: formData.idExpiryDate ? new Date(formData.idExpiryDate) : undefined as any,
        nationality: formData.nationality,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        familyMembersCount: Number(formData.familyMembersCount),
        email: formData.email || undefined,
        educationLevel: formData.educationLevel || undefined,
        workSector: formData.workSector || undefined,
        profession: formData.profession || undefined,
        monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
        housingType: formData.housingType || undefined,
        rentAmount: formData.housingType === 'rented' && formData.rentAmount ? Number(formData.rentAmount) : undefined,
        city: formData.city || undefined,
        neighborhood: formData.neighborhood || undefined,
        relativeMobileNumber: formData.relativeMobileNumber,
        // Case status
        isCritical: formData.isCritical,
        isHospitalized: formData.isHospitalized,
        hasInsurance: formData.hasInsurance,
        insuranceProvider: formData.hasInsurance ? formData.insuranceProvider : '',
        insuranceCategory: formData.hasInsurance ? formData.insuranceCategory : '',
        categoryId: formData.categoryId || undefined,
        categoryName: formData.categoryId ? (activeCategories.find(c => c.id === formData.categoryId)?.nameAr || activeCategories.find(c => c.id === formData.categoryId)?.name) : undefined,
        // Medical information
        medicalCondition: formData.medicalCondition,
        requiredTreatment: formData.requiredTreatment,
        cost: Number(formData.cost),
        medicalReportUrl,
        medicalReports: uploadedReports,
        status: 'pending',
        coordinatorNotes: formData.coordinatorNotes,
        createdAt: new Date(),
        createdBy: 'admin',
      };
      if (formData.phoneNumber) requestData.phoneNumber = formData.phoneNumber;
      if (idDocumentUrl) {
        requestData.idDocumentUrl = idDocumentUrl;
        requestData.idDocumentName = idDocumentName;
        requestData.idDocumentUploadedAt = new Date();
      }
      
      await addRequest(requestData);
      
      toast.success(t('dashboard.admin.helps.addSuccess'));
      setIsAddOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error adding case:', error);
      toast.error(t('dashboard.admin.helps.addError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (request: TreatmentSupportRequest) => {
    setSelectedRequest(request);
    setFormData({
      nameArabic: request.nameArabic || '',
      nameEnglish: request.nameEnglish || '',
      idNumber: request.idNumber || request.nationalId || '',
      idType: request.idType || 'national_id',
      dateOfBirth: request.dateOfBirth ? new Date(request.dateOfBirth).toISOString().split('T')[0] : '',
      idExpiryDate: request.idExpiryDate ? new Date(request.idExpiryDate).toISOString().split('T')[0] : '',
      nationality: request.nationality || '',
      gender: request.gender || 'male',
      maritalStatus: request.maritalStatus || 'single',
      familyMembersCount: String(request.familyMembersCount || ''),
      email: request.email || '',
      educationLevel: request.educationLevel || '',
      workSector: request.workSector || '',
      profession: request.profession || '',
      monthlyIncome: request.monthlyIncome != null ? String(request.monthlyIncome) : '',
      housingType: request.housingType || '',
      rentAmount: request.rentAmount != null ? String(request.rentAmount) : '',
      city: request.city || '',
      neighborhood: request.neighborhood || '',
      phoneNumber: request.phoneNumber || '',
      relativeMobileNumber: request.relativeMobileNumber || '',
      isCritical: request.isCritical ?? false,
      isHospitalized: request.isHospitalized ?? false,
      hasInsurance: request.hasInsurance ?? false,
      insuranceProvider: request.insuranceProvider || '',
      insuranceCategory: request.insuranceCategory || '',
      categoryId: request.categoryId || '',
      medicalCondition: request.medicalCondition || '',
      requiredTreatment: request.requiredTreatment || '',
      cost: String(request.cost || ''),
      coordinatorNotes: request.coordinatorNotes || '',
    });
    setPendingReports([]);
    setIsEditOpen(true);
  };

  // Edit case
  const handleEditCase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRequest) return;
    
    if (!formData.idNumber || !formData.phoneNumber) {
      toast.error(t('dashboard.admin.helps.validation.required'));
      return;
    }

    if (formData.idType === 'national_id' && !/^[12]\d{9}$/.test(formData.idNumber)) {
      toast.error(t('dashboard.admin.helps.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload new medical report if provided
      let newReports = selectedRequest.medicalReports || [];
      
      // Handle legacy single report
      if (selectedRequest.medicalReportUrl && newReports.length === 0) {
        newReports = [{
          url: selectedRequest.medicalReportUrl,
          name: 'Medical Report',
          uploadedAt: selectedRequest.createdAt,
        }];
      }
      
      // Upload any new pending reports
      for (const report of pendingReports) {
        const timestamp = Date.now();
        const url = await uploadFile(
          report.file,
          `treatment-reports/${selectedRequest.id}/${timestamp}_${report.file.name}`
        );
        newReports = [...newReports, {
          url,
          name: report.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          reportDate: report.reportDate ? new Date(report.reportDate) : new Date(),
          providerId: report.providerId,
          providerName: report.providerName,
          department: report.department,
        }];
      }

      const updateData: Partial<TreatmentSupportRequest> = {
        nameArabic: formData.nameArabic,
        nameEnglish: formData.nameEnglish,
        idNumber: formData.idNumber,
        idType: formData.idType,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined,
        idExpiryDate: formData.idExpiryDate ? new Date(formData.idExpiryDate) : undefined,
        nationality: formData.nationality,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        familyMembersCount: Number(formData.familyMembersCount),
        email: formData.email || undefined,
        educationLevel: formData.educationLevel || undefined,
        workSector: formData.workSector || undefined,
        profession: formData.profession || undefined,
        monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
        housingType: formData.housingType || undefined,
        rentAmount: formData.housingType === 'rented' && formData.rentAmount ? Number(formData.rentAmount) : undefined,
        city: formData.city || undefined,
        neighborhood: formData.neighborhood || undefined,
        phoneNumber: formData.phoneNumber,
        relativeMobileNumber: formData.relativeMobileNumber,
        medicalCondition: formData.medicalCondition,
        requiredTreatment: formData.requiredTreatment,
        cost: Number(formData.cost),
        coordinatorNotes: formData.coordinatorNotes,
        medicalReports: newReports,
        updatedAt: new Date(),
      };
      
      await updateRequest(selectedRequest.id, updateData);
      
      toast.success(t('dashboard.admin.helps.editSuccess'));
      setIsEditOpen(false);
      setSelectedRequest(null);
      resetForm();
    } catch (error) {
      console.error('Error editing case:', error);
      toast.error(t('dashboard.admin.helps.editError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open add reports modal
  const openAddReportsModal = (request: TreatmentSupportRequest) => {
    setSelectedRequest(request);
    setNewReportFiles([]);
    setIsAddReportsOpen(true);
  };

  // Add medical reports to existing case
  const handleAddReports = async () => {
    if (!selectedRequest || newReportFiles.length === 0) return;
    
    setIsSubmitting(true);
    try {
      let existingReports = selectedRequest.medicalReports || [];
      
      // Handle legacy single report
      if (selectedRequest.medicalReportUrl && existingReports.length === 0) {
        existingReports = [{
          url: selectedRequest.medicalReportUrl,
          name: 'Medical Report',
          uploadedAt: selectedRequest.createdAt,
        }];
      }
      
      const newReports: MedicalReport[] = [];
      
      for (const file of newReportFiles) {
        const timestamp = Date.now();
        const url = await uploadFile(
          file,
          `treatment-reports/${selectedRequest.id}/${timestamp}_${file.name}`
        );
        newReports.push({
          url,
          name: file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
        });
      }

      await updateRequest(selectedRequest.id, {
        medicalReports: [...existingReports, ...newReports],
        updatedAt: new Date(),
      });
      
      toast.success(t('dashboard.admin.helps.reportsAdded'));
      setIsAddReportsOpen(false);
      setSelectedRequest(null);
      setNewReportFiles([]);
    } catch (error) {
      console.error('Error adding reports:', error);
      toast.error(t('dashboard.admin.helps.reportsError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Review a case (coordinator function)
  const handleReviewCase = async () => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const fitsAllCriteria = reviewData.socialStatusFits && reviewData.medicalConditionFits;
      
      // Use auto-approve setting if criteria fits
      const newStatus = fitsAllCriteria 
        ? (autoApproveEnabled ? 'approved' : 'pending_approval') 
        : 'rejected';
      
      await updateRequest(selectedRequest.id, {
        status: newStatus,
        coordinatorId: currentUser?.uid,
        coordinatorNotes: reviewData.coordinatorNotes,
        socialStatusFits: reviewData.socialStatusFits,
        medicalConditionFits: reviewData.medicalConditionFits,
        reviewedAt: new Date(),
        updatedAt: new Date(),
      });
      
      toast.success(fitsAllCriteria 
        ? t('dashboard.admin.helps.sentForApproval')
        : t('dashboard.admin.helps.caseRejected')
      );
      setIsReviewOpen(false);
      setSelectedRequest(null);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.reviewError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Approve/Reject case (admin function)
  const handleApprovalDecision = async () => {
    if (!selectedRequest || !approvalData.decision) return;
    
    setIsSubmitting(true);
    try {
      if (approvalData.decision === 'approve') {
        await updateRequest(selectedRequest.id, {
          status: 'approved',
          adminId: currentUser?.uid,
          adminNotes: approvalData.adminNotes,
          approvedAmount: Number(approvalData.approvedAmount),
          adminDecisionAt: new Date(),
        updatedAt: new Date(),
        });
        toast.success(t('dashboard.admin.helps.approveSuccess'));
    } else {
        await updateRequest(selectedRequest.id, {
          status: 'rejected',
          adminId: currentUser?.uid,
          adminNotes: approvalData.adminNotes,
          adminDecisionAt: new Date(),
          updatedAt: new Date(),
        });
        toast.success(t('dashboard.admin.helps.rejectSuccess'));
      }
      
      setIsApprovalOpen(false);
      setSelectedRequest(null);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.decisionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Direct approve (skip coordinator review)
  const handleDirectApprove = async () => {
    if (!selectedRequest) return;
    
    // First mark as reviewed, then approve
    setIsSubmitting(true);
    try {
      await updateRequest(selectedRequest.id, {
        status: 'approved',
        coordinatorId: currentUser?.uid,
        socialStatusFits: true,
        medicalConditionFits: true,
        reviewedAt: new Date(),
        adminId: currentUser?.uid,
        adminNotes: approvalData.adminNotes,
        approvedAmount: Number(approvalData.approvedAmount) || selectedRequest.cost,
        adminDecisionAt: new Date(),
        updatedAt: new Date(),
      });
      toast.success(t('dashboard.admin.helps.approveSuccess'));
      setIsApprovalOpen(false);
      setSelectedRequest(null);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.decisionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload invoices
  const handleUploadInvoices = async () => {
    if (!selectedRequest || pendingInvoices.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const invoiceUrls: string[] = [];
      const uploadedInvoices = [];
      
      for (const invoice of pendingInvoices) {
        const timestamp = Date.now();
        // file is optional — numbers-only entries skip the upload step.
        const url = invoice.file
          ? await uploadFile(
              invoice.file,
              `treatment-invoices/${selectedRequest.id}/${timestamp}_${invoice.file.name}`,
            )
          : '';
        invoiceUrls.push(url);
        uploadedInvoices.push({
          url,
          name: invoice.file?.name || `Invoice ${invoice.invoiceNumber || timestamp}`,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          providerId: invoice.providerId,
          providerName: invoice.providerName,
          providerNameAr: invoice.providerNameAr,
          vatNumber: invoice.vatNumber,
          invoiceDate: new Date(invoice.invoiceDate),
          invoiceNumber: invoice.invoiceNumber,
          totalBeforeDiscount: invoice.totalBeforeDiscount,
          totalAfterDiscount: invoice.totalAfterDiscount,
          taxableAmount: invoice.taxableAmount,
          nonTaxableAmount: invoice.nonTaxableAmount,
          taxAmount: invoice.taxAmount,
        });
      }

      await updateRequest(selectedRequest.id, {
        status: 'pending_invoices',
        invoiceUrls,
        invoices: uploadedInvoices,
        invoicesUploadedAt: new Date(),
        invoicesUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      toast.success(t('dashboard.admin.helps.invoicesUploaded'));
      setIsInvoiceOpen(false);
      setSelectedRequest(null);
      setPendingInvoices([]);
    } catch (error: any) {
      console.error('Upload invoices error:', error);
      const detail = error?.code || error?.message || String(error);
      toast.error(`${t('dashboard.admin.helps.invoiceError')}: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload payment slips
  const handleUploadPaymentSlips = async () => {
    if (!selectedRequest || paymentSlipFiles.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const paymentSlipUrls: string[] = [...(selectedRequest.paymentSlipUrls || [])];
      
      for (const file of paymentSlipFiles) {
        const timestamp = Date.now();
        const url = await uploadFile(
          file,
          `payment-slips/${selectedRequest.id}/${timestamp}_${file.name}`
        );
        paymentSlipUrls.push(url);
      }

      await updateRequest(selectedRequest.id, {
        paymentSlipUrls,
        paymentSlipsUploadedAt: new Date(),
        paymentSlipsUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      toast.success(t('dashboard.admin.helps.paymentSlipsUploaded'));
      setIsPaymentSlipOpen(false);
      setSelectedRequest(null);
      setPaymentSlipFiles([]);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.paymentSlipError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete case
  const handleDelete = async (id: string) => {
    if (!confirm(t('dashboard.admin.helps.confirmDelete'))) return;
    
    try {
      await deleteRequest(id);
      toast.success(t('dashboard.admin.helps.deleteSuccess'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.deleteError'));
    }
  };

  // Start editing details
  const startEditingDetails = () => {
    if (!selectedRequest) return;
    setEditFormData({
      nameArabic: selectedRequest.nameArabic || '',
      nameEnglish: selectedRequest.nameEnglish || '',
      idNumber: selectedRequest.idNumber || selectedRequest.nationalId || '',
      idType: selectedRequest.idType || 'national_id',
      dateOfBirth: selectedRequest.dateOfBirth ? new Date(selectedRequest.dateOfBirth).toISOString().split('T')[0] : '',
      idExpiryDate: selectedRequest.idExpiryDate ? new Date(selectedRequest.idExpiryDate).toISOString().split('T')[0] : '',
      nationality: selectedRequest.nationality || '',
      gender: selectedRequest.gender || 'male',
      maritalStatus: selectedRequest.maritalStatus || 'single',
      familyMembersCount: String(selectedRequest.familyMembersCount || ''),
      email: selectedRequest.email || '',
      educationLevel: selectedRequest.educationLevel || '',
      workSector: selectedRequest.workSector || '',
      profession: selectedRequest.profession || '',
      monthlyIncome: selectedRequest.monthlyIncome != null ? String(selectedRequest.monthlyIncome) : '',
      housingType: selectedRequest.housingType || '',
      rentAmount: selectedRequest.rentAmount != null ? String(selectedRequest.rentAmount) : '',
      city: selectedRequest.city || '',
      neighborhood: selectedRequest.neighborhood || '',
      phoneNumber: selectedRequest.phoneNumber || '',
      relativeMobileNumber: selectedRequest.relativeMobileNumber || '',
      isCritical: selectedRequest.isCritical ?? false,
      isHospitalized: selectedRequest.isHospitalized ?? false,
      hasInsurance: selectedRequest.hasInsurance ?? false,
      insuranceProvider: selectedRequest.insuranceProvider || '',
      insuranceCategory: selectedRequest.insuranceCategory || '',
      categoryId: selectedRequest.categoryId || '',
      medicalCondition: selectedRequest.medicalCondition || '',
      requiredTreatment: selectedRequest.requiredTreatment || '',
      cost: String(selectedRequest.cost || ''),
      coordinatorNotes: selectedRequest.coordinatorNotes || '',
    });
    setIsEditingDetails(true);
  };

  // Save edited details
  const saveEditedDetails = async () => {
    if (!selectedRequest) return;
    
    if (!editFormData.idNumber || !editFormData.phoneNumber) {
      toast.error(t('dashboard.admin.helps.validation.required'));
      return;
    }

    if (editFormData.idType === 'national_id' && !/^[12]\d{9}$/.test(editFormData.idNumber)) {
      toast.error(t('dashboard.admin.helps.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      await updateRequest(selectedRequest.id, {
        nameArabic: editFormData.nameArabic,
        nameEnglish: editFormData.nameEnglish,
        idNumber: editFormData.idNumber,
        idType: editFormData.idType,
        dateOfBirth: editFormData.dateOfBirth ? new Date(editFormData.dateOfBirth) : undefined,
        idExpiryDate: editFormData.idExpiryDate ? new Date(editFormData.idExpiryDate) : undefined,
        nationality: editFormData.nationality,
        gender: editFormData.gender,
        maritalStatus: editFormData.maritalStatus,
        familyMembersCount: Number(editFormData.familyMembersCount),
        email: editFormData.email || undefined,
        educationLevel: editFormData.educationLevel || undefined,
        workSector: editFormData.workSector || undefined,
        profession: editFormData.profession || undefined,
        monthlyIncome: editFormData.monthlyIncome ? Number(editFormData.monthlyIncome) : undefined,
        housingType: editFormData.housingType || undefined,
        rentAmount: editFormData.housingType === 'rented' && editFormData.rentAmount ? Number(editFormData.rentAmount) : undefined,
        city: editFormData.city || undefined,
        neighborhood: editFormData.neighborhood || undefined,
        phoneNumber: editFormData.phoneNumber,
        relativeMobileNumber: editFormData.relativeMobileNumber,
        medicalCondition: editFormData.medicalCondition,
        requiredTreatment: editFormData.requiredTreatment,
        cost: Number(editFormData.cost),
        isCritical: editFormData.isCritical,
        isHospitalized: editFormData.isHospitalized,
        hasInsurance: editFormData.hasInsurance,
        insuranceProvider: editFormData.insuranceProvider,
        insuranceCategory: editFormData.insuranceCategory,
        categoryId: editFormData.categoryId || '',
        categoryName: editFormData.categoryId
          ? (activeCategories.find(c => c.id === editFormData.categoryId)?.nameAr || activeCategories.find(c => c.id === editFormData.categoryId)?.name || '')
          : '',
        coordinatorNotes: editFormData.coordinatorNotes,
        updatedAt: new Date(),
      });

      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        nameArabic: editFormData.nameArabic,
        nameEnglish: editFormData.nameEnglish,
        idNumber: editFormData.idNumber,
        idType: editFormData.idType,
        dateOfBirth: editFormData.dateOfBirth ? new Date(editFormData.dateOfBirth) : prev.dateOfBirth,
        idExpiryDate: editFormData.idExpiryDate ? new Date(editFormData.idExpiryDate) : prev.idExpiryDate,
        nationality: editFormData.nationality,
        gender: editFormData.gender,
        maritalStatus: editFormData.maritalStatus,
        familyMembersCount: Number(editFormData.familyMembersCount),
        email: editFormData.email,
        educationLevel: editFormData.educationLevel || undefined,
        workSector: editFormData.workSector || undefined,
        profession: editFormData.profession,
        monthlyIncome: editFormData.monthlyIncome ? Number(editFormData.monthlyIncome) : undefined,
        housingType: editFormData.housingType || undefined,
        rentAmount: editFormData.rentAmount ? Number(editFormData.rentAmount) : undefined,
        city: editFormData.city,
        neighborhood: editFormData.neighborhood,
        phoneNumber: editFormData.phoneNumber,
        relativeMobileNumber: editFormData.relativeMobileNumber,
        medicalCondition: editFormData.medicalCondition,
        requiredTreatment: editFormData.requiredTreatment,
        cost: Number(editFormData.cost),
        isCritical: editFormData.isCritical,
        isHospitalized: editFormData.isHospitalized,
        hasInsurance: editFormData.hasInsurance,
        insuranceProvider: editFormData.insuranceProvider,
        insuranceCategory: editFormData.insuranceCategory,
        categoryId: editFormData.categoryId || '',
        categoryName: editFormData.categoryId
          ? (activeCategories.find(c => c.id === editFormData.categoryId)?.nameAr || activeCategories.find(c => c.id === editFormData.categoryId)?.name || '')
          : (prev.categoryName || ''),
        coordinatorNotes: editFormData.coordinatorNotes,
      } : null);
      
      toast.success(t('dashboard.admin.helps.editSuccess'));
      setIsEditingDetails(false);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.editError'));
    } finally {
      setIsSubmitting(false);
    }
  };


  // Get remaining budget for a link (after marketing deduction)
  const getLinkRemainingBudget = (linkId: string, excludeCaseId?: string, excludeEntryIdx?: number) => {
    const usage = linkUsageMap[linkId];
    if (!usage) return { exists: false, actualReceived: 0, availableForCases: 0, totalAllocated: 0, remaining: Infinity, count: 0 };
    let totalAllocated = usage.totalAllocated;
    if (excludeCaseId !== undefined && excludeEntryIdx !== undefined) {
      const req = requests.find(r => r.id === excludeCaseId);
      const entry = req?.donationEntries?.[excludeEntryIdx];
      if (entry) totalAllocated -= (entry.amount || 0);
    }
    const available = calcLinkAvailable(usage.actualReceived);
    const remaining = available - totalAllocated;
    return { exists: true, actualReceived: usage.actualReceived, availableForCases: available, totalAllocated, remaining, count: usage.count };
  };

  // Get remaining budget for a case (raw cost - sum of funded amounts)
  const getCaseRemainingBudget = (caseId: string, excludeLinkKey?: string) => {
    const req = requests.find(r => r.id === caseId);
    if (!req) return { caseCost: 0, totalFunded: 0, remaining: 0, hasVAT: false };
    // Use invoices total if available, otherwise fall back to estimated cost
    const invoicesTotal = req.invoices && req.invoices.length > 0
      ? req.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0)
      : 0;
    const hasInvoices = invoicesTotal > 0;
    const invoiceVAT = hasInvoices
      ? req.invoices!.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0)
      : 0;
    // The case's NET medical cost: invoice totals when available, else
    // the declared case.cost. donationEntries[].amount is also NET (it's
    // sourced from the link's availableForCases after marketing deduction),
    // so we compare NET to NET here. getEffectiveCost is the GROSS
    // fundraising target — never used as case-side denominator.
    const base = hasInvoices ? invoicesTotal : (req.cost || 0);
    const caseCost = base;
    const hasVAT = hasInvoices ? invoiceVAT > 0 : false;
    let totalFunded = 0;
    for (const entry of (req.donationEntries || [])) {
      if (excludeLinkKey && entry.linkId === excludeLinkKey) continue;
      totalFunded += entry.amount || 0;
    }
    return { caseCost, totalFunded, remaining: caseCost - totalFunded, hasVAT };
  };

  // Add donation entry
  const handleAllocateLink = async () => {
    if (!selectedRequest || !allocateLinkKey) return;

    const link = allLinks.find(l => l._key === allocateLinkKey);
    if (!link) return;

    // Duplicate detection
    const alreadyOnThisCase = selectedRequest.donationEntries?.some(
      e => e.linkId === link._key
    );
    if (alreadyOnThisCase) {
      toast.error(t('dashboard.admin.helps.linksView.duplicateLink'));
      return;
    }

    // Dual budget validation
    const entryAmount = Number(allocateAmount) || 0;
    if (entryAmount <= 0) return;

    const linkBudget = getLinkRemainingBudget(link._key);
    if (entryAmount > linkBudget.remaining) {
      toast.error(t('dashboard.admin.helps.linkBudget.exceedsRemaining', { remaining: linkBudget.remaining.toLocaleString('en-US') }));
      return;
    }
    const caseBudget = getCaseRemainingBudget(selectedRequest.id);
    if (caseBudget.caseCost > 0 && entryAmount > caseBudget.remaining) {
      toast.error(t('dashboard.admin.helps.caseBudget.exceedsRemaining', { remaining: caseBudget.remaining.toLocaleString('en-US') }));
      return;
    }

    try {
      assertCategoryMatch(link, selectedRequest);
    } catch (e: any) {
      toast.error(t(e.code === 'CATEGORY_MISSING'
        ? 'dashboard.admin.helps.alloc.categoryMissing'
        : 'dashboard.admin.helps.alloc.categoryMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      const newEntry: DonationEntry = {
        linkId: link._key,
        platform: link.platform,
        caseNumber: link.platformCaseNumber,
        amount: entryAmount,
        addedAt: new Date(),
        addedBy: currentUser?.uid,
      };

      const existingEntries = selectedRequest.donationEntries || [];
      const updatedEntries = [...existingEntries, newEntry];

      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      setAllocateLinkKey('');
      setAllocateAmount('');
      setShowAllocateLink(false);
      toast.success(t('dashboard.admin.helps.linksView.linkAssigned'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove donation entry
  const handleRemoveDonation = async (index: number) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const existingEntries = selectedRequest.donationEntries || [];
      const updatedEntries = existingEntries.filter((_, i) => i !== index);
      
      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      toast.success(t('dashboard.admin.helps.donationRemoved'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing a donation entry
  const startEditingDonation = (index: number) => {
    const entry = selectedRequest?.donationEntries?.[index];
    if (entry) {
      setEditingDonationIndex(index);
      setEditingDonation({
        linkId: entry.linkId,
        platform: entry.platform,
        caseNumber: entry.caseNumber,
        amount: entry.amount?.toString() || '',
      });
    }
  };

  // Save edited donation entry
  const saveEditedDonation = async () => {
    if (!selectedRequest || editingDonationIndex === null) return;

    // Dual budget validation
    const editAmount = Number(editingDonation.amount) || 0;
    const budget = getLinkRemainingBudget(editingDonation.linkId, selectedRequest.id, editingDonationIndex);
    if (budget.exists && budget.actualReceived > 0) {
      if (editAmount > budget.remaining) {
        toast.error(t('dashboard.admin.helps.linkBudget.exceedsRemaining', { remaining: budget.remaining.toLocaleString('en-US') }));
        return;
      }
    }
    const caseBudgetEdit = getCaseRemainingBudget(selectedRequest.id, editingDonation.linkId);
    if (caseBudgetEdit.caseCost > 0 && editAmount > caseBudgetEdit.remaining) {
      toast.error(t('dashboard.admin.helps.caseBudget.exceedsRemaining', { remaining: caseBudgetEdit.remaining.toLocaleString('en-US') }));
      return;
    }

    const linkForGate = allLinks.find(l => l._key === editingDonation.linkId);
    if (linkForGate && selectedRequest) {
      try {
        assertCategoryMatch(linkForGate, selectedRequest);
      } catch (e: any) {
        toast.error(t(e.code === 'CATEGORY_MISSING'
          ? 'dashboard.admin.helps.alloc.categoryMissing'
          : 'dashboard.admin.helps.alloc.categoryMismatch'));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const existingEntries = selectedRequest.donationEntries || [];
      const updatedEntries = existingEntries.map((entry, i) => {
        if (i === editingDonationIndex) {
          const updatedEntry: DonationEntry = {
            linkId: editingDonation.linkId,
            platform: editingDonation.platform as 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
            caseNumber: editingDonation.caseNumber,
            amount: editAmount,
            addedAt: entry.addedAt,
            addedBy: entry.addedBy,
          };
          return updatedEntry;
        }
        return entry;
      });
      
      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      setEditingDonationIndex(null);
      toast.success(t('dashboard.admin.helps.donationUpdated'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add case update
  const handleAddUpdate = async () => {
    if (!selectedRequest || !newUpdateText.trim()) return;
    
    setIsSubmitting(true);
    try {
      const newUpdate: CaseUpdate = {
        id: `update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: newUpdateText.trim(),
        statusAtTime: selectedRequest.status,
        createdAt: new Date(),
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        createdByRole: 'admin',
      };
      
      const existingUpdates = selectedRequest.updates || [];
      const updatedUpdates = [...existingUpdates, newUpdate];
      
      await updateRequest(selectedRequest.id, {
        updates: updatedUpdates,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, updates: updatedUpdates } : null);
      setNewUpdateText('');
      setShowAddUpdate(false);
      toast.success(t('dashboard.admin.helps.updates.addUpdate'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete case update (admin only)
  const handleDeleteUpdate = async (updateId: string) => {
    if (!selectedRequest) return;
    if (!confirm(t('dashboard.admin.helps.updates.confirmDelete'))) return;
    
    setIsSubmitting(true);
    try {
      const existingUpdates = selectedRequest.updates || [];
      const updatedUpdates = existingUpdates.filter(u => u.id !== updateId);
      
      await updateRequest(selectedRequest.id, {
        updates: updatedUpdates,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, updates: updatedUpdates } : null);
      toast.success(t('dashboard.admin.helps.updates.updateDeleted'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload ID document
  const handleUploadIdDocument = async (file: File) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const timestamp = Date.now();
      const url = await uploadFile(
        file,
        `id-documents/${selectedRequest.id}/${timestamp}_${file.name}`
      );
      
      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        idDocumentName: file.name,
        idDocumentUploadedAt: new Date(),
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        idDocumentUrl: url,
        idDocumentName: file.name,
      } : null);
      
      toast.success(t('dashboard.admin.helps.idDocumentUploaded'));
    } catch (error) {
      console.error('ID document upload error:', error);
      toast.error(t('dashboard.admin.helps.idDocumentError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add report from details view
  const handleAddReportFromDetails = async () => {
    if (!selectedRequest || pendingReports.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const existingReports = selectedRequest.medicalReports || [];
      if (selectedRequest.medicalReportUrl && existingReports.length === 0) {
        existingReports.push({
          url: selectedRequest.medicalReportUrl,
          name: 'Medical Report',
          uploadedAt: selectedRequest.createdAt,
        });
      }
      
      const newReports = [...existingReports];
      
      for (const report of pendingReports) {
        const timestamp = Date.now();
        const reportUrl = await uploadFile(
          report.file,
          `treatment-reports/${selectedRequest.id}/${timestamp}_${report.file.name}`
        );
        
        newReports.push({
        url: reportUrl,
          name: report.file.name,
        uploadedAt: new Date(),
        uploadedBy: currentUser?.uid,
          providerId: report.providerId,
          providerName: report.providerName,
          department: report.department,
          reportDate: report.reportDate ? new Date(report.reportDate) : undefined,
        });
      }
      
      await updateRequest(selectedRequest.id, {
        medicalReports: newReports,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        medicalReports: newReports,
      } : null);
      
      toast.success(t('dashboard.admin.helps.reportsAdded'));
      setPendingReports([]);
    } catch (error) {
      toast.error(t('dashboard.admin.helps.reportsError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add invoice from details view
  const handleAddInvoiceFromDetails = async () => {
    if (!selectedRequest || pendingInvoices.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const existingInvoiceUrls = selectedRequest.invoiceUrls || [];
      const existingInvoices = selectedRequest.invoices || [];
      const newInvoiceUrls = [...existingInvoiceUrls];
      const newInvoices = [...existingInvoices];
      
      for (const invoice of pendingInvoices) {
        const timestamp = Date.now();
        // file is optional — numbers-only entries skip the upload step.
        const url = invoice.file
          ? await uploadFile(
              invoice.file,
              `treatment-invoices/${selectedRequest.id}/${timestamp}_${invoice.file.name}`,
            )
          : '';
        newInvoiceUrls.push(url);
        newInvoices.push({
          url,
          name: invoice.file?.name || `Invoice ${invoice.invoiceNumber || timestamp}`,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          providerId: invoice.providerId,
          providerName: invoice.providerName,
          providerNameAr: invoice.providerNameAr,
          vatNumber: invoice.vatNumber,
          invoiceDate: new Date(invoice.invoiceDate),
          invoiceNumber: invoice.invoiceNumber,
          totalBeforeDiscount: invoice.totalBeforeDiscount,
          totalAfterDiscount: invoice.totalAfterDiscount,
          taxableAmount: invoice.taxableAmount,
          nonTaxableAmount: invoice.nonTaxableAmount,
          taxAmount: invoice.taxAmount,
        });
      }
      
      await updateRequest(selectedRequest.id, {
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
        invoicesUploadedAt: new Date(),
        invoicesUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
      } : null);
      
      toast.success(t('dashboard.admin.helps.invoiceAdded'));
      setPendingInvoices([]);
    } catch (error: any) {
      console.error('Add invoice error:', error);
      const detail = error?.code || error?.message || String(error);
      toast.error(`${t('dashboard.admin.helps.invoiceError')}: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload progress reports
  const handleUploadProgressReports = async () => {
    if (!selectedRequest || pendingProgressReports.length === 0) return;
    try {
      const timestamp = Date.now();
      const uploaded = await Promise.all(pendingProgressReports.map(async (p, i) => {
        if (!p.file) throw new Error('missing file');
        const path = `treatment-progress-reports/${selectedRequest.id}/${timestamp}_${i}_${p.file.name}`;
        const url = await uploadFile(p.file, path);
        return {
          url,
          name: p.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          reportDate: p.reportDate ? new Date(p.reportDate) : undefined,
          providerId: p.providerId,
          providerName: p.providerName,
          department: p.department || undefined,
          notes: p.notes || undefined,
        };
      }));
      const previousCount = (selectedRequest.progressReports || []).length;
      const next = [...(selectedRequest.progressReports || []), ...uploaded];
      await updateRequest(selectedRequest.id, { progressReports: next, updatedAt: new Date() });
      setSelectedRequest(prev => prev ? { ...prev, progressReports: next } : null);
      setPendingProgressReports([]);
      toast.success(t('common.progressReports.toast.uploadSuccess'));
      if (shouldOfferStartTreatment(selectedRequest.status, previousCount, next.length)) {
        setStartPromptRequest({ ...selectedRequest, progressReports: next });
      }
    } catch (e) {
      console.error(e);
      toast.error(t('common.progressReports.toast.uploadFailed'));
    }
  };

  // Add payment slip from details view
  const handleAddPaymentSlipFromDetails = async () => {
    if (!selectedRequest || !newPaymentSlipFile) return;
    
    setIsSubmitting(true);
    try {
      const timestamp = Date.now();
      const slipUrl = await uploadFile(
        newPaymentSlipFile,
        `payment-slips/${selectedRequest.id}/${timestamp}_${newPaymentSlipFile.name}`
      );
      
      const existingSlips = selectedRequest.paymentSlipUrls || [];
      const updatedSlips = [...existingSlips, slipUrl];
      
      await updateRequest(selectedRequest.id, {
        paymentSlipUrls: updatedSlips,
        paymentSlipsUploadedAt: new Date(),
        paymentSlipsUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        paymentSlipUrls: updatedSlips,
      } : null);
      
      toast.success(t('dashboard.admin.helps.paymentSlipAdded'));
      setNewPaymentSlipFile(null);
    } catch (error) {
      console.error('Error uploading payment slip:', error);
      toast.error(t('dashboard.admin.helps.paymentSlipError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete report
  const handleDeleteReport = async (reportIndex: number) => {
    if (!selectedRequest) return;
    if (!confirm(t('dashboard.admin.helps.confirmDeleteReport'))) return;
    
    const existingReports = selectedRequest.medicalReports || [];
    const updatedReports = existingReports.filter((_, i) => i !== reportIndex);
    
    setIsSubmitting(true);
    try {
      await updateRequest(selectedRequest.id, {
        medicalReports: updatedReports,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        medicalReports: updatedReports,
      } : null);
      
      toast.success(t('dashboard.admin.helps.reportDeleted'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.deleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open review modal
  const openReviewModal = (request: TreatmentSupportRequest) => {
    setSelectedRequest(request);
    setReviewData({
      socialStatusFits: request.socialStatusFits || false,
      medicalConditionFits: request.medicalConditionFits || false,
      coordinatorNotes: request.coordinatorNotes || '',
    });
    setIsReviewOpen(true);
  };

  // Open approval modal
  const openApprovalModal = (request: TreatmentSupportRequest, decision: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setApprovalData({
      decision,
      approvedAmount: String(request.cost || ''),
      adminNotes: '',
    });
    setIsApprovalOpen(true);
  };

  // Approve for treatment (after donation publishing)
  const handleApproveTreatment = async (request: TreatmentSupportRequest) => {
    try {
      await updateRequest(request.id, {
        status: 'approved_treatment',
        updatedAt: new Date(),
      });
      toast.success(t('dashboard.admin.helps.approvedTreatmentSuccess'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.approvedTreatmentError'));
    }
  };

  // Delete invoice
  const handleDeleteInvoice = async (invoiceIndex: number) => {
    if (!selectedRequest) return;
    if (!confirm(t('dashboard.admin.helps.confirmDeleteInvoice'))) return;
    
    try {
      const existingInvoiceUrls = selectedRequest.invoiceUrls || [];
      const existingInvoices = selectedRequest.invoices || [];
      
      const newInvoiceUrls = existingInvoiceUrls.filter((_, i) => i !== invoiceIndex);
      const newInvoices = existingInvoices.filter((_, i) => i !== invoiceIndex);
      
      await updateRequest(selectedRequest.id, {
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
      } : null);
      
      toast.success(t('dashboard.admin.helps.invoiceDeleted'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.invoiceDeleteError'));
    }
  };

  // Delete legacy invoice (without detailed metadata)
  const handleDeleteLegacyInvoice = async (invoiceIndex: number) => {
    if (!selectedRequest) return;
    if (!confirm(t('dashboard.admin.helps.confirmDeleteInvoice'))) return;
    
    try {
      const existingInvoiceUrls = selectedRequest.invoiceUrls || [];
      const newInvoiceUrls = existingInvoiceUrls.filter((_, i) => i !== invoiceIndex);
      
      await updateRequest(selectedRequest.id, {
        invoiceUrls: newInvoiceUrls,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        invoiceUrls: newInvoiceUrls,
      } : null);
      
      toast.success(t('dashboard.admin.helps.invoiceDeleted'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.invoiceDeleteError'));
    }
  };

  const handleUpdateInvoiceMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingInvoices = selectedRequest.invoices || [];
    if (editingFileIndex >= existingInvoices.length) return;

    setIsSubmitting(true);
    try {
      const updatedInvoices = [...existingInvoices];
      const editProvider = activeProviders.find(p => p.id === editingInvoiceData.providerId);
      updatedInvoices[editingFileIndex] = {
        ...updatedInvoices[editingFileIndex],
        providerId: editingInvoiceData.providerId,
        providerName: editingInvoiceData.providerName,
        providerNameAr: editProvider?.nameAr || editingInvoiceData.providerName,
        vatNumber: editingInvoiceData.vatNumber,
        invoiceDate: new Date(editingInvoiceData.invoiceDate),
        invoiceNumber: editingInvoiceData.invoiceNumber,
        totalBeforeDiscount: Number(editingInvoiceData.totalBeforeDiscount) || 0,
        totalAfterDiscount: Number(editingInvoiceData.totalAfterDiscount) || 0,
        taxableAmount: Number(editingInvoiceData.taxableAmount) || 0,
        nonTaxableAmount: Number(editingInvoiceData.nonTaxableAmount) || 0,
        taxAmount: Number(editingInvoiceData.taxAmount) || 0,
      };

      await updateRequest(selectedRequest.id, {
        invoices: updatedInvoices,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, invoices: updatedInvoices } : null);
      setIsEditingInvoice(false);
      toast.success(t('dashboard.admin.helps.invoiceUpdated'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.invoiceUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateReportMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingReports = selectedRequest.medicalReports || [];
    if (editingFileIndex >= existingReports.length) return;

    setIsSubmitting(true);
    try {
      const updatedReports = [...existingReports];
      const editProvider = activeProviders.find(p => p.id === editingReportData.providerId);
      updatedReports[editingFileIndex] = {
        ...updatedReports[editingFileIndex],
        providerId: editingReportData.providerId,
        providerName: editProvider?.name || updatedReports[editingFileIndex].providerName || '',
        department: editingReportData.department,
        reportDate: editingReportData.reportDate ? new Date(editingReportData.reportDate) : updatedReports[editingFileIndex].reportDate,
      };

      await updateRequest(selectedRequest.id, {
        medicalReports: updatedReports,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, medicalReports: updatedReports } : null);
      setIsEditingReport(false);
      toast.success(t('dashboard.admin.helps.reportUpdated'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.reportUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProgressReportMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingReports = selectedRequest.progressReports || [];
    if (editingFileIndex >= existingReports.length) return;

    setIsSubmitting(true);
    try {
      const updatedReports = [...existingReports];
      const editProvider = activeProviders.find(p => p.id === editingProgressReportData.providerId);
      updatedReports[editingFileIndex] = {
        ...updatedReports[editingFileIndex],
        providerId: editingProgressReportData.providerId,
        providerName: editProvider?.name || updatedReports[editingFileIndex].providerName || '',
        department: editingProgressReportData.department || undefined,
        reportDate: editingProgressReportData.reportDate ? new Date(editingProgressReportData.reportDate) : updatedReports[editingFileIndex].reportDate,
        notes: editingProgressReportData.notes || undefined,
      };

      await updateRequest(selectedRequest.id, {
        progressReports: updatedReports,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, progressReports: updatedReports } : null);
      setIsEditingProgressReport(false);
      toast.success(t('dashboard.admin.helps.reportUpdated'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.reportUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProgressReport = async (reportIndex: number) => {
    if (!selectedRequest) return;
    const next = (selectedRequest.progressReports || []).filter((_, i) => i !== reportIndex);
    try {
      await updateRequest(selectedRequest.id, { progressReports: next, updatedAt: new Date() });
      setSelectedRequest(prev => prev ? { ...prev, progressReports: next } : null);
      toast.success(t('common.progressReports.toast.deleteSuccess'));
    } catch {
      toast.error(t('common.progressReports.toast.deleteFailed'));
    }
  };

  // Revert from approved_treatment back to approved
  const handleRevertToApproved = async (request: TreatmentSupportRequest) => {
    try {
      await updateRequest(request.id, {
        status: 'approved',
        updatedAt: new Date(),
      });
      toast.success(t('dashboard.admin.helps.revertToApprovedSuccess'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.revertToApprovedError'));
    }
  };

  // Move approved_treatment -> treatment_started (requires >=1 progress report)
  const handleStartTreatment = async (request: TreatmentSupportRequest) => {
    const gate = canStartTreatment(request);
    if (!gate.ok) {
      toast.error(t('dashboard.admin.helps.startTreatmentGateError'));
      return;
    }
    try {
      const now = new Date();
      const newUpdate = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: t('dashboard.admin.helps.timeline.treatmentStartedContent'),
        statusAtTime: 'treatment_started' as const,
        createdAt: now,
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || t('common.unknownUser'),
        createdByRole: 'admin' as const,
      };
      await updateRequest(request.id, {
        status: 'treatment_started',
        treatmentStartedAt: now,
        treatmentStartedBy: currentUser?.uid,
        updatedAt: now,
        updates: [...(request.updates || []), newUpdate],
      });
      setSelectedRequest(prev => prev && prev.id === request.id ? {
        ...prev,
        status: 'treatment_started',
        treatmentStartedAt: now,
        treatmentStartedBy: currentUser?.uid,
        updates: [...(request.updates || []), newUpdate],
      } : prev);
      toast.success(t('dashboard.admin.helps.startTreatmentSuccess'));
    } catch (error) {
      console.error(error);
      toast.error(t('dashboard.admin.helps.startTreatmentError'));
    }
  };

  // Move treatment_started -> pending_invoices (label "Treatment Completed")
  const handleMarkCompleted = async (request: TreatmentSupportRequest) => {
    // Check if invoices exist (support both new and legacy structure)
    if (!request.invoices?.length && !request.invoiceUrls?.length) {
      toast.error(t('dashboard.admin.helps.noInvoicesToMarkTreated'));
      return;
    }
    try {
      const now = new Date();
      const newUpdate = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: t('dashboard.admin.helps.timeline.treatmentCompletedContent'),
        statusAtTime: 'pending_invoices' as const,
        createdAt: now,
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || t('common.unknownUser'),
        createdByRole: 'admin' as const,
      };
      await updateRequest(request.id, {
        status: 'pending_invoices',
        updatedAt: now,
        updates: [...(request.updates || []), newUpdate],
      });
      toast.success(t('dashboard.admin.helps.markTreatedSuccess'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.markTreatedError'));
    }
  };

  // Pay invoices / Close case (move to completed) - requires payment slips
  const handlePayCase = async (request: TreatmentSupportRequest) => {
    // Check if payment slips exist
    if (!request.paymentSlipUrls?.length) {
      toast.error(t('dashboard.admin.helps.noSlipsToPay'));
      return;
    }
    try {
      await updateRequest(request.id, {
        status: 'completed',
        updatedAt: new Date(),
      });
      toast.success(t('dashboard.admin.helps.closeCaseSuccess'));
    } catch (error) {
      toast.error(t('dashboard.admin.helps.closeCaseError'));
    }
  };

  // Sort handler
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort function
  const sortRequests = (list: TreatmentSupportRequest[]) => {
    return [...list].sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortColumn) {
        case 'nationalId':
          aVal = a.nationalId || a.idNumber || '';
          bVal = b.nationalId || b.idNumber || '';
          break;
        case 'createdAt':
          aVal = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          break;
        case 'name':
          aVal = dir === 'rtl' ? (a.nameArabic || a.applicantName || '') : (a.nameEnglish || a.applicantName || '');
          bVal = dir === 'rtl' ? (b.nameArabic || b.applicantName || '') : (b.nameEnglish || b.applicantName || '');
          break;
        case 'age':
          aVal = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
          bVal = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
          break;
        case 'condition':
          aVal = a.medicalCondition || '';
          bVal = b.medicalCondition || '';
          break;
        case 'cost':
          aVal = getNetMedicalCost(a);
          bVal = getNetMedicalCost(b);
          break;
        case 'totalReceived':
          aVal = a.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
          bVal = b.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
          break;
        case 'updatedAt':
          aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          break;
        case 'lastUpdate':
          aVal = a.updates?.length ? new Date(a.updates[a.updates.length - 1].createdAt).getTime() : 0;
          bVal = b.updates?.length ? new Date(b.updates[b.updates.length - 1].createdAt).getTime() : 0;
          break;
        default:
          aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      }
      
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Render sort icon
  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ms-1 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ms-1" /> : <ArrowDown className="h-3 w-3 ms-1" />;
  };


  const handleExportCases = () => {
    const formatDate = (d: any) => {
      if (!d) return '';
      const date = d instanceof Date ? d : d.toDate ? d.toDate() : new Date(d);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
    };

    const casesData = requests.map(r => ({
      'Case #': r.caseNumber || '',
      'Name (Arabic)': r.nameArabic || r.applicantName || '',
      'Name (English)': r.nameEnglish || '',
      'ID Number': r.idNumber || r.nationalId || '',
      'Phone': r.phoneNumber || '',
      'Nationality': r.nationality || '',
      'Gender': r.gender || '',
      'Marital Status': r.maritalStatus || '',
      'Family Members': r.familyMembersCount || '',
      'Classification': r.categoryName || '',
      'Medical Condition': r.medicalCondition || '',
      'Required Treatment': r.requiredTreatment || '',
      'Medical cost': getNetMedicalCost(r),
      'Approved Amount': r.approvedAmount || '',
      'Total Donations': r.donationEntries?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0,
      'Status': r.status || '',
      'Created At': formatDate(r.createdAt),
      'Created By': r.createdBy || '',
      'Coordinator Notes': r.coordinatorNotes || '',
      'Admin Notes': r.adminNotes || '',
      'Reports Count': r.medicalReports?.length || (r.medicalReportUrl ? 1 : 0),
      'Invoices Count': r.invoices?.length || r.invoiceUrls?.length || 0,
      'Donation Links': r.donationEntries?.length || 0,
    }));

    const linksData: any[] = [];
    requests.forEach(r => {
      r.donationEntries?.forEach(d => {
        linksData.push({
          'Case #': r.caseNumber || '',
          'Patient Name': r.nameArabic || r.applicantName || '',
          'ID Number': r.idNumber || r.nationalId || '',
          'Case Status': r.status || '',
          'Medical cost': getNetMedicalCost(r),
          'Platform': d.platform || '',
          'Platform Case #': d.caseNumber || '',
          'Donation URL': d.url || '',
          'Donor Name': d.donorName || '',
          'Amount': d.amount || 0,
          'Added At': formatDate(d.addedAt),
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(casesData);
    const ws2 = XLSX.utils.json_to_sheet(linksData.length > 0 ? linksData : [{ 'No Data': '' }]);

    const setCols = (ws: XLSX.WorkSheet, widths: number[]) => {
      ws['!cols'] = widths.map(w => ({ wch: w }));
    };
    setCols(ws1, [8, 25, 25, 15, 15, 15, 10, 15, 12, 20, 30, 30, 12, 12, 12, 15, 12, 12, 30, 30, 12, 12, 12]);
    setCols(ws2, [8, 25, 15, 15, 12, 12, 15, 40, 20, 12, 12, 12]);

    XLSX.utils.book_append_sheet(wb, ws1, language === 'ar' ? 'الحالات' : 'Cases');
    XLSX.utils.book_append_sheet(wb, ws2, language === 'ar' ? 'روابط التبرع' : 'Donation Links');

    XLSX.writeFile(wb, `cases_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const renderRequestsTable = (requestsList: TreatmentSupportRequest[], actions: ('review' | 'approve' | 'view' | 'approveTreatment' | 'startTreatment' | 'markTreated' | 'payCase' | 'revertToApproved')[]) => {
    const totalCost = requestsList.reduce((sum, r) => sum + getNetMedicalCost(r), 0);
    const totalDonations = requestsList.reduce((sum, r) => sum + (r.donationEntries?.reduce((s: number, e: any) => s + (e.amount || 0), 0) || 0), 0);

    return (
    <div className="rounded-md border overflow-x-auto">
      <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.admin.helps.table.caseNumber')}
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('createdAt')}>
              <span className="flex items-center">{t('dashboard.admin.helps.table.creationDate')}<SortIcon column="createdAt" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('name')}>
              <span className="flex items-center">{t('dashboard.admin.helps.table.name')}<SortIcon column="name" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('age')}>
              <span className="flex items-center">{t('dashboard.admin.helps.table.age')}<SortIcon column="age" /></span>
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.admin.helps.table.classification')}
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('cost')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center">{t('dashboard.admin.helps.table.cost')}<SortIcon column="cost" /></span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="inline-flex items-center gap-1">{totalCost.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('totalReceived')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center">{t('dashboard.admin.helps.table.totalReceived')}<SortIcon column="totalReceived" /></span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="inline-flex items-center gap-1">{totalDonations.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('updatedAt')}>
              <span className="flex items-center">{t('dashboard.admin.helps.table.lastModified')}<SortIcon column="updatedAt" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('lastUpdate')}>
              <span className="flex items-center">{t('dashboard.admin.helps.table.lastUpdate')}<SortIcon column="lastUpdate" /></span>
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.admin.helps.table.links')}
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.admin.helps.table.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requestsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                {t('dashboard.admin.helps.noCases')}
              </TableCell>
            </TableRow>
          ) : (
            sortRequests(requestsList).map((request) => (
              <TableRow key={request.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedRequest(request); setIsViewOpen(true); }}>
                <TableCell className="font-mono text-muted-foreground">{request.caseNumber ?? '-'}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {request.createdAt
                    ? formatLocalDate(
                        request.createdAt instanceof Date
                          ? request.createdAt
                          : (request.createdAt as any)?.toDate?.() || new Date(request.createdAt),
                        'PP',
                      )
                    : '-'}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex flex-col">
                    <span>{dir === 'rtl' ? (request.nameArabic || request.applicantName) : (request.nameEnglish || request.applicantName)}</span>
                    <span className="text-xs text-muted-foreground font-mono" dir="ltr">{request.nationalId}</span>
                  </div>
                </TableCell>
                <TableCell>{request.dateOfBirth ? Math.floor((Date.now() - new Date(request.dateOfBirth).getTime()) / 31557600000) : (request.age || '-')}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {!readOnly ? (
                    <CategorySelect
                      value={request.categoryId}
                      activeCategories={activeCategories}
                      onChange={(newId) => updateRequestCategory(request, newId)}
                    />
                  ) : (
                    <span className="text-sm">{getCategoryLabel(request)}</span>
                  )}
                </TableCell>
                <TableCell><span className="inline-flex items-center gap-1">{getNetMedicalCost(request).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span></TableCell>
                <TableCell>
                  {(() => {
                    const allocated = (request.donationEntries || []).reduce(
                      (sum: number, e: any) => sum + (e.amount || 0),
                      0,
                    );
                    const netCost = getNetMedicalCost(request);
                    const fullyFunded = netCost > 0 && allocated >= netCost;
                    return (
                      <span
                        className={`inline-flex items-center gap-1${
                          fullyFunded ? ' text-green-700 bg-green-50 px-2 py-0.5 rounded-full' : ''
                        }`}
                      >
                        {allocated.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {request.updatedAt ? formatLocalDate(new Date(request.updatedAt), 'PP') : '-'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {request.updates && request.updates.length > 0 
                    ? formatLocalDate(
                        request.updates[request.updates.length - 1].createdAt instanceof Date 
                          ? request.updates[request.updates.length - 1].createdAt 
                          : (request.updates[request.updates.length - 1].createdAt as any)?.toDate?.() || new Date(request.updates[request.updates.length - 1].createdAt), 
                        'PP'
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {(request.donationEntries?.length || 0) > 0 ? (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">{request.donationEntries?.length}</Badge>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedRequest(request); setIsViewOpen(true); }}>
                        <Eye className="h-4 w-4 me-2" />
                        {t('dashboard.admin.helps.view')}
                      </DropdownMenuItem>

                      {!readOnly && (
                        <>
                          {request.status !== 'completed' && !actions.includes('review') && !actions.includes('approve') && !actions.includes('approveTreatment') && !actions.includes('startTreatment') && !actions.includes('markTreated') && !actions.includes('payCase') && (
                            <DropdownMenuItem onClick={() => openEditModal(request)}>
                              <Edit className="h-4 w-4 me-2" />
                              {t('dashboard.admin.helps.edit')}
                            </DropdownMenuItem>
                          )}

                          {request.status !== 'completed' && !actions.includes('review') && !actions.includes('approve') && !actions.includes('approveTreatment') && !actions.includes('startTreatment') && !actions.includes('markTreated') && !actions.includes('payCase') && (
                            <DropdownMenuItem onClick={() => openAddReportsModal(request)}>
                              <FilePlus className="h-4 w-4 me-2" />
                              {t('dashboard.admin.helps.addReports')}
                            </DropdownMenuItem>
                          )}

                          {actions.includes('review') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openReviewModal(request)}>
                                <ClipboardCheck className="h-4 w-4 me-2 text-blue-600" />
                                {t('dashboard.admin.helps.review')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('approve') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openApprovalModal(request, 'approve')}>
                                <CheckCircle className="h-4 w-4 me-2 text-green-600" />
                                {t('dashboard.admin.helps.approve')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('approveTreatment') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleApproveTreatment(request)}>
                                <CheckCircle className="h-4 w-4 me-2 text-teal-600" />
                                {t('dashboard.admin.helps.approveTreatment')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('startTreatment') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleStartTreatment(request)}
                                disabled={!canStartTreatment(request).ok}
                              >
                                <CheckCircle className="h-4 w-4 me-2 text-indigo-600" />
                                {t('dashboard.admin.helps.startTreatmentButton')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('markTreated') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleMarkCompleted(request)}
                                disabled={!request.invoices?.length && !request.invoiceUrls?.length}
                              >
                                <CheckCircle className="h-4 w-4 me-2 text-cyan-600" />
                                {t('dashboard.admin.helps.markTreated')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('revertToApproved') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleRevertToApproved(request)}>
                                <Undo2 className="h-4 w-4 me-2 text-orange-600" />
                                {t('dashboard.admin.helps.revertToApproved')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {actions.includes('payCase') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handlePayCase(request)}
                                disabled={!request.paymentSlipUrls?.length}
                              >
                                <CheckCircle className="h-4 w-4 me-2 text-emerald-600" />
                                {t('dashboard.admin.helps.closeCase')}
                              </DropdownMenuItem>
                            </>
                          )}

                          {request.status !== 'rejected' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openApprovalModal(request, 'reject')} className="text-red-600 focus:text-red-600">
                                <XCircle className="h-4 w-4 me-2" />
                                {t('dashboard.admin.helps.reject')}
                              </DropdownMenuItem>
                            </>
                          )}

                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(request.id)} className="text-destructive">
                            <Trash2 className="h-4 w-4 me-2" />
                            {t('dashboard.admin.helps.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </TooltipProvider>
      </div>
    );
  };

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <Heart className="h-7 w-7 text-primary" />
            {titleOverride || t('dashboard.admin.helps.title')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {descriptionOverride || t('dashboard.admin.helps.description')}
          </p>
        </div>
      </div>

      {/* View Switcher */}
      <div className="flex items-stretch gap-2">
          <div className="flex w-full items-stretch rounded-lg border bg-muted p-0.5 gap-0.5">
            <button
              onClick={() => setActiveView('cases')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'cases' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Heart className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewCases')}
              {requests.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{requests.length}</Badge>}
            </button>
            <button
              onClick={() => setActiveView('awareness')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'awareness' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <BookOpen className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewAwareness')}
            </button>
            <button
              onClick={() => setActiveView('training')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'training' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <GraduationCap className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewTraining')}
            </button>
            <button
              onClick={() => setActiveView('links')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'links' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <HandHelping className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewLinks')}
              {allLinks.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{allLinks.length}</Badge>}
            </button>
            {/* Marketing payments view hidden per ops request (2026-07-14) — view + data kept, only the switcher button removed */}
            {false && (
              <button
                onClick={() => setActiveView('marketing')}
                className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'marketing' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Wallet className="h-3.5 w-3.5" />
                {t('dashboard.admin.helps.viewMarketing')}
                {marketingPayments.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{marketingPayments.length}</Badge>}
              </button>
            )}
            <button
              onClick={() => setActiveView('vat')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'vat' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <PercentSquare className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewVat')}
              {vatRefunds.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{vatRefunds.length}</Badge>}
            </button>
            <button
              onClick={() => setActiveView('cashbacks')}
              className={`inline-flex flex-1 justify-center items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${activeView === 'cashbacks' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Banknote className="h-3.5 w-3.5" />
              {t('dashboard.admin.helps.viewCashbacks')}
              {cashbacks.filter(c => c.status === 'pending').length > 0 && (
                <Badge className="bg-yellow-500 text-white text-xs px-1.5 py-0">{cashbacks.filter(c => c.status === 'pending').length}</Badge>
              )}
            </button>
          </div>
      </div>

      {/* Cases View */}
      {activeView === 'cases' && (
        <>
          {/* Search + Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t('dashboard.admin.helps.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
                <span className="font-semibold text-foreground">{filteredRequests.length}</span>
                {t('dashboard.admin.helps.casesCount')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && hasMissingCaseNumbers && (
                <Button variant="outline" onClick={handleBackfillCaseNumbers} disabled={isBackfilling} className="gap-1.5">
                  {isBackfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                  <span className="hidden sm:inline">{isBackfilling ? 'Assigning...' : 'Assign Case Numbers'}</span>
                </Button>
              )}
              <Button variant="outline" className="gap-1.5" onClick={() => setIsMonthlyCostOpen(true)}>
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">{t('dashboard.admin.helps.monthlyCostReport.buttonLabel')}</span>
              </Button>
              {!readOnly && (
                <Button variant="outline" className="gap-1.5" onClick={handleExportCases}>
                  <FileDown className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('dashboard.admin.helps.exportCases')}</span>
                </Button>
              )}
              {!readOnly && (
                <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-1.5">
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('dashboard.admin.helps.settings.title')}</span>
                </Button>
              )}
              {!readOnly && (
                <Button onClick={() => setIsAddOpen(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('dashboard.admin.helps.addCase')}</span>
                </Button>
              )}
            </div>
          </div>

          {/* Cases Tabs */}
          <Card>
            <CardContent className="p-3 sm:p-6">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Tabs value={topTab} onValueChange={handleTopTabChange} className="w-full" dir={dir}>
                  <div className="flex gap-2 overflow-x-auto mb-3">
                    {[
                      { label: t('dashboard.admin.helps.stats.total'), cases: filteredRequests },
                      { label: t('dashboard.admin.helps.tabs.pendingReview'), cases: pendingReview },
                      { label: t('dashboard.admin.helps.tabs.pendingApproval'), cases: pendingApproval },
                      { label: t('dashboard.admin.helps.tabs.approved'), cases: approved },
                      { label: t('dashboard.admin.helps.tabs.approvedTreatment'), cases: approvedTreatment },
                      { label: t('dashboard.admin.helps.tabs.treated'), cases: treated },
                      { label: t('dashboard.admin.helps.tabs.completed'), cases: completed },
                      { label: t('dashboard.admin.helps.tabs.rejected'), cases: rejected },
                    ].map((s, i) => (
                      <div key={i} className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                        <p className="text-xs text-muted-foreground whitespace-nowrap">{s.label}</p>
                        <p className="text-base font-bold inline-flex items-center gap-1">
                          {Math.round(s.cases.reduce((sum, c) => sum + getNetMedicalCost(c), 0)).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto pb-2 mb-4 -mx-3 px-3 scrollbar-thin">
                    <TabsList className="inline-flex w-max md:w-full" dir={dir}>
                      <TabsTrigger value="pending" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.pendingReview')}
                        {pendingReview.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{pendingReview.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="approval" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.pendingApproval')}
                        {pendingApproval.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{pendingApproval.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="approved" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.approved')}
                        {approved.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{approved.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="approvedTreatment" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.approvedTreatment')}
                        {approvedTreatment.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{approvedTreatment.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="treatmentStarted" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.treatmentStarted')}
                        {treatmentStarted.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{treatmentStarted.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="treated" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.treated')}
                        {treated.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{treated.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="completed" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.completed')}
                        {completed.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{completed.length}</Badge>}
                      </TabsTrigger>
                      <TabsTrigger value="rejected" className="md:flex-1 gap-1.5 whitespace-nowrap text-xs sm:text-sm px-3">
                        {t('dashboard.admin.helps.tabs.rejected')}
                        {rejected.length > 0 && <Badge className="bg-primary text-white text-[10px] px-1.5">{rejected.length}</Badge>}
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="pending">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.pendingReviewDesc')}
                    </p>
                    {renderRequestsTable(pendingReview, ['review'])}
                  </TabsContent>
                  <TabsContent value="approval">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.pendingApprovalDesc')}
                    </p>
                    {renderRequestsTable(pendingApproval, ['approve'])}
                  </TabsContent>
                  <TabsContent value="approved">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.approvedDesc')}
                    </p>
                    {renderRequestsTable(approved, ['approveTreatment'])}
                  </TabsContent>
                  <TabsContent value="approvedTreatment">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.approvedTreatmentDesc')}
                    </p>
                    {renderRequestsTable(approvedTreatment, ['view', 'startTreatment', 'revertToApproved'])}
                  </TabsContent>
                  <TabsContent value="treatmentStarted">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.treatmentStartedDesc')}
                    </p>
                    {renderRequestsTable(treatmentStarted, ['view', 'markTreated'])}
                  </TabsContent>
                  <TabsContent value="treated">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.admin.helps.treatedDesc')}
                    </p>
                    {renderRequestsTable(treated, ['payCase'])}
                  </TabsContent>
                  <TabsContent value="completed">
                    {renderRequestsTable(completed, ['view'])}
                  </TabsContent>
                  <TabsContent value="rejected">
                    {renderRequestsTable(rejected, ['view'])}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Links View */}
      {activeView === 'links' && (
        <>
          {/* Links Search + Add */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t('dashboard.admin.helps.linksView.searchPlaceholder')}
                value={linksSearchQuery}
                onChange={(e) => setLinksSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
                <span className="font-semibold text-foreground">{filteredLinks.length}</span>
                {t('dashboard.admin.helps.linksView.totalLinks')}
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Button variant="outline" className="gap-1.5" onClick={handleAiAllocate}>
                    <Sparkles className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.aiAllocate')}</span>
                  </Button>
                )}
                {!readOnly && (
                  <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-1.5">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.settings.title')}</span>
                  </Button>
                )}
                {!readOnly && (
                  <Button onClick={() => setShowAddLinkFromTab(true)} className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.linksView.addLink')}</span>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Links Summary */}
          {(() => {
            const totalReceived = filteredLinks.reduce((s, l) => s + (l.actualReceived || 0), 0);
            const totalMarketing = filteredLinks.reduce((s, l) => s + (l.marketingShare || 0), 0);
            const totalNet = filteredLinks.reduce((s, l) => s + (l.availableForCases || 0), 0);
            const totalAllocated = filteredLinks.reduce((s, l) => s + (l.totalAllocated || 0), 0);
            const totalRemaining = filteredLinks.reduce((s, l) => s + (l.remaining || 0), 0);
            const totalVAT = filteredLinks.reduce((s, l) => s + (l.refundableVAT || 0), 0);
            const totalCashback = Math.ceil(filteredLinks.reduce((s, l) => s + (l.cashbackAmount || 0), 0));
            const pct = (v: number) => totalReceived > 0 ? `${(v / totalReceived * 100).toFixed(1)}%` : '—';
            return (
              <div className="flex gap-2 overflow-x-auto">
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalDonations')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalReceived).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalReceived)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalMarketing')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalMarketing).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalMarketing)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalNet')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalNet).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalNet)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalRemaining')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalRemaining).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalRemaining)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalAllocatedCard')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalAllocated).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalAllocated)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalVAT')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalVAT).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalVAT)}</span>
                  </p>
                </div>
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.linksView.totalCashback')}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">
                    {Math.round(totalCashback).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                    <span className="text-[10px] font-semibold text-namat-green ms-1">{pct(totalCashback)}</span>
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Links Table */}
          <Card>
            <CardContent className="p-3 sm:p-6">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : sortedLinks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Link2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>{t('dashboard.admin.helps.linksView.noLinks')}</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px] text-start">#</TableHead>
                        <TableHead className="text-start">{t('dashboard.admin.helps.linksView.linkDetails')}</TableHead>
                        <TableHead className="text-start min-w-[200px]">{t('dashboard.admin.helps.linksView.cases')}</TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('actualReceived')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.donationAmount')}<LinksSortIcon column="actualReceived" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('marketingShare')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.marketingAmount')}<LinksSortIcon column="marketingShare" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('availableForCases')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.netAmount')}<LinksSortIcon column="availableForCases" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('remaining')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.remaining')}<LinksSortIcon column="remaining" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('totalAllocated')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.totalAllocated')}<LinksSortIcon column="totalAllocated" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('refundableVAT')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.vatAmount')}<LinksSortIcon column="refundableVAT" /></span>
                        </TableHead>
                        <TableHead className={`text-start cursor-pointer hover:bg-muted/50`} onClick={() => handleLinksSort('cashback')}>
                          <span className="flex items-center">{t('dashboard.admin.helps.linksView.cashbackAmount')}<LinksSortIcon column="cashback" /></span>
                        </TableHead>
                        <TableHead className="text-end">{t('dashboard.admin.helps.table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedLinks.map((link, i) => (
                        <TableRow key={link._key}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline">{t(`dashboard.admin.helps.platforms.${link.platform}`)}</Badge>
                                <span className="font-mono text-sm">{link.platformCaseNumber}</span>
                              </div>
                              {link.linkTitle && <span className="text-sm font-medium">{link.linkTitle}</span>}
                              {donationStartByLink[link._key] && (
                                <span className="text-xs text-muted-foreground">
                                  {t('dashboard.admin.helps.linksView.startDate')}: {formatLocalDate(donationStartByLink[link._key], 'PP')}
                                </span>
                              )}
                              {link.url && (
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs w-fit" onClick={(e) => e.stopPropagation()}>
                                  <ExternalLink className="h-3 w-3" />
                                  {t('dashboard.admin.helps.linksView.linkUrl')}
                                </a>
                              )}
                              <div className="mt-1 w-fit min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                                {!readOnly ? (
                                  <CategorySelect
                                    value={link.treatmentCategoryId}
                                    activeCategories={categories.filter(c => c.isActive)}
                                    onChange={(newId) => changeLinkCategoryWithCascade(link, newId)}
                                  />
                                ) : (
                                  <span className="text-sm">
                                    {categories.find((c) => c.id === link.treatmentCategoryId)
                                      ? (dir === 'rtl'
                                          ? (categories.find((c) => c.id === link.treatmentCategoryId)?.nameAr || categories.find((c) => c.id === link.treatmentCategoryId)?.name)
                                          : categories.find((c) => c.id === link.treatmentCategoryId)?.name)
                                      : '-'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1.5">
                              {link.cases.map((c, ci) => (
                                <span
                                  key={ci}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs font-medium border"
                                  title={`#${c.caseNumber ?? '-'} · ${c.idNumber || '-'} · ${c.allocated.toLocaleString('en-US')} SAR`}
                                >
                                  <span className="font-semibold">{dir === 'rtl' ? c.nameAr : c.nameEn}</span>
                                  {c.idNumber && <span className="text-muted-foreground font-mono" dir="ltr">{c.idNumber}</span>}
                                </span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1">
                              {link.actualReceived.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              {(link.marketingShare || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1">
                              {link.availableForCases.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                              {link.remaining.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                              {link.totalAllocated.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              {(link.refundableVAT || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              {Math.ceil(link.cashbackAmount || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </TableCell>
                          <TableCell className="text-end">
                            {!readOnly && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isSubmitting}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" dir={dir}>
                                  {link.cases.length === 0 && (
                                    <>
                                      <DropdownMenuItem onClick={() => { setAssigningLink(link); setAssignCaseId(''); }}>
                                        <ClipboardCheck className="h-4 w-4 me-2" />
                                        {t('dashboard.admin.helps.linksView.assignToCase')}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}
                                  <DropdownMenuItem onClick={() => startEditLink(link)}>
                                    <Pencil className="h-4 w-4 me-2" />
                                    {t('dashboard.admin.helps.linksView.editLink')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setManagingDonationsLink(link); setShowDonationForm(false); }}>
                                    <SaudiRiyalIcon className="h-4 w-4 me-2" />
                                    {t('dashboard.admin.helps.linksView.manageDonations')}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeleteLink(link)}>
                                    <Trash2 className="h-4 w-4 me-2" />
                                    {t('dashboard.admin.helps.linksView.deleteLink')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Link from Links Tab */}
          <Dialog open={showAddLinkFromTab} onOpenChange={(open) => { setShowAddLinkFromTab(open); if (!open) resetAddLinkTabForm(); }}>
            <DialogContent className="max-w-lg" aria-describedby={undefined} dir={dir}>
              <DialogHeader>
                <DialogTitle>{t('dashboard.admin.helps.linksView.addLink')}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.donationPlatform')} *</Label>
                  <select
                    value={addLinkTabForm.platform}
                    onChange={(e) => setAddLinkTabForm(prev => ({ ...prev, platform: e.target.value as any }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    dir={dir}
                  >
                    <option value="">{t('dashboard.admin.helps.form.selectPlatform')}</option>
                    <option value="jood">{t('dashboard.admin.helps.platforms.jood')}</option>
                    <option value="ehsan">{t('dashboard.admin.helps.platforms.ehsan')}</option>
                    <option value="tabarru">{t('dashboard.admin.helps.platforms.tabarru')}</option>
                    <option value="shefaa">{t('dashboard.admin.helps.platforms.shefaa')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.donationCaseNumber')} *</Label>
                  <Input
                    value={addLinkTabForm.caseNumber}
                    onChange={(e) => setAddLinkTabForm(prev => ({ ...prev, caseNumber: e.target.value }))}
                    dir="ltr"
                    placeholder={t('dashboard.admin.helps.form.donationCaseNumberPlaceholder')}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>{t('dashboard.admin.helps.form.linkTitle')}</Label>
                  <Input
                    value={addLinkTabForm.linkTitle}
                    onChange={(e) => setAddLinkTabForm(prev => ({ ...prev, linkTitle: e.target.value }))}
                    placeholder={t('dashboard.admin.helps.form.linkTitlePlaceholder')}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>{t('dashboard.admin.helps.form.donationUrl')}</Label>
                  <Input
                    value={addLinkTabForm.url}
                    onChange={(e) => setAddLinkTabForm(prev => ({ ...prev, url: e.target.value }))}
                    dir="ltr"
                    placeholder={t('dashboard.admin.helps.form.donationUrlPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.linksView.treatmentCategory')}</Label>
                  <CategorySelect
                    value={addLinkTabForm.treatmentCategoryId || undefined}
                    activeCategories={activeCategories}
                    onChange={(id) => setAddLinkTabForm(prev => ({ ...prev, treatmentCategoryId: id || '' }))}
                    triggerClassName="h-10 w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.actualReceived')}</Label>
                  <Input
                    type="number"
                    value={addLinkTabForm.actualReceived}
                    onChange={(e) => setAddLinkTabForm(prev => ({ ...prev, actualReceived: e.target.value }))}
                    dir="ltr"
                    placeholder={t('dashboard.admin.helps.form.actualReceivedPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.linksView.availableForTreatment')}</Label>
                  <Input
                    type="number"
                    value={calcLinkAvailable(Number(addLinkTabForm.actualReceived) || 0)}
                    readOnly
                    disabled
                    dir="ltr"
                    className="bg-muted/40"
                  />
                  <p className="text-xs text-muted-foreground">{t('dashboard.admin.helps.linksView.availableForTreatmentHint')}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowAddLinkFromTab(false); resetAddLinkTabForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleAddLinkFromTab} disabled={isSubmitting || !addLinkTabForm.platform || !addLinkTabForm.caseNumber}>
                  {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Plus className="h-4 w-4 me-2" />}
                  {t('dashboard.admin.helps.addDonationBtn')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Link from Links Tab */}
          <Dialog open={!!editingLink} onOpenChange={(open) => { if (!open) setEditingLink(null); }}>
            <DialogContent className="max-w-lg" aria-describedby={undefined} dir={dir}>
              <DialogHeader>
                <DialogTitle>{t('dashboard.admin.helps.linksView.editLink')}</DialogTitle>
              </DialogHeader>
              {editingLink && (
                <>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground pb-2">
                    <Badge variant="outline">{t(`dashboard.admin.helps.platforms.${editingLink.platform}`)}</Badge>
                    <span className="font-mono">{editingLink.platformCaseNumber}</span>
                    {editingLink.cases.length > 0 && (
                      <span>· {editingLink.cases.length} {t('dashboard.admin.helps.linksView.cases').toLowerCase()}</span>
                    )}
                  </div>
                  {editingLink.cases.length > 1 && (
                    <div className="px-3 py-2 rounded-md text-xs bg-amber-50 text-amber-800 border border-amber-200">
                      {t('dashboard.admin.helps.linksView.editMultiCaseWarning', { count: editingLink.cases.length })}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                    <div className="space-y-2 col-span-2">
                      <Label>{t('dashboard.admin.helps.form.linkTitle')}</Label>
                      <Input
                        value={editLinkForm.linkTitle}
                        onChange={(e) => setEditLinkForm(prev => ({ ...prev, linkTitle: e.target.value }))}
                        placeholder={t('dashboard.admin.helps.form.linkTitlePlaceholder')}
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>{t('dashboard.admin.helps.form.donationUrl')}</Label>
                      <Input
                        value={editLinkForm.url}
                        onChange={(e) => setEditLinkForm(prev => ({ ...prev, url: e.target.value }))}
                        dir="ltr"
                        placeholder={t('dashboard.admin.helps.form.donationUrlPlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('dashboard.admin.helps.linksView.treatmentCategory')}</Label>
                      <CategorySelect
                        value={editLinkForm.treatmentCategoryId || undefined}
                        activeCategories={activeCategories}
                        onChange={(id) => setEditLinkForm(prev => ({ ...prev, treatmentCategoryId: id || '' }))}
                        triggerClassName="h-10 w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('dashboard.admin.helps.form.actualReceived')}</Label>
                      <Input
                        type="number"
                        value={editLinkForm.actualReceived}
                        onChange={(e) => setEditLinkForm(prev => ({ ...prev, actualReceived: e.target.value }))}
                        dir="ltr"
                        disabled={!!editingLink && donations.some(d => d.linkId === editingLink._key && d.source === 'jood')}
                        placeholder={t('dashboard.admin.helps.form.actualReceivedPlaceholder')}
                      />
                      {!!editingLink && donations.some(d => d.linkId === editingLink._key && d.source === 'jood') && (
                        <p className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.derivedFromDonations')}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t('dashboard.admin.helps.linksView.availableForTreatment')}</Label>
                      <Input
                        type="number"
                        value={calcLinkAvailable(Number(editLinkForm.actualReceived) || 0)}
                        readOnly
                        disabled
                        dir="ltr"
                        className="bg-muted/40"
                      />
                      <p className="text-xs text-muted-foreground">{t('dashboard.admin.helps.linksView.availableForTreatmentHint')}</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditingLink(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSaveEditLink} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                      {t('common.save')}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Manage Donations Dialog */}
          <Dialog open={!!managingDonationsLink} onOpenChange={(open) => { if (!open) { setManagingDonationsLink(null); setShowDonationForm(false); setDonationForm(emptyDonationForm); } }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} dir={dir}>
              <DialogHeader>
                <DialogTitle>
                  {t('dashboard.admin.helps.linksView.manageDonations')}
                  {managingDonationsLink && (
                    <span className="block text-sm font-normal text-muted-foreground mt-1">
                      {managingDonationsLink.linkTitle || managingDonationsLink.platformCaseNumber} ·{' '}
                      {(donationSumByLink[managingDonationsLink._key] || 0).toLocaleString('en-US')} {t('common.sar')}
                      {' · '}{(donationsByLink[managingDonationsLink._key] || []).length} {t('dashboard.admin.helps.linksView.donationsCount')}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              {managingDonationsLink && (
                <div className="space-y-4">
                  {!showDonationForm && (
                    <Button size="sm" onClick={startAddDonation} disabled={isSubmitting}>
                      <Plus className="h-4 w-4 me-2" />
                      {t('dashboard.admin.helps.linksView.addDonation')}
                    </Button>
                  )}

                  {showDonationForm && (
                    <div className="border rounded-md p-4 space-y-3 bg-muted/30">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>{t('dashboard.admin.helps.form.actualReceived')} *</Label>
                          <Input type="number" value={donationForm.amount} dir="ltr"
                            onChange={(e) => setDonationForm(f => ({ ...f, amount: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('dashboard.admin.helps.linksView.donationDate')} *</Label>
                          <Input type="date" value={donationForm.date} dir="ltr"
                            onChange={(e) => setDonationForm(f => ({ ...f, date: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('dashboard.admin.helps.linksView.donorName')}</Label>
                          <Input value={donationForm.donorName}
                            onChange={(e) => setDonationForm(f => ({ ...f, donorName: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('dashboard.admin.helps.linksView.donorPhone')}</Label>
                          <Input value={donationForm.donorPhone} dir="ltr"
                            onChange={(e) => setDonationForm(f => ({ ...f, donorPhone: e.target.value }))} />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                          <Label>{t('dashboard.admin.helps.linksView.paymentMethod')}</Label>
                          <Input value={donationForm.paymentMethod}
                            onChange={(e) => setDonationForm(f => ({ ...f, paymentMethod: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => { setShowDonationForm(false); setDonationForm(emptyDonationForm); }}>
                          {t('common.cancel')}
                        </Button>
                        <Button size="sm" onClick={handleSaveDonation} disabled={isSubmitting}>
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                          {t('common.save')}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-md max-h-[45vh] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donationDate')}</TableHead>
                          <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donationAmount')}</TableHead>
                          <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donorName')}</TableHead>
                          <TableHead className="text-start">{t('dashboard.admin.helps.linksView.source')}</TableHead>
                          <TableHead className="text-end">{t('dashboard.admin.helps.table.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(donationsByLink[managingDonationsLink._key] || []).length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('dashboard.admin.helps.linksView.noDonations')}</TableCell></TableRow>
                        ) : (
                          (donationsByLink[managingDonationsLink._key] || []).map((d) => (
                            <TableRow key={d.id}>
                              <TableCell className="whitespace-nowrap">{formatLocalDate(d.date, 'PP')}</TableCell>
                              <TableCell>
                                <span className="inline-flex items-center gap-1">
                                  {(d.amount || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                                </span>
                              </TableCell>
                              <TableCell>{d.donorName || '-'}</TableCell>
                              <TableCell>
                                <Badge variant={d.source === 'jood' ? 'secondary' : 'outline'} className="text-xs">
                                  {t(`dashboard.admin.helps.linksView.donationSource.${d.source}`)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-end">
                                <div className="flex gap-1 justify-end">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={isSubmitting} onClick={() => startEditDonation(d)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" disabled={isSubmitting} onClick={() => handleDeleteDonation(d)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Assign Link to Case Dialog */}
          <Dialog open={!!assigningLink} onOpenChange={(open) => { if (!open) { setAssigningLink(null); setAssignCaseId(''); } }}>
            <DialogContent className="max-w-md" aria-describedby={undefined} dir={dir}>
              <DialogHeader>
                <DialogTitle>{t('dashboard.admin.helps.linksView.assignToCase')}</DialogTitle>
              </DialogHeader>
              {assigningLink && (
                <>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground pb-2">
                    <Badge variant="outline">{t(`dashboard.admin.helps.platforms.${assigningLink.platform}`)}</Badge>
                    <span className="font-mono">{assigningLink.platformCaseNumber}</span>
                    {assigningLink.availableForCases > 0 && (
                      <span className="inline-flex items-center gap-1">{assigningLink.availableForCases.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></span>
                    )}
                  </div>
                  {assigningLink.linkTitle && (
                    <p className="text-sm text-muted-foreground truncate">{assigningLink.linkTitle}</p>
                  )}
                  {(() => {
                    const linkCat = assigningLink?.treatmentCategoryId;
                    const availableCases = !linkCat
                      ? []
                      : requests.filter(r => r.categoryId === linkCat);
                    return (
                      <div className="space-y-2 py-4">
                        <Label>{t('dashboard.admin.helps.linksView.selectCase')}</Label>
                        {!linkCat && (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            {t('dashboard.admin.helps.linksView.classifyCaseFirst')}
                          </p>
                        )}
                        {linkCat && availableCases.length === 0 && (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            {t('dashboard.admin.helps.linksView.noCasesForCategory')}
                          </p>
                        )}
                        <select
                          value={assignCaseId}
                          onChange={(e) => setAssignCaseId(e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          dir={dir}
                        >
                          <option value="">{t('dashboard.admin.helps.linksView.selectCasePlaceholder')}</option>
                          {availableCases.map(r => (
                            <option key={r.id} value={r.id}>
                              #{r.caseNumber} - {dir === 'rtl' ? (r.nameArabic || r.applicantName) : (r.nameEnglish || r.applicantName)}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setAssigningLink(null); setAssignCaseId(''); }}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleAssignLink} disabled={isSubmitting || !assignCaseId}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 me-2" />}
                      {t('dashboard.admin.helps.linksView.assignBtn')}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Awareness View */}
      {activeView === 'awareness' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input placeholder={t('dashboard.admin.helps.searchPlaceholder')} disabled className="pl-10" />
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
                <span className="font-semibold text-foreground">0</span>
                {t('dashboard.admin.helps.awareness.title')}
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-1.5">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.settings.title')}</span>
                  </Button>
                )}
                {!readOnly && (
                  <Button onClick={() => {}} className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.awareness.addProgram')}</span>
                  </Button>
                )}
              </div>
            )}
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <h3 className="text-lg font-semibold mb-2">{t('dashboard.admin.helps.awareness.title')}</h3>
                <p className="text-sm">{t('dashboard.admin.helps.awareness.description')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Training View */}
      {activeView === 'training' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input placeholder={t('dashboard.admin.helps.searchPlaceholder')} disabled className="pl-10" />
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
                <span className="font-semibold text-foreground">0</span>
                {t('dashboard.admin.helps.training.title')}
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-1.5">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.settings.title')}</span>
                  </Button>
                )}
                {!readOnly && (
                  <Button onClick={() => {}} className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.admin.helps.training.addCourse')}</span>
                  </Button>
                )}
              </div>
            )}
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-12 text-muted-foreground">
                <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <h3 className="text-lg font-semibold mb-2">{t('dashboard.admin.helps.training.title')}</h3>
                <p className="text-sm">{t('dashboard.admin.helps.training.description')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Marketing Payments View */}
      {activeView === 'marketing' && (() => {
        const marketingFactor = marketingX / ((marketingX + marketingY) || 1);
        const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const monthlyBuckets: Record<string, { start: Date; amount: number }> = {};
        for (const dn of donations) {
          if (!dn.date || isNaN(dn.date.getTime())) continue;
          // Marketing (Maras) is charged only on real-dated Jood donations.
          // Legacy/non-Jood agg_* lump sums and non-Jood platforms are excluded.
          if ((dn.importKey || '').startsWith('agg_')) continue;
          if (dn.platform !== 'jood') continue;
          const ms = new Date(dn.date.getFullYear(), dn.date.getMonth(), 1);
          (monthlyBuckets[monthKey(ms)] = monthlyBuckets[monthKey(ms)] || { start: ms, amount: 0 }).amount += dn.amount || 0;
        }
        const monthlyRows = Object.values(monthlyBuckets).sort((a, b) => a.start.getTime() - b.start.getTime());
        let monthlyCumDonations = 0, monthlyCumMarketing = 0;
        const monthlyExpected = monthlyRows.map(r => {
          const expected = Math.ceil(r.amount * marketingFactor);
          monthlyCumDonations += r.amount;
          monthlyCumMarketing += expected;
          const end = new Date(r.start.getFullYear(), r.start.getMonth() + 1, 0);
          return { start: r.start, end, donations: r.amount, expected, cumDonations: monthlyCumDonations, cumMarketing: monthlyCumMarketing };
        });
        // Cost = marketing % of ALL donations (every platform) except the
        // current incomplete month. Paid = all marketing payments.
        // Due = cost - paid.
        const currentMonth = monthKey(new Date());
        const accrualBase = donations.reduce((s, d) => {
          if (!d.date || isNaN(d.date.getTime())) return s;
          return monthKey(d.date) === currentMonth ? s : s + (d.amount || 0);
        }, 0);
        const marketingCost = Math.round(accrualBase * marketingFactor);
        const totalPaid = marketingPayments.reduce((s, p) => s + (p.amount || 0), 0);
        const totalDue = marketingCost - totalPaid;
        return (
          <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto">
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.marketingCost')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1">{Math.round(marketingCost).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.totalPaid')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-green-600">{Math.round(totalPaid).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.totalDue')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-amber-600">{Math.round(totalDue).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{t('dashboard.admin.helps.marketing.title')}</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowWeeklyMarketing(true)}>
                      <ArrowUpDown className="h-4 w-4" />{t('dashboard.admin.helps.marketing.monthlyExpected')}
                    </Button>
                    {!readOnly && (
                      <Button size="sm" className="gap-2" onClick={() => { setLedgerForm({ amount: '', date: '', notes: '', status: 'pending' }); setLedgerReceiptFile(null); setLedgerDialog({ kind: 'marketing', id: null }); }}>
                        <Plus className="h-4 w-4" />{t('dashboard.admin.helps.marketing.addPayment')}
                      </Button>
                    )}
                  </div>
                </div>
                {marketingPayments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.marketing.noPayments')}</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.amount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.date')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.receipt')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.notes')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.action')}</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {marketingPayments.map((p, idx) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="text-end font-medium">{p.amount.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end text-sm">{format(p.date, 'yyyy-MM-dd')}</TableCell>
                          <TableCell className="text-end">
                            {p.receiptUrl && (
                              <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="text-end text-xs text-muted-foreground">{p.notes}</TableCell>
                          <TableCell className="text-end">
                            {!readOnly && (
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => { setLedgerForm({ amount: String(p.amount), date: format(p.date, 'yyyy-MM-dd'), notes: p.notes || '', status: 'pending' }); setLedgerReceiptFile(null); setLedgerDialog({ kind: 'marketing', id: p.id }); }}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={async () => { if (confirm(t('dashboard.admin.helps.marketing.confirmDelete'))) { await deleteMarketingPayment(p.id); toast.success(t('dashboard.admin.helps.marketing.deleteSuccess')); } }}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Dialog open={showWeeklyMarketing} onOpenChange={setShowWeeklyMarketing}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} dir={dir}>
                <DialogHeader>
                  <DialogTitle>
                    {t('dashboard.admin.helps.marketing.monthlyExpected')}
                    <span className="block text-sm font-normal text-muted-foreground mt-1">
                      {t('dashboard.admin.helps.marketing.monthlyExpectedHint', { pct: Math.round(marketingFactor * 100) })}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                {monthlyExpected.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.linksView.noDonations')}</p>
                ) : (
                  <div className="border rounded-md max-h-[65vh] overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="text-start">{t('dashboard.admin.helps.marketing.month')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donationAmount')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.admin.helps.marketing.expectedCost')}</TableHead>
                        <TableHead className="text-start">{t('dashboard.admin.helps.marketing.cumulative')}</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {monthlyExpected.map((w, i) => (
                          <TableRow key={i}>
                            <TableCell className="whitespace-nowrap text-xs" dir="ltr">
                              {formatLocalDate(w.start, 'MMM yyyy')}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1">{w.donations.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1 font-medium text-amber-600">{w.expected.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1 text-muted-foreground">{w.cumMarketing.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        );
      })()}

      {/* VAT Refund View */}
      {activeView === 'vat' && (() => {
        const vatDue = allLinks.reduce((s, l) => s + (l.refundableVAT || 0), 0);
        // Only approved refunds count; rejected = dead; pending not yet counted
        const totalRefunded = vatRefunds.filter(r => r.status === 'approved').reduce((s, r) => s + (r.amount || 0), 0);
        const remaining = vatDue - totalRefunded;
        return (
          <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto">
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.vatDue')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1">{Math.round(vatDue).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.totalRefunded')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-green-600">{Math.round(totalRefunded).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.remaining')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-amber-600">{Math.round(remaining).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{t('dashboard.admin.helps.vat.title')}</h3>
                  {!readOnly && (
                    <Button size="sm" className="gap-2" onClick={() => { setLedgerForm({ amount: '', date: '', notes: '', status: 'pending' }); setLedgerReceiptFile(null); setLedgerDialog({ kind: 'vat', id: null }); }}>
                      <Plus className="h-4 w-4" />{t('dashboard.admin.helps.vat.addRefund')}
                    </Button>
                  )}
                </div>
                {vatRefunds.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.vat.noRefunds')}</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.amount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.date')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.receipt')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.notes')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.status')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.action')}</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {vatRefunds.map((r, idx) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="text-end font-medium">{r.amount.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end text-sm">{format(r.date, 'yyyy-MM-dd')}</TableCell>
                          <TableCell className="text-end">
                            {r.receiptUrl && (
                              <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="text-end text-xs text-muted-foreground">{r.notes}</TableCell>
                          <TableCell className="text-end">
                            <Badge className={r.status === 'approved' ? 'bg-green-100 text-green-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                              {t(`dashboard.admin.helps.vat.${r.status || 'pending'}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end">
                            {!readOnly && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {r.status !== 'approved' && (
                                    <DropdownMenuItem onClick={async () => { await updateVatRefund(r.id, { status: 'approved' }); toast.success(t('dashboard.admin.helps.vat.statusUpdated')); }}>
                                      <CheckCircle className="h-4 w-4 me-2 text-green-600" />
                                      {t('dashboard.admin.helps.vat.approve')}
                                    </DropdownMenuItem>
                                  )}
                                  {r.status !== 'rejected' && (
                                    <DropdownMenuItem onClick={async () => { await updateVatRefund(r.id, { status: 'rejected' }); toast.success(t('dashboard.admin.helps.vat.statusUpdated')); }}>
                                      <XCircle className="h-4 w-4 me-2 text-red-600" />
                                      {t('dashboard.admin.helps.vat.reject')}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => { setLedgerForm({ amount: String(r.amount), date: format(r.date, 'yyyy-MM-dd'), notes: r.notes || '', status: r.status || 'pending' }); setLedgerReceiptFile(null); setLedgerDialog({ kind: 'vat', id: r.id }); }}>
                                    <Pencil className="h-4 w-4 me-2" />
                                    {t('common.edit')}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-destructive" onClick={async () => { if (confirm(t('dashboard.admin.helps.vat.confirmDelete'))) { await deleteVatRefund(r.id); toast.success(t('dashboard.admin.helps.vat.deleteSuccess')); } }}>
                                    <Trash2 className="h-4 w-4 me-2" />
                                    {t('common.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Cashbacks View */}
      {activeView === 'cashbacks' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{cashbacks.filter(c => c.status === 'pending').length}</div>
                <div className="text-sm text-muted-foreground">{t('dashboard.admin.helps.cashbacks.totalPending')}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{cashbacks.filter(c => c.status === 'confirmed').reduce((s, c) => s + c.totalAmount, 0).toLocaleString('en-US')}</div>
                <div className="text-sm text-muted-foreground">{t('dashboard.admin.helps.cashbacks.totalConfirmed')}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{cashbacks.filter(c => c.status === 'rejected').length}</div>
                <div className="text-sm text-muted-foreground">{t('dashboard.admin.helps.cashbacks.totalRejected')}</div>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">{t('dashboard.admin.helps.cashbacks.title')}</h3>
              {cashbacksLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : cashbacks.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.cashbacks.noCashbacks')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.hospital')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.cases')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.totalAmount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.date')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.receipt')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.coordinator')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.status')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.cashbacks.action')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cashbacks.map((cb, idx) => (
                      <TableRow key={cb.id}>
                        <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="text-end text-sm">{cb.providerName}</TableCell>
                        <TableCell className="text-end text-xs">
                          {cb.cases.map((c, i) => (
                            <div key={i}>{c.caseName || c.caseId} — {c.amount.toLocaleString('en-US')}</div>
                          ))}
                        </TableCell>
                        <TableCell className="text-end font-medium">{cb.totalAmount.toLocaleString('en-US')}</TableCell>
                        <TableCell className="text-end text-sm">{format(cb.date, 'yyyy-MM-dd')}</TableCell>
                        <TableCell className="text-end">
                          {cb.receiptUrl && (
                            <a href={cb.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                              <FileText className="h-4 w-4" />
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-end text-xs">{cb.createdByName}</TableCell>
                        <TableCell className="text-end">
                          <Badge className={cb.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : cb.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {t(`dashboard.admin.helps.cashbacks.${cb.status}`)}
                          </Badge>
                          {cb.adminNotes && (
                            <p className="text-xs text-muted-foreground mt-1">{cb.adminNotes}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-end">
                          {!readOnly && cb.status === 'pending' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedCashback(cb);
                                setCashbackDecision({ decision: '', adminNotes: '' });
                                setIsCashbackReviewOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Marketing / VAT Ledger Add-Edit Dialog */}
      <Dialog open={!!ledgerDialog} onOpenChange={(open) => { if (!open) setLedgerDialog(null); }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {ledgerDialog?.kind === 'vat'
                ? t(ledgerDialog?.id ? 'dashboard.admin.helps.vat.editRefund' : 'dashboard.admin.helps.vat.addRefund')
                : t(ledgerDialog?.id ? 'dashboard.admin.helps.marketing.editPayment' : 'dashboard.admin.helps.marketing.addPayment')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {ledgerDialog?.kind === 'vat' ? t('dashboard.admin.helps.vat.title') : t('dashboard.admin.helps.marketing.title')}
            </DialogDescription>
          </DialogHeader>
          {ledgerDialog && (() => {
            const ns = ledgerDialog.kind === 'vat' ? 'dashboard.admin.helps.vat' : 'dashboard.admin.helps.marketing';
            return (
              <>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>{t(`${ns}.amount`)} *</Label>
                    <Input type="number" value={ledgerForm.amount} onChange={(e) => setLedgerForm(f => ({ ...f, amount: e.target.value }))} dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t(`${ns}.date`)} *</Label>
                    <Input type="date" value={ledgerForm.date} onChange={(e) => setLedgerForm(f => ({ ...f, date: e.target.value }))} dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t(`${ns}.uploadReceipt`)}{ledgerDialog.id ? '' : ' *'}</Label>
                    <Input type="file" accept=".pdf,image/*" onChange={(e) => setLedgerReceiptFile(e.target.files?.[0] || null)} />
                    {ledgerReceiptFile && <p className="text-xs text-muted-foreground">{ledgerReceiptFile.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t(`${ns}.notes`)}</Label>
                    <Textarea value={ledgerForm.notes} onChange={(e) => setLedgerForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
                  </div>
                  {ledgerDialog.kind === 'vat' && (
                    <div className="space-y-2">
                      <Label>{t('dashboard.admin.helps.vat.status')}</Label>
                      <select
                        value={ledgerForm.status}
                        onChange={(e) => setLedgerForm(f => ({ ...f, status: e.target.value }))}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        dir={dir}
                      >
                        <option value="pending">{t('dashboard.admin.helps.vat.pending')}</option>
                        <option value="approved">{t('dashboard.admin.helps.vat.approved')}</option>
                        <option value="rejected">{t('dashboard.admin.helps.vat.rejected')}</option>
                      </select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setLedgerDialog(null)}>{t('dashboard.admin.helps.cancel')}</Button>
                  <Button
                    disabled={isSubmittingLedger}
                    onClick={async () => {
                      const kind = ledgerDialog.kind;
                      const editId = ledgerDialog.id;
                      if (!ledgerForm.amount || Number(ledgerForm.amount) <= 0) { toast.error(t(`${ns}.amountRequired`)); return; }
                      if (!ledgerForm.date) { toast.error(t(`${ns}.dateRequired`)); return; }
                      if (!editId && !ledgerReceiptFile) { toast.error(t(`${ns}.receiptRequired`)); return; }
                      setIsSubmittingLedger(true);
                      try {
                        let receiptUrl = '';
                        let receiptName = '';
                        if (ledgerReceiptFile) {
                          const folder = kind === 'vat' ? 'vat-refund-receipts' : 'marketing-receipts';
                          const uuid = crypto.randomUUID();
                          const path = `${folder}/${uuid}/${Date.now()}_${ledgerReceiptFile.name}`;
                          const sref = ref(storage, path);
                          await uploadBytes(sref, ledgerReceiptFile);
                          receiptUrl = await getDownloadURL(sref);
                          receiptName = ledgerReceiptFile.name;
                        }
                        const base: any = {
                          amount: Number(ledgerForm.amount),
                          date: new Date(ledgerForm.date),
                          notes: ledgerForm.notes.trim() || undefined,
                        };
                        if (kind === 'vat') base.status = ledgerForm.status || 'pending';
                        if (receiptUrl) { base.receiptUrl = receiptUrl; base.receiptName = receiptName; }
                        if (editId) {
                          if (kind === 'vat') await updateVatRefund(editId, base);
                          else await updateMarketingPayment(editId, base);
                        } else {
                          const full = {
                            ...base,
                            receiptUrl,
                            receiptName,
                            createdBy: currentUser?.uid || '',
                            createdByName: currentUser?.displayName || currentUser?.email || '',
                          };
                          if (kind === 'vat') await addVatRefund(full);
                          else await addMarketingPayment(full);
                        }
                        toast.success(t(`${ns}.addSuccess`));
                        setLedgerDialog(null);
                      } catch (error) {
                        console.error('Ledger save error:', error);
                        toast.error(t(`${ns}.addError`));
                      } finally {
                        setIsSubmittingLedger(false);
                      }
                    }}
                  >
                    {isSubmittingLedger && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                    {t('common.save')}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Cashback Review Dialog */}
      <Dialog open={isCashbackReviewOpen} onOpenChange={setIsCashbackReviewOpen}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.helps.cashbacks.decision')}</DialogTitle>
            {selectedCashback && (
              <DialogDescription>
                {selectedCashback.providerName} — {selectedCashback.totalAmount.toLocaleString('en-US')} SAR
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-3">
              <Button
                variant={cashbackDecision.decision === 'confirm' ? 'default' : 'outline'}
                className="flex-1 gap-2"
                onClick={() => setCashbackDecision(d => ({ ...d, decision: 'confirm' }))}
              >
                <CheckCircle className="h-4 w-4" />
                {t('dashboard.admin.helps.cashbacks.confirm')}
              </Button>
              <Button
                variant={cashbackDecision.decision === 'reject' ? 'destructive' : 'outline'}
                className="flex-1 gap-2"
                onClick={() => setCashbackDecision(d => ({ ...d, decision: 'reject' }))}
              >
                <XCircle className="h-4 w-4" />
                {t('dashboard.admin.helps.cashbacks.reject')}
              </Button>
            </div>
            <div className="space-y-2">
              <Label>{t('dashboard.admin.helps.cashbacks.adminNotes')}</Label>
              <Textarea
                value={cashbackDecision.adminNotes}
                onChange={(e) => setCashbackDecision(d => ({ ...d, adminNotes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCashbackReviewOpen(false)}>
              {t('dashboard.admin.helps.cancel')}
            </Button>
            <Button
              disabled={!cashbackDecision.decision || isSubmittingCashback}
              onClick={async () => {
                if (!selectedCashback || !cashbackDecision.decision) return;
                setIsSubmittingCashback(true);
                try {
                  await updateCashback(selectedCashback.id, {
                    status: cashbackDecision.decision === 'confirm' ? 'confirmed' : 'rejected',
                    adminId: currentUser?.uid || '',
                    adminNotes: cashbackDecision.adminNotes.trim() || undefined,
                    adminDecisionAt: new Date(),
                  });
                  toast.success(t(`dashboard.admin.helps.cashbacks.${cashbackDecision.decision === 'confirm' ? 'confirmSuccess' : 'rejectSuccess'}`));
                  setIsCashbackReviewOpen(false);
                } catch (error) {
                  console.error('Cashback decision error:', error);
                  toast.error(t('dashboard.admin.helps.cashbacks.decisionError'));
                } finally {
                  setIsSubmittingCashback(false);
                }
              }}
            >
              {isSubmittingCashback && <Loader2 className="h-4 w-4 animate-spin" />}
              {cashbackDecision.decision === 'confirm' ? t('dashboard.admin.helps.cashbacks.confirm') : t('dashboard.admin.helps.cashbacks.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Case Modal */}
      <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) setAddFormStep(1); }}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" dir={dir}>
          <div className="px-6 pt-6 pb-4 border-b shrink-0 bg-gray-50">
            <DialogHeader dir={dir}>
              <DialogTitle>{t('dashboard.admin.helps.addCase')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.admin.helps.addCaseDescription')}
              </DialogDescription>
            </DialogHeader>
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">{t('common.step')} {addFormStep} {t('common.of')} {ADD_TOTAL_STEPS}</span>
                <span className="text-xs font-bold text-primary">{Math.round(addFormStep / ADD_TOTAL_STEPS * 100)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div className="bg-primary h-1.5 rounded-full transition-all duration-500" style={{ width: `${addFormStep / ADD_TOTAL_STEPS * 100}%` }} />
              </div>
              <div className="flex justify-between mt-1.5">
                {addStepLabels.map((label, i) => (
                  <span key={i} className={`text-[10px] font-medium ${addFormStep >= i + 1 ? 'text-primary' : 'text-muted-foreground'}`}>{label}</span>
                ))}
              </div>
            </div>
          </div>
          <form onSubmit={handleAddCase} className="flex flex-col min-h-0 flex-1">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {addFormStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminNameArabic">{t('dashboard.admin.helps.form.nameArabic')}</Label>
                  <Input
                    id="adminNameArabic"
                    value={formData.nameArabic}
                    onChange={(e) => setFormData(prev => ({ ...prev, nameArabic: e.target.value }))}
                    dir="rtl"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.admin.helps.form.nameArabicHint')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="adminNameEnglish">{t('dashboard.admin.helps.form.nameEnglish')}</Label>
                  <Input
                    id="adminNameEnglish"
                    value={formData.nameEnglish}
                    onChange={(e) => setFormData(prev => ({ ...prev, nameEnglish: e.target.value }))}
                    dir="ltr"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.admin.helps.form.nameEnglishHint')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="adminIdType">{t('dashboard.admin.helps.form.idType')}</Label>
                  <Select
                    value={formData.idType}
                    onValueChange={(value: TreatmentIdType) => setFormData(prev => ({ ...prev, idType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national_id">{t('dashboard.admin.helps.form.idTypes.nationalId')}</SelectItem>
                      <SelectItem value="residency">{t('dashboard.admin.helps.form.idTypes.residency')}</SelectItem>
                      <SelectItem value="umrah_visit_visa">{t('dashboard.admin.helps.form.idTypes.umrahVisitVisa')}</SelectItem>
                      <SelectItem value="passport">{t('dashboard.admin.helps.form.idTypes.passport')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminIdNumber">{t('dashboard.admin.helps.form.idNumber')} *</Label>
                  <Input
                    id="adminIdNumber"
                    value={formData.idNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                    required
                    dir="ltr"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.admin.helps.form.idNumberHint')}
                  </p>
                </div>

                <div className="space-y-2">
                  <HijriDatePicker label={t('dashboard.admin.helps.form.idExpiryDate')} value={formData.idExpiryDate} onChange={(v) => setFormData(prev => ({ ...prev, idExpiryDate: v }))} />
                </div>

                {/* ID Document Upload */}
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.idDocument')}</Label>
                  {pendingIdDocument ? (
                    <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted/50">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate flex-1">{pendingIdDocument.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setPendingIdDocument(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setPendingIdDocument(file);
                        }}
                      />
                      <div className="flex items-center justify-center gap-2 h-10 border-2 border-dashed rounded-md hover:border-primary transition-colors">
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {t('dashboard.admin.helps.form.uploadIdDocument')}
                        </span>
                      </div>
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.admin.helps.form.idDocumentHint')}
                  </p>
                </div>
              </div>
              )}

              {addFormStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <HijriDatePicker label={t('dashboard.admin.helps.form.dateOfBirth')} value={formData.dateOfBirth} onChange={(v) => setFormData(prev => ({ ...prev, dateOfBirth: v }))} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminNationality">{t('dashboard.admin.helps.form.nationality')}</Label>
                  <Input
                    id="adminNationality"
                    value={formData.nationality}
                    onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                    dir={dir}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminEmail">{t('dashboard.admin.helps.form.email')}</Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('dashboard.admin.helps.form.emailPlaceholder')}
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPhoneNumber">{t('dashboard.admin.helps.form.phoneNumber')} *</Label>
                  <Input
                    id="adminPhoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="05XXXXXXXX"
                    required
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminRelativeMobileNumber">{t('dashboard.admin.helps.form.relativeMobileNumber')}</Label>
                  <Input
                    id="adminRelativeMobileNumber"
                    value={formData.relativeMobileNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))}
                    placeholder="05XXXXXXXX"
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminCity">{t('dashboard.admin.helps.form.city')}</Label>
                  <Input
                    id="adminCity"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder={t('dashboard.admin.helps.form.cityPlaceholder')}
                    dir={dir}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminNeighborhood">{t('dashboard.admin.helps.form.neighborhood')}</Label>
                  <Input
                    id="adminNeighborhood"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData(prev => ({ ...prev, neighborhood: e.target.value }))}
                    placeholder={t('dashboard.admin.helps.form.neighborhoodPlaceholder')}
                    dir={dir}
                  />
                </div>

              </div>
              )}

              {addFormStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminGender">{t('dashboard.admin.helps.form.gender')}</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value: TreatmentGender) => setFormData(prev => ({ ...prev, gender: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('dashboard.admin.helps.form.genders.male')}</SelectItem>
                      <SelectItem value="female">{t('dashboard.admin.helps.form.genders.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminMaritalStatus">{t('dashboard.admin.helps.form.maritalStatus')}</Label>
                  <Select
                    value={formData.maritalStatus}
                    onValueChange={(value: TreatmentMaritalStatus) => setFormData(prev => ({ ...prev, maritalStatus: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t('dashboard.admin.helps.form.maritalStatuses.single')}</SelectItem>
                      <SelectItem value="married">{t('dashboard.admin.helps.form.maritalStatuses.married')}</SelectItem>
                      <SelectItem value="divorced">{t('dashboard.admin.helps.form.maritalStatuses.divorced')}</SelectItem>
                      <SelectItem value="widowed">{t('dashboard.admin.helps.form.maritalStatuses.widowed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminFamilyMembersCount">{t('dashboard.admin.helps.form.familyMembersCount')}</Label>
                  <Input
                    id="adminFamilyMembersCount"
                    type="number"
                    min="0"
                    value={formData.familyMembersCount}
                    onChange={(e) => setFormData(prev => ({ ...prev, familyMembersCount: e.target.value }))}
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.educationLevel')}</Label>
                  <Select value={formData.educationLevel} onValueChange={(value: TreatmentEducationLevel) => setFormData(prev => ({ ...prev, educationLevel: value }))}>
                    <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectEducationLevel')} /></SelectTrigger>
                    <SelectContent>
                      {(['illiterate', 'elementary', 'intermediate', 'high_school', 'diploma', 'bachelors', 'masters', 'doctorate'] as TreatmentEducationLevel[]).map((level) => (
                        <SelectItem key={level} value={level}>{t(`dashboard.admin.helps.form.educationLevels.${level}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.workSector')}</Label>
                  <Select value={formData.workSector} onValueChange={(value: TreatmentWorkSector) => setFormData(prev => ({ ...prev, workSector: value }))}>
                    <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectWorkSector')} /></SelectTrigger>
                    <SelectContent>
                      {(['government', 'private', 'military', 'retired', 'unemployed', 'student', 'other'] as TreatmentWorkSector[]).map((sector) => (
                        <SelectItem key={sector} value={sector}>{t(`dashboard.admin.helps.form.workSectors.${sector}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminProfession">{t('dashboard.admin.helps.form.profession')}</Label>
                  <Input id="adminProfession" value={formData.profession} onChange={(e) => setFormData(prev => ({ ...prev, profession: e.target.value }))} placeholder={t('dashboard.admin.helps.form.professionPlaceholder')} dir={dir} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminMonthlyIncome">{t('dashboard.admin.helps.form.monthlyIncome')}</Label>
                  <Input id="adminMonthlyIncome" type="number" min="0" value={formData.monthlyIncome} onChange={(e) => setFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))} dir="ltr" />
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.housingType')}</Label>
                  <Select value={formData.housingType} onValueChange={(value: TreatmentHousingType) => setFormData(prev => ({ ...prev, housingType: value }))}>
                    <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectHousingType')} /></SelectTrigger>
                    <SelectContent>
                      {(['owned', 'rented', 'provided', 'other'] as TreatmentHousingType[]).map((type) => (
                        <SelectItem key={type} value={type}>{t(`dashboard.admin.helps.form.housingTypes.${type}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.housingType === 'rented' && (
                  <div className="space-y-2">
                    <Label htmlFor="adminRentAmount">{t('dashboard.admin.helps.form.rentAmount')}</Label>
                    <Input id="adminRentAmount" type="number" min="0" value={formData.rentAmount} onChange={(e) => setFormData(prev => ({ ...prev, rentAmount: e.target.value }))} dir="ltr" />
                  </div>
                )}
              </div>
              )}

              {addFormStep === 4 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminMedicalCondition">{t('dashboard.admin.helps.form.condition')}</Label>
                  <Textarea
                    id="adminMedicalCondition"
                    value={formData.medicalCondition}
                    onChange={(e) => setFormData(prev => ({ ...prev, medicalCondition: e.target.value }))}
                    dir={dir}
                    rows={5}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="adminRequiredTreatment">{t('dashboard.admin.helps.form.treatment')}</Label>
                  <Textarea
                    id="adminRequiredTreatment"
                    value={formData.requiredTreatment}
                    onChange={(e) => setFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))}
                    dir={dir}
                    rows={5}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminCost">{t('dashboard.admin.helps.form.cost')}</Label>
                  <Input
                    id="adminCost"
                    type="number"
                    min="0"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                    dir="ltr"
                  />
              </div>

                {/* Medical Reports */}
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.medicalReports')}</Label>
                  <MedicalReportUploader
                    reports={pendingReports}
                    onReportsChange={setPendingReports}
                    translationPrefix="dashboard.admin.helps.form"
                  />
                </div>
              </div>
              )}

              {addFormStep === 5 && (
              <div className="space-y-4">
                {/* Is Case Critical */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.admin.helps.form.isCritical')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-isCritical"
                        checked={formData.isCritical === true}
                        onChange={() => setFormData(prev => ({ ...prev, isCritical: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-isCritical"
                        checked={formData.isCritical === false}
                        onChange={() => setFormData(prev => ({ ...prev, isCritical: false }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Is Patient Hospitalized */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.admin.helps.form.isHospitalized')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-isHospitalized"
                        checked={formData.isHospitalized === true}
                        onChange={() => setFormData(prev => ({ ...prev, isHospitalized: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-isHospitalized"
                        checked={formData.isHospitalized === false}
                        onChange={() => setFormData(prev => ({ ...prev, isHospitalized: false }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Has Insurance */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.admin.helps.form.hasInsurance')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-hasInsurance"
                        checked={formData.hasInsurance === true}
                        onChange={() => setFormData(prev => ({ ...prev, hasInsurance: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admin-hasInsurance"
                        checked={formData.hasInsurance === false}
                        onChange={() => setFormData(prev => ({ ...prev, hasInsurance: false, insuranceProvider: '' }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Insurance Provider & Category (conditional) */}
                {formData.hasInsurance && (
                  <>
                <div className="space-y-2">
                      <Label htmlFor="admin-insuranceProvider">{t('dashboard.admin.helps.form.insuranceProvider')}</Label>
                      <Input
                        id="admin-insuranceProvider"
                        value={formData.insuranceProvider}
                        onChange={(e) => setFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                    dir={dir}
                  />
                </div>
                <div className="space-y-2">
                      <Label htmlFor="admin-insuranceCategory">{t('dashboard.admin.helps.form.insuranceCategory')}</Label>
                    <Input
                        id="admin-insuranceCategory"
                        value={formData.insuranceCategory}
                        onChange={(e) => setFormData(prev => ({ ...prev, insuranceCategory: e.target.value }))}
                        dir={dir}
                      />
                    </div>
                  </>
                )}

                {/* Category */}
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.form.category')}</Label>
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.admin.helps.form.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>

                <div className="space-y-2">
                  <Label htmlFor="adminCoordinatorNotes">{t('dashboard.admin.helps.form.notes')}</Label>
                  <Textarea
                    id="adminCoordinatorNotes"
                    value={formData.coordinatorNotes}
                    onChange={(e) => setFormData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                    dir={dir}
                    rows={6}
                  />
                </div>
              </div>
              )}

            </div>

            <div className="flex justify-between gap-2 px-6 py-4 border-t shrink-0 bg-gray-50">
              {addFormStep > 1 ? (
                <Button type="button" variant="ghost" onClick={() => setAddFormStep(s => s - 1)}>
                  {t('common.previous')}
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                {addFormStep < ADD_TOTAL_STEPS ? (
                  <Button type="button" onClick={() => setAddFormStep(s => s + 1)}>
                    {t('common.next')}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {t('dashboard.admin.helps.form.submit')}
                  </Button>
                )}
              </div>
            </div>
          </form>
          </DialogContent>
        </Dialog>

      {/* View Case Modal */}
      <Dialog open={isViewOpen} onOpenChange={(open) => { setIsViewOpen(open); if (!open) { setPreviewUrl(null); setIsEditingDetails(false); setPendingReports([]); } }}>
        <DialogContent className="w-[95vw] max-w-7xl max-h-[99vh]" aria-describedby={undefined} dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle className="flex items-center gap-3">
              {selectedRequest && (
                <>
                  <span>{dir === 'rtl' ? (selectedRequest.nameArabic || selectedRequest.applicantName) : (selectedRequest.nameEnglish || selectedRequest.applicantName)}</span>
                  <span className="text-muted-foreground font-normal text-sm" dir="ltr">{selectedRequest.nationalId || selectedRequest.idNumber}</span>
                  {(() => {
                    const dob = (selectedRequest as any).dateOfBirth;
                    let age: number | null = null;
                    if (dob) {
                      const d = dob instanceof Date ? dob : dob.toDate ? dob.toDate() : new Date(dob);
                      if (!isNaN(d.getTime())) {
                        const now = new Date();
                        age = now.getFullYear() - d.getFullYear();
                        if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
                      }
                    }
                    if (age === null) age = (selectedRequest as any).age ?? null;
                    return age !== null ? <span className="text-muted-foreground font-normal text-sm">({age})</span> : null;
                  })()}
                  {(() => {
                    const cat = categories.find(c => c.id === selectedRequest.categoryId);
                    if (!cat) return null;
                    return <Badge variant="outline" className="font-normal">{dir === 'rtl' ? cat.nameAr : cat.name}</Badge>;
                  })()}
                  <Badge className={STATUS_COLORS[selectedRequest.status]}>
                    {t(`dashboard.admin.helps.statuses.${selectedRequest.status}`)}
                  </Badge>
                  {!readOnly && selectedRequest.status === 'approved_treatment' && (
                    <Button
                      type="button"
                      size="sm"
                      className="ms-auto me-8"
                      disabled={!canStartTreatment(selectedRequest).ok}
                      title={canStartTreatment(selectedRequest).ok ? '' : t('dashboard.admin.helps.startTreatmentGateTooltip')}
                      onClick={() => handleStartTreatment(selectedRequest)}
                    >
                      {t('dashboard.admin.helps.startTreatmentButton')}
                    </Button>
                  )}
                  {!readOnly && selectedRequest.status === 'treatment_started' && (
                    <Button
                      type="button"
                      size="sm"
                      className="ms-auto me-8"
                      onClick={() => handleMarkCompleted(selectedRequest)}
                    >
                      {t('dashboard.admin.helps.markCompletedButton')}
                    </Button>
                  )}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <Tabs defaultValue="details" className="w-full" dir={dir} onValueChange={(tab) => {
              if (tab === 'reports') {
                const firstReport = selectedRequest.medicalReports?.[0]?.url || selectedRequest.medicalReportUrl || null;
                setPreviewUrl(firstReport);
              } else if (tab === 'progressReports') {
                setPreviewUrl(selectedRequest.progressReports?.[0]?.url || null);
              } else if (tab === 'invoices') {
                const firstInvoice = selectedRequest.invoices?.[0]?.url || selectedRequest.invoiceUrls?.[0] || null;
                setPreviewUrl(firstInvoice);
              } else if (tab === 'paymentSlips') {
                setPreviewUrl(selectedRequest.paymentSlipUrls?.[0] || null);
              } else {
                setPreviewUrl(null);
              }
            }}>
              <TabsList className="w-full mb-4">
                <TabsTrigger value="details" className="flex-1">{t('dashboard.admin.helps.caseTabs.details')}</TabsTrigger>
                <TabsTrigger value="reports" className="flex-1 gap-2">
                  {t('dashboard.admin.helps.caseTabs.reports')}
                  {(selectedRequest.medicalReports?.length || 0) > 0 && (
                    <Badge variant="secondary" className="text-xs">{selectedRequest.medicalReports?.length}</Badge>
                  )}
                </TabsTrigger>
                {['approved', 'approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="donations" className="flex-1 gap-2">
                    {t('dashboard.admin.helps.caseTabs.donations')}
                    {(selectedRequest.donationEntries?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.donationEntries?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {['approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="progressReports" className="flex-1 gap-2">
                    {t('dashboard.admin.helps.caseTabs.progressReports')}
                    {(selectedRequest.progressReports?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.progressReports?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {['approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="invoices" className="flex-1 gap-2">
                    {t('dashboard.admin.helps.caseTabs.invoices')}
                    {(selectedRequest.invoices?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.invoices?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {selectedRequest.status === 'pending_invoices' && (
                  <TabsTrigger value="paymentSlips" className="flex-1 gap-2">
                    {t('dashboard.admin.helps.caseTabs.paymentSlips')}
                    {(selectedRequest.paymentSlipUrls?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.paymentSlipUrls?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                <TabsTrigger value="updates" className="flex-1 gap-2">
                  {t('dashboard.admin.helps.caseTabs.updates')}
                  {(selectedRequest.updates?.length || 0) > 0 && (
                    <Badge variant="secondary" className="text-xs">{selectedRequest.updates?.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="max-h-[72vh] flex flex-col">
                {/* Edit Toggle Button */}
                <div className="flex justify-end mb-4 shrink-0">
                  {!readOnly && selectedRequest.status !== 'completed' && !isEditingDetails && (
                    <Button variant="outline" onClick={startEditingDetails}>
                      <Edit className="h-4 w-4 me-2" />
                      {t('dashboard.admin.helps.edit')}
                    </Button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto">

                {isEditingDetails ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    {/* Card 1 - Personal Info */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.personalInfo')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.nameArabic')}</Label>
                        <Input
                          value={editFormData.nameArabic}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nameArabic: e.target.value }))}
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.nameEnglish')}</Label>
                        <Input
                          value={editFormData.nameEnglish}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nameEnglish: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.idType')}</Label>
                        <Select
                          value={editFormData.idType}
                          onValueChange={(value: TreatmentIdType) => setEditFormData(prev => ({ ...prev, idType: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="national_id">{t('dashboard.admin.helps.form.idTypes.nationalId')}</SelectItem>
                            <SelectItem value="residency">{t('dashboard.admin.helps.form.idTypes.residency')}</SelectItem>
                            <SelectItem value="umrah_visit_visa">{t('dashboard.admin.helps.form.idTypes.umrahVisitVisa')}</SelectItem>
                            <SelectItem value="passport">{t('dashboard.admin.helps.form.idTypes.passport')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.idNumber')} *</Label>
                        <Input
                          value={editFormData.idNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <HijriDatePicker label={t('dashboard.admin.helps.form.dateOfBirth')} labelClassName="text-xs font-medium leading-none" value={editFormData.dateOfBirth} onChange={(v) => setEditFormData(prev => ({ ...prev, dateOfBirth: v }))} />
                      </div>
                      <div className="space-y-1">
                        <HijriDatePicker label={t('dashboard.admin.helps.form.idExpiryDate')} labelClassName="text-xs font-medium leading-none" value={editFormData.idExpiryDate} onChange={(v) => setEditFormData(prev => ({ ...prev, idExpiryDate: v }))} />
                      </div>
                      
                      {/* ID Document Upload */}
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.idDocument')}</Label>
                        {selectedRequest.idDocumentUrl ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setIdPreviewUrl(selectedRequest.idDocumentUrl!)}
                              className="block w-16 h-12 border rounded-lg overflow-hidden bg-muted hover:opacity-80 hover:ring-2 hover:ring-primary transition-all flex-shrink-0 flex items-center justify-center cursor-pointer"
                            >
                              {isPdfUrl(selectedRequest.idDocumentUrl) ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
                                  <span className="text-[8px] font-bold text-red-500">PDF</span>
                                </div>
                              ) : (
                                <img src={selectedRequest.idDocumentUrl} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              )}
                            </button>
                            <label className="cursor-pointer flex-1">
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadIdDocument(file);
                                }}
                                disabled={isSubmitting}
                              />
                              <Button variant="outline" size="sm" className="w-full h-10" asChild disabled={isSubmitting}>
                                <span className="flex items-center gap-1">
                                  {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                  {t('dashboard.admin.helps.form.uploadIdDocument')}
                                </span>
                              </Button>
                            </label>
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadIdDocument(file);
                              }}
                              disabled={isSubmitting}
                            />
                            <div className="flex items-center justify-center gap-2 h-10 border-2 border-dashed rounded-md hover:border-primary transition-colors">
                              {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                <Upload className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-xs text-muted-foreground">
                                {t('dashboard.admin.helps.form.uploadIdDocument')}
                              </span>
                            </div>
                          </label>
                        )}
                      </div>
                      </div>
                    </div>

                    {/* Card 2 - Contact & Demographics */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.contact')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.nationality')}</Label>
                        <Input
                          value={editFormData.nationality}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nationality: e.target.value }))}
                          dir={dir}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.email')}</Label>
                        <Input type="email" value={editFormData.email} onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))} dir="ltr" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.phoneNumber')} *</Label>
                        <Input
                          value={editFormData.phoneNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          placeholder="05XXXXXXXX"
                          required
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.relativeMobileNumber')}</Label>
                        <Input
                          value={editFormData.relativeMobileNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))}
                          placeholder="05XXXXXXXX"
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.city')}</Label>
                        <Input value={editFormData.city} onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))} dir={dir} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.neighborhood')}</Label>
                        <Input value={editFormData.neighborhood} onChange={(e) => setEditFormData(prev => ({ ...prev, neighborhood: e.target.value }))} dir={dir} />
                      </div>
                      </div>
                    </div>

                    {/* Card 3 - Socioeconomic */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.socioeconomic')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.gender')}</Label>
                        <Select
                          value={editFormData.gender}
                          onValueChange={(value: TreatmentGender) => setEditFormData(prev => ({ ...prev, gender: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">{t('dashboard.admin.helps.form.genders.male')}</SelectItem>
                            <SelectItem value="female">{t('dashboard.admin.helps.form.genders.female')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.maritalStatus')}</Label>
                        <Select
                          value={editFormData.maritalStatus}
                          onValueChange={(value: TreatmentMaritalStatus) => setEditFormData(prev => ({ ...prev, maritalStatus: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">{t('dashboard.admin.helps.form.maritalStatuses.single')}</SelectItem>
                            <SelectItem value="married">{t('dashboard.admin.helps.form.maritalStatuses.married')}</SelectItem>
                            <SelectItem value="divorced">{t('dashboard.admin.helps.form.maritalStatuses.divorced')}</SelectItem>
                            <SelectItem value="widowed">{t('dashboard.admin.helps.form.maritalStatuses.widowed')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.familyMembersCount')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editFormData.familyMembersCount}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, familyMembersCount: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.educationLevel')}</Label>
                        <Select value={editFormData.educationLevel} onValueChange={(value: TreatmentEducationLevel) => setEditFormData(prev => ({ ...prev, educationLevel: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectEducationLevel')} /></SelectTrigger>
                          <SelectContent>
                            {(['illiterate', 'elementary', 'intermediate', 'high_school', 'diploma', 'bachelors', 'masters', 'doctorate'] as TreatmentEducationLevel[]).map((level) => (
                              <SelectItem key={level} value={level}>{t(`dashboard.admin.helps.form.educationLevels.${level}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.workSector')}</Label>
                        <Select value={editFormData.workSector} onValueChange={(value: TreatmentWorkSector) => setEditFormData(prev => ({ ...prev, workSector: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectWorkSector')} /></SelectTrigger>
                          <SelectContent>
                            {(['government', 'private', 'military', 'retired', 'unemployed', 'student', 'other'] as TreatmentWorkSector[]).map((sector) => (
                              <SelectItem key={sector} value={sector}>{t(`dashboard.admin.helps.form.workSectors.${sector}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.profession')}</Label>
                        <Input value={editFormData.profession} onChange={(e) => setEditFormData(prev => ({ ...prev, profession: e.target.value }))} dir={dir} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.monthlyIncome')}</Label>
                        <Input type="number" min="0" value={editFormData.monthlyIncome} onChange={(e) => setEditFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))} dir="ltr" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.housingType')}</Label>
                        <Select value={editFormData.housingType} onValueChange={(value: TreatmentHousingType) => setEditFormData(prev => ({ ...prev, housingType: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.form.selectHousingType')} /></SelectTrigger>
                          <SelectContent>
                            {(['owned', 'rented', 'provided', 'other'] as TreatmentHousingType[]).map((type) => (
                              <SelectItem key={type} value={type}>{t(`dashboard.admin.helps.form.housingTypes.${type}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {editFormData.housingType === 'rented' && (
                        <div className="space-y-1">
                          <Label className="text-xs">{t('dashboard.admin.helps.form.rentAmount')}</Label>
                          <Input type="number" min="0" value={editFormData.rentAmount} onChange={(e) => setEditFormData(prev => ({ ...prev, rentAmount: e.target.value }))} dir="ltr" />
                        </div>
                      )}
                      </div>
                    </div>

                    {/* Card 4 - Medical Info */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.medical')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.condition')}</Label>
                        <Textarea
                          value={editFormData.medicalCondition}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, medicalCondition: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.treatment')}</Label>
                        <Textarea
                          value={editFormData.requiredTreatment}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.cost')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editFormData.cost}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, cost: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      </div>
                    </div>

                    {/* Card 5 - Case Classifications */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.caseStatus')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.admin.helps.form.isCritical')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-isCritical"
                              checked={editFormData.isCritical === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, isCritical: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-isCritical"
                              checked={editFormData.isCritical === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, isCritical: false }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.admin.helps.form.isHospitalized')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-isHospitalized"
                              checked={editFormData.isHospitalized === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, isHospitalized: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-isHospitalized"
                              checked={editFormData.isHospitalized === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, isHospitalized: false }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.admin.helps.form.hasInsurance')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-hasInsurance"
                              checked={editFormData.hasInsurance === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, hasInsurance: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="admin-edit-hasInsurance"
                              checked={editFormData.hasInsurance === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, hasInsurance: false, insuranceProvider: '', insuranceCategory: '' }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      {editFormData.hasInsurance && (
                        <>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('dashboard.admin.helps.form.insuranceProvider')}</Label>
                            <Input
                              value={editFormData.insuranceProvider}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                              dir={dir}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('dashboard.admin.helps.form.insuranceCategory')}</Label>
                            <Input
                              value={editFormData.insuranceCategory}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, insuranceCategory: e.target.value }))}
                              dir={dir}
                            />
                          </div>
                        </>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.category')}</Label>
                        <Select
                          value={editFormData.categoryId}
                          onValueChange={(value) => setEditFormData(prev => ({ ...prev, categoryId: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('dashboard.admin.helps.form.selectCategory')} />
                          </SelectTrigger>
                          <SelectContent>
                            {activeCategories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.admin.helps.form.notes')}</Label>
                        <Textarea
                          value={editFormData.coordinatorNotes}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Column 1 - Applicant Info */}
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg space-y-3">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.admin.helps.applicantInfo')}
                        </h4>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.nationalId')}</Label>
                          <p className="font-mono font-medium">{selectedRequest.nationalId}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.age')}</Label>
                          <p className="font-medium">{selectedRequest.dateOfBirth ? Math.floor((Date.now() - new Date(selectedRequest.dateOfBirth).getTime()) / 31557600000) : (selectedRequest.age || '-')} {t('common.years')}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.applicantName')}</Label>
                          <p className="font-medium">{selectedRequest.applicantName}</p>
                        </div>
                        {selectedRequest.phoneNumber && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.phoneNumber')}</Label>
                            <p className="font-medium font-mono" dir="ltr">{selectedRequest.phoneNumber}</p>
                          </div>
                        )}
                        
                        {/* ID Document (View Only) */}
                        {selectedRequest.idDocumentUrl && (
                          <div className="pt-2 border-t">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.idDocument')}</Label>
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setIdPreviewUrl(selectedRequest.idDocumentUrl!)}
                                className="w-20 h-14 border rounded-lg overflow-hidden bg-muted hover:opacity-80 hover:ring-2 hover:ring-primary transition-all flex-shrink-0 flex items-center justify-center cursor-pointer"
                              >
                                {isPdfUrl(selectedRequest.idDocumentUrl) ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
                                    <span className="text-[9px] font-bold text-red-500">PDF</span>
                                  </div>
                                ) : (
                                  <img src={selectedRequest.idDocumentUrl} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                )}
                              </button>
                              <span className="text-sm truncate max-w-[150px] text-muted-foreground">{selectedRequest.idDocumentName || 'ID Document'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Column 2 - Medical Info */}
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg space-y-3">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.admin.helps.medicalInfo')}
                        </h4>
                        {(selectedRequest.categoryId || selectedRequest.categoryName) && (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(selectedRequest.categoryId)}`}>
                              {getCategoryLabel(selectedRequest)}
                            </span>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.condition')}</Label>
                          <p className="font-medium">{selectedRequest.medicalCondition}</p>
                        </div>
                        {selectedRequest.requiredTreatment && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.treatment')}</Label>
                            <p className="font-medium">{selectedRequest.requiredTreatment}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.cost')}</Label>
                          <p className="font-medium text-lg inline-flex items-center gap-1">{getNetMedicalCost(selectedRequest).toLocaleString('en-US')} <SaudiRiyalIcon className="h-5 w-5" /></p>
                        </div>
                        {selectedRequest.approvedAmount !== undefined && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.approvedAmount')}</Label>
                            <p className="font-medium text-lg text-green-600 inline-flex items-center gap-1">{selectedRequest.approvedAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-5 w-5" /></p>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* Column 3 - Workflow Info */}
                    <div className="space-y-4">
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.admin.helps.caseInfo')}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.createdAt')}</Label>
                          <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.createdAt))}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.createdBy')}</Label>
                          <p className="font-medium text-sm capitalize">
                            {translateRole(selectedRequest.createdBy || 'customer')}
                            {selectedRequest.submittedByName && selectedRequest.createdBy === 'customer' && (
                              <span className="text-muted-foreground"> ({selectedRequest.submittedByName})</span>
                            )}
                          </p>
                        </div>
                        {selectedRequest.reviewedAt && (
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.reviewedAt')}</Label>
                            <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.reviewedAt))}</p>
                          </div>
                        )}
                        {selectedRequest.adminDecisionAt && (
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.decisionAt')}</Label>
                            <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.adminDecisionAt))}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Coordinator Review Info */}
                    {(selectedRequest.socialStatusFits !== undefined || selectedRequest.coordinatorNotes) && (
                      <div className="p-4 border rounded-lg space-y-3 bg-blue-50 dark:bg-blue-950">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.admin.helps.coordinatorReview')}
                        </h4>
                        <div className="space-y-2">
                          {selectedRequest.socialStatusFits !== undefined && (
                            <div className="flex items-center gap-2">
                              <Badge variant={selectedRequest.socialStatusFits ? 'default' : 'destructive'}>
                                {selectedRequest.socialStatusFits ? '✓' : '✗'}
                        </Badge>
                              <span className="text-sm">{t('dashboard.admin.helps.reviewForm.socialStatusFits')}</span>
                            </div>
                          )}
                          {selectedRequest.medicalConditionFits !== undefined && (
                            <div className="flex items-center gap-2">
                              <Badge variant={selectedRequest.medicalConditionFits ? 'default' : 'destructive'}>
                                {selectedRequest.medicalConditionFits ? '✓' : '✗'}
                        </Badge>
                              <span className="text-sm">{t('dashboard.admin.helps.reviewForm.medicalConditionFits')}</span>
                        </div>
                          )}
                          {selectedRequest.coordinatorNotes && (
                            <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded">
                              <Label className="text-xs text-muted-foreground">{t('dashboard.admin.helps.form.notes')}</Label>
                              <p className="text-sm">{selectedRequest.coordinatorNotes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {selectedRequest.adminNotes && (
                      <div className="p-4 border rounded-lg space-y-3 bg-purple-50 dark:bg-purple-950">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.admin.helps.adminNotes')}
                        </h4>
                        <p className="text-sm">{selectedRequest.adminNotes}</p>
                      </div>
                    )}

                    {/* Cost Summary Card */}
                    {((selectedRequest.cost || 0) > 0 || (selectedRequest.invoices && selectedRequest.invoices.length > 0)) && (
                      <div className="p-4 border rounded-lg space-y-3 bg-primary/5 border-primary/20">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.admin.helps.costSummary')}
                        </h4>
                        <div className="space-y-2">
                          {selectedRequest.invoices && selectedRequest.invoices.length > 0 && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('dashboard.admin.helps.totalInvoices')}</span>
                                <span className="font-bold text-primary inline-flex items-center gap-1">
                                  {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                                </span>
                              </div>
                              {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0) > 0 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-muted-foreground">{t('dashboard.admin.helps.totalVATInvoices')}</span>
                                  <span className="font-medium text-sm inline-flex items-center gap-1">
                                    {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                                  </span>
                                </div>
                              )}
                              {(() => {
                                const expectedCb = (selectedRequest.invoices || []).reduce((sum: number, inv: any) => {
                                  const prov = providers.find(p => p.id === inv.providerId);
                                  const pct = prov?.cashbackPercentage || 0;
                                  return sum + ((inv.totalAfterDiscount || 0) + (inv.taxAmount || 0)) * pct / 100;
                                }, 0);
                                return expectedCb > 0 ? (
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-purple-700">{t('dashboard.admin.helps.cashbackExpected')}</span>
                                    <span className="font-medium text-sm text-purple-700 inline-flex items-center gap-1">
                                      {Math.round(expectedCb).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                                    </span>
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                )}
                </div>
                {isEditingDetails && selectedRequest.status !== 'completed' && (
                  <div className="flex justify-end gap-2 pt-4 border-t mt-4 shrink-0 bg-white -mx-2 px-2 py-3 rounded-b-lg">
                    <Button variant="outline" onClick={() => setIsEditingDetails(false)} disabled={isSubmitting}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={saveEditedDetails} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                      {t('common.save')}
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="reports" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Reports List */}
                  <div className="w-full md:w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col max-h-[30vh] md:max-h-none">
                    {/* Header with Add Report Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      {t('dashboard.admin.helps.medicalReports')}
                    </h4>
                      {!readOnly && selectedRequest.status !== 'completed' && (
                        <MedicalReportUploader
                          reports={pendingReports}
                          onReportsChange={setPendingReports}
                          translationPrefix="dashboard.admin.helps"
                        />
                      )}
                    </div>
                    
                    {/* Reports with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                    {selectedRequest.medicalReports?.map((report, i) => (
                        <div
                          key={`rep-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === report.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(report.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{report.name}</span>
                              </div>
                              {report.providerName && (
                                <p className={`text-sm truncate ${previewUrl === report.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{report.providerName}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={report.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('report'); }}
                                >
                                  <Edit className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          </div>
                      </div>
                    ))}
                      
                      {/* Legacy reports (single URL) */}
                    {selectedRequest.medicalReportUrl && !selectedRequest.medicalReports?.length && (
                        <div
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === selectedRequest.medicalReportUrl ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                        onClick={() => setPreviewUrl(selectedRequest.medicalReportUrl!)}
                      >
                          <div className="flex gap-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="h-4 w-4 flex-shrink-0" />
                              <span className="font-medium text-sm">{t('dashboard.admin.helps.viewReport')}</span>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={selectedRequest.medicalReportUrl!}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                            </div>
                          </div>
                        </div>
                    )}
                    
                    {!selectedRequest.medicalReports?.length && !selectedRequest.medicalReportUrl && (
                      <p className="text-sm text-muted-foreground">{t('dashboard.admin.helps.noReports')}</p>
                    )}
                    </div>
                    
                    {/* Upload Pending Reports Button */}
                    {pendingReports.length > 0 && selectedRequest.status !== 'completed' && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                          <Button
                            size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={handleAddReportFromDetails}
                          disabled={isSubmitting}
                          >
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('dashboard.admin.helps.uploadReport')} ({pendingReports.length})
                          </Button>
                        {/* Pending Reports Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingReports.map((report, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {report.thumbnailUrl ? (
                                  report.file.type === 'application/pdf' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-muted">
                                      <FileText className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                  ) : (
                                    <img src={report.thumbnailUrl} alt={report.file.name} className="w-full h-full object-cover" />
                                  )
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                          )}
                        </div>
                          <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingReports(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                          </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium truncate max-w-32">{report.file.name}</div>
                                <div className="text-muted-foreground">{report.providerName}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        </div>
                    )}
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Document Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Document Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.admin.helps.selectReport')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Donations Tab */}
              <TabsContent value="donations" className="h-[72vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-3">
                    {(() => {
                      const caseCost = getCaseRemainingBudget(selectedRequest.id).caseCost;
                      return (
                        <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-amber-50 border border-amber-200">
                          <span className="text-sm text-amber-700">{t('dashboard.admin.helps.caseCost')}</span>
                          <span className="font-semibold text-amber-900 inline-flex items-center gap-1" dir="ltr">
                            {caseCost.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const caseCost = getCaseRemainingBudget(selectedRequest.id).caseCost;
                      const totalFunded = selectedRequest.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
                      const coveragePct = caseCost > 0 ? Math.round((totalFunded / caseCost) * 100) : 0;
                      const expectedCashback = (selectedRequest.invoices || []).reduce((sum: number, inv: any) => {
                        const prov = providers.find(p => p.id === inv.providerId);
                        const pct = prov?.cashbackPercentage || 0;
                        return sum + ((inv.totalAfterDiscount || 0) + (inv.taxAmount || 0)) * pct / 100;
                      }, 0);
                      return (
                        <>
                          <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-blue-50 border border-blue-200">
                            <span className="text-sm text-blue-700">{t('dashboard.admin.helps.totalFunded')}</span>
                            <span className="font-semibold text-blue-900 inline-flex items-center gap-1" dir="ltr">
                              {totalFunded.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200">
                            <span className="text-sm text-emerald-700">{t('dashboard.admin.helps.coverage')}</span>
                            <span className="font-semibold text-emerald-900">
                              {coveragePct}%
                            </span>
                          </div>
                          {expectedCashback > 0 && (
                            <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-purple-50 border border-purple-200">
                              <span className="text-sm text-purple-700">{t('dashboard.admin.helps.cashbackExpected')}</span>
                              <span className="font-semibold text-purple-900 inline-flex items-center gap-1" dir="ltr">
                                {Math.round(expectedCashback).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* Existing Donations List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.admin.helps.donationsList')}
                      </h4>
                      {!readOnly && ['approved', 'approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                        <Button
                          size="sm"
                          onClick={() => { setAllocateLinkKey(''); setAllocateAmount(''); setShowAllocateLink(true); }}
                        >
                          <Link2 className="h-4 w-4 me-2" />
                          {t('dashboard.admin.helps.allocateLink')}
                        </Button>
                      )}
                    </div>
                    {selectedRequest.donationEntries && selectedRequest.donationEntries.length > 0 ? (
                      <div className="space-y-3">
                        {selectedRequest.donationEntries.map((entry, index) => {
                          const lk = links.find(l => l.id === entry.linkId);
                          return (
                          <div key={index} className="p-4 border rounded-lg bg-card space-y-3">
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <Badge variant="secondary">
                                      {t(`dashboard.admin.helps.platforms.${entry.platform}`)}
                                    </Badge>
                                    <span className="font-mono font-medium" dir="ltr">{entry.caseNumber}</span>
                                    {lk?.treatmentCategoryId && (() => {
                                      const cat = activeCategories.find(c => c.id === lk.treatmentCategoryId);
                                      return cat ? <Badge variant="outline">{dir === 'rtl' ? (cat.nameAr || cat.name) : cat.name}</Badge> : null;
                                    })()}
                                    {lk?.linkTitle && (
                                      <span className="text-sm text-muted-foreground">{lk.linkTitle}</span>
                                    )}
                                    {lk?.url && (
                                      <a
                                        href={lk.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        {t('dashboard.admin.helps.form.donationUrl')}
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {(() => {
                                      const usage = linkUsageMap[entry.linkId];
                                      if (!usage || usage.count <= 1) return null;
                                      const pct = usage.totalAllocated > 0 ? Math.round(((entry.amount || 0) / usage.totalAllocated) * 100) : 0;
                                      return (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 border border-purple-200 text-xs font-medium text-purple-700">
                                          {pct}% · <span dir="ltr" className="inline-flex items-center gap-0.5">{usage.totalAllocated.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></span> ({usage.count})
                                        </span>
                                      );
                                    })()}
                                    {!readOnly && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveDonation(index)}
                                        disabled={isSubmitting}
                                        className="text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {entry.amount !== undefined && entry.amount > 0 && (
                                  <div className="flex gap-6 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">{t('dashboard.admin.helps.form.amount')}: </span>
                                      <span className="font-medium inline-flex items-center gap-1" dir="ltr">{entry.amount.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span>
                                    </div>
                                  </div>
                                )}
                                {entry.addedAt && (
                                  <div className="text-xs text-muted-foreground">
                                    {formatLocalDate(new Date(entry.addedAt))}
                                  </div>
                                )}
                              </>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>{t('dashboard.admin.helps.noDonations')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Allocate Link to Case Modal */}
              <Dialog open={showAllocateLink} onOpenChange={(open) => {
                setShowAllocateLink(open);
                if (!open) { setAllocateLinkKey(''); setAllocateAmount(''); }
              }}>
                <DialogContent className="max-w-lg" aria-describedby={undefined} dir={dir}>
                  <DialogHeader>
                    <DialogTitle>{t('dashboard.admin.helps.allocateLink')}</DialogTitle>
                  </DialogHeader>
                  {(() => {
                    // Available links: have remaining budget and not already on this case
                    const caseEntryKeys = new Set(
                      (selectedRequest?.donationEntries || []).map(e => e.linkId)
                    );
                    const caseCategoryId = selectedRequest?.categoryId;
                    const availableLinks = !caseCategoryId
                      ? []
                      : allLinks.filter(l =>
                          l.remaining > 0 &&
                          !caseEntryKeys.has(l._key) &&
                          l.treatmentCategoryId === caseCategoryId,
                        );
                    const selectedLink = allLinks.find(l => l._key === allocateLinkKey);
                    const caseBudget = selectedRequest ? getCaseRemainingBudget(selectedRequest.id) : { caseCost: 0, totalFunded: 0, remaining: 0 };
                    const maxAmount = selectedLink ? Math.min(selectedLink.remaining, caseBudget.remaining) : 0;

                    return (
                      <>
                        <div className="space-y-4 py-4">
                          {!caseCategoryId && (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                              {t('dashboard.admin.helps.linksView.classifyCaseFirst')}
                            </p>
                          )}
                          {caseCategoryId && availableLinks.length === 0 && (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                              {t('dashboard.admin.helps.linksView.noLinksForCategory')}
                            </p>
                          )}
                          <div className="space-y-2">
                            <Label>{t('dashboard.admin.helps.selectLink')} *</Label>
                            {(() => {
                              const sel = allLinks.find(l => l._key === allocateLinkKey);
                              const selectLabel = sel
                                ? `${sel.linkTitle ? `${sel.linkTitle} — ` : ''}${t(`dashboard.admin.helps.platforms.${sel.platform}`)} / ${sel.platformCaseNumber}`
                                : t('dashboard.admin.helps.selectLinkPlaceholder');
                              return (
                                <Popover open={openAllocateLinkPicker} onOpenChange={setOpenAllocateLinkPicker}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={openAllocateLinkPicker}
                                      className="w-full justify-between h-10 font-normal"
                                      dir={dir}
                                    >
                                      <span className="truncate text-start">{selectLabel}</span>
                                      <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                    dir={dir}
                                  >
                                    <Command
                                      filter={(value, search) => {
                                        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                                      }}
                                    >
                                      <CommandInput placeholder={t('dashboard.admin.helps.selectLinkPlaceholder')} dir={dir} />
                                      <CommandList>
                                        <CommandEmpty>{t('dashboard.admin.helps.linksView.noLinksForCategory')}</CommandEmpty>
                                        <CommandGroup>
                                          {availableLinks.map((l) => {
                                            const label = `${l.linkTitle ? `${l.linkTitle} — ` : ''}${t(`dashboard.admin.helps.platforms.${l.platform}`)} / ${l.platformCaseNumber}`;
                                            const haystack = `${label} ${l.platformCaseNumber} ${l.linkTitle || ''}`.toLowerCase();
                                            return (
                                              <CommandItem
                                                key={l._key}
                                                value={haystack}
                                                onSelect={() => {
                                                  setAllocateLinkKey(l._key);
                                                  const max = Math.min(l.remaining, caseBudget.remaining);
                                                  setAllocateAmount(max > 0 ? String(max) : '');
                                                  setOpenAllocateLinkPicker(false);
                                                }}
                                              >
                                                <Check className={`me-2 h-4 w-4 ${allocateLinkKey === l._key ? 'opacity-100' : 'opacity-0'}`} />
                                                <div className="flex flex-col">
                                                  <span className="text-sm">{label}</span>
                                                  <span className="text-xs text-muted-foreground" dir="ltr">
                                                    {t('dashboard.admin.helps.linksView.remaining')}: {l.remaining.toLocaleString('en-US')} SAR
                                                  </span>
                                                </div>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              );
                            })()}
                          </div>
                          {selectedLink && (
                            <>
                              <div className="px-3 py-2 rounded-md text-xs bg-muted/50 border space-y-1">
                                {selectedLink.linkTitle && <p className="font-medium text-sm">{selectedLink.linkTitle}</p>}
                                <p>{t('dashboard.admin.helps.linkBudget.available')}: <span className="font-semibold" dir="ltr">{selectedLink.remaining.toLocaleString('en-US')}</span> SAR</p>
                                <p>{t('dashboard.admin.helps.caseBudget.remaining')}: <span className="font-semibold" dir="ltr">{caseBudget.remaining.toLocaleString('en-US')}</span> SAR</p>
                                {maxAmount > 0 && <p>{t('dashboard.admin.helps.linkBudget.maxLabel')}: <span className="font-semibold" dir="ltr">{maxAmount.toLocaleString('en-US')}</span> SAR</p>}
                              </div>
                              <div className="space-y-2">
                                <Label>{t('dashboard.admin.helps.form.amount')} *</Label>
                                <Input
                                  type="number"
                                  value={allocateAmount}
                                  onChange={(e) => setAllocateAmount(e.target.value)}
                                  dir="ltr"
                                  max={maxAmount}
                                  placeholder={t('dashboard.admin.helps.form.amountPlaceholder')}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => { setShowAllocateLink(false); setAllocateLinkKey(''); setAllocateAmount(''); }}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            onClick={handleAllocateLink}
                            disabled={isSubmitting || !allocateLinkKey || !allocateAmount || Number(allocateAmount) <= 0}
                          >
                            {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Link2 className="h-4 w-4 me-2" />}
                            {t('dashboard.admin.helps.allocateBtn')}
                          </Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Invoices Tab */}
              <TabsContent value="progressReports" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Progress Reports List */}
                  <div className="w-full md:w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col max-h-[30vh] md:max-h-none">
                    {/* Header with Add Report Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('common.progressReports.title')}
                      </h4>
                      {!readOnly && ['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                        <ProgressReportUploader
                          reports={[]}
                          pending={pendingProgressReports}
                          onPendingChange={setPendingProgressReports}
                          translationPrefix="common.progressReports"
                          hideList
                        />
                      )}
                    </div>

                    {/* Reports with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {selectedRequest.progressReports?.map((report, i) => (
                        <div
                          key={`prog-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === report.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(report.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{report.name}</span>
                              </div>
                              {report.providerName && (
                                <p className={`text-sm truncate ${previewUrl === report.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{(() => {
                                  const p = activeProviders.find(pr => pr.id === report.providerId);
                                  return language === 'ar'
                                    ? (p?.nameAr || report.providerName)
                                    : (report.providerName || p?.name);
                                })()}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={report.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('progressReport'); }}
                                >
                                  <Edit className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {!selectedRequest.progressReports?.length && (
                        <p className="text-sm text-muted-foreground">{t('common.progressReports.list.empty')}</p>
                      )}
                    </div>

                    {/* Upload Pending Reports Button */}
                    {pendingProgressReports.length > 0 && ['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                        <Button
                          size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={handleUploadProgressReports}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('common.progressReports.addButton')} ({pendingProgressReports.length})
                        </Button>
                        {/* Pending Reports Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingProgressReports.map((report, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {report.thumbnailUrl && report.file && !report.file.type.includes('pdf') ? (
                                  <img src={report.thumbnailUrl} alt={report.file?.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingProgressReports(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium truncate max-w-32">{report.file?.name}</div>
                                <div className="text-muted-foreground">{language === 'ar' ? report.providerNameAr || report.providerName : report.providerName}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Document Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Document Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.admin.helps.selectReport')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="invoices" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Invoices List */}
                  <div className="w-full md:w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col max-h-[30vh] md:max-h-none">
                    {/* Header with Add Invoice Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      {t('dashboard.admin.helps.treatmentInvoices')}
                    </h4>
                      {!readOnly && selectedRequest.status === 'treatment_started' && (
                        <InvoiceUploader
                          invoices={pendingInvoices}
                          onInvoicesChange={setPendingInvoices}
                          translationPrefix="dashboard.admin.helps"
                          nationalId={selectedRequest.nationalId}
                        />
                      )}
                    </div>
                    
                    {/* Invoices with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {selectedRequest.invoices?.map((invoice, i) => (
                        <div
                          key={`inv-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === invoice.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(invoice.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 flex-shrink-0" />
                                  <span className="font-medium text-sm">{invoice.invoiceNumber}</span>
                                </div>
                                <span className="font-medium text-sm inline-flex items-center gap-1">{((invoice.totalAfterDiscount || 0) + (invoice.taxAmount || 0)).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span>
                              </div>
                              {(invoice.providerName || invoice.providerNameAr || invoice.providerId) && (
                                <p className={`text-sm truncate ${previewUrl === invoice.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{(() => {
                                  const p = activeProviders.find(pr => pr.id === invoice.providerId);
                                  return language === 'ar'
                                    ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                                    : (invoice.providerName || p?.name || invoice.providerNameAr);
                                })()}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={invoice.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('invoice'); }}
                                >
                                  <Edit className="h-2.5 w-2.5" />
                                </button>
                              )}
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center h-5 w-5 rounded border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(i); }}
                                >
                                  <Trash2 className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Legacy invoices (without details) */}
                      {!selectedRequest.invoices?.length && selectedRequest.invoiceUrls?.map((url, i) => (
                        <div
                          key={`inv-legacy-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(url)}
                        >
                          <div className="flex gap-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="h-4 w-4 flex-shrink-0" />
                              <span className="text-sm">{t('dashboard.admin.helps.invoice')} {i + 1}</span>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center h-5 w-5 rounded border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLegacyInvoice(i); }}
                                >
                                  <Trash2 className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {!selectedRequest.invoices?.length && !selectedRequest.invoiceUrls?.length && (
                        <p className="text-sm text-muted-foreground">{t('dashboard.admin.helps.noInvoices')}</p>
                          )}
                        </div>

                    {/* Upload Pending Invoices Button */}
                    {pendingInvoices.length > 0 && selectedRequest.status === 'treatment_started' && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                          <Button
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={handleAddInvoiceFromDetails}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('dashboard.admin.helps.uploadInvoiceBtn')} ({pendingInvoices.length})
                          </Button>
                        {/* Pending Invoices Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingInvoices.map((invoice, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {invoice.thumbnailUrl && invoice.file ? (
                                  invoice.file.type === 'application/pdf' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-muted">
                                      <FileText className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                  ) : (
                                    <img src={invoice.thumbnailUrl} alt={invoice.file.name} className="w-full h-full object-cover" />
                                  )
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingInvoices(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium">{invoice.invoiceNumber}</div>
                                <div className="text-muted-foreground">{(() => {
                                  const p = activeProviders.find(pr => pr.id === invoice.providerId);
                                  return language === 'ar'
                                    ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                                    : (invoice.providerName || p?.name || invoice.providerNameAr);
                                })()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Invoice Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Invoice Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.admin.helps.selectInvoice')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Payment Slips Tab */}
              <TabsContent value="paymentSlips" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Payment Slips List */}
                  <div className="w-full md:w-80 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto max-h-[30vh] md:max-h-none">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                      {t('dashboard.admin.helps.paymentSlips')}
                    </h4>
                    {selectedRequest.paymentSlipUrls?.map((url, i) => (
                      <div
                        key={`slip-${i}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                        onClick={() => setPreviewUrl(url)}
                      >
                        <div className="flex gap-3">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{t('dashboard.admin.helps.paymentSlip')} {i + 1}</span>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <a
                              href={url}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download className="h-2.5 w-2.5" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                    {!selectedRequest.paymentSlipUrls?.length && (
                      <p className="text-sm text-muted-foreground">{t('dashboard.admin.helps.noPaymentSlips')}</p>
                    )}
                    
                    {/* Add Payment Slip Section */}
                    {!readOnly && selectedRequest.status === 'pending_invoices' && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                        <Label className="text-xs">{t('dashboard.admin.helps.addNewPaymentSlip')}</Label>
                        <div className="flex flex-col gap-2">
                          <Input
                            id="adminNewPaymentSlipFile"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setNewPaymentSlipFile(e.target.files?.[0] || null)}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('adminNewPaymentSlipFile')?.click()}
                          >
                            <Upload className="h-4 w-4 me-2" />
                            {t('common.chooseFile')}
                          </Button>
                          {newPaymentSlipFile && (
                            <span className="text-xs text-muted-foreground truncate">
                              {newPaymentSlipFile.name}
                            </span>
                          )}
                        </div>
                        {newPaymentSlipFile && (
                          <Button
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={handleAddPaymentSlipFromDetails}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                            {t('dashboard.admin.helps.uploadPaymentSlipBtn')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Payment Slip Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Payment Slip Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.admin.helps.selectPaymentSlip')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Updates Tab */}
              <TabsContent value="updates" className="max-h-[72vh] overflow-y-auto">
                <div className="space-y-4">
                  {/* Header with Add Button */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      {t('dashboard.admin.helps.updates.title')}
                    </h4>
                    {!readOnly && (
                      <Button onClick={() => setShowAddUpdate(true)} className="bg-primary">
                        <Plus className="h-4 w-4 me-2" />
                        {t('dashboard.admin.helps.updates.addUpdate')}
                      </Button>
                    )}
                  </div>

                  {/* Updates List */}
                  <div className="space-y-3">
                    {selectedRequest.updates && selectedRequest.updates.length > 0 ? (
                      [...selectedRequest.updates].reverse().map((update) => (
                        <div key={update.id} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{update.createdByName}</span>
                              <span className="text-xs text-muted-foreground">({update.createdByRole})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={STATUS_COLORS[update.statusAtTime]}>
                                {t(`dashboard.admin.helps.statuses.${update.statusAtTime}`)}
                              </Badge>
                              {!readOnly && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteUpdate(update.id)}
                                  disabled={isSubmitting}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm" dir={dir}>{update.content}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatLocalDate(update.createdAt instanceof Date ? update.createdAt : (update.createdAt as any)?.toDate?.() || new Date(update.createdAt), 'PPpp')}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        {t('dashboard.admin.helps.updates.noUpdates')}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Add Update Modal */}
              <Dialog open={showAddUpdate} onOpenChange={(open) => {
                setShowAddUpdate(open);
                if (!open) setNewUpdateText('');
              }}>
                <DialogContent className="max-w-lg" dir={dir}>
                  <DialogHeader>
                    <DialogTitle>{t('dashboard.admin.helps.updates.addUpdate')}</DialogTitle>
                    <DialogDescription>
                      {t('dashboard.admin.helps.updates.title')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{t('dashboard.admin.helps.updates.placeholder')}</Label>
                      <Textarea
                        value={newUpdateText}
                        onChange={(e) => setNewUpdateText(e.target.value)}
                        placeholder={t('dashboard.admin.helps.updates.placeholder')}
                        dir={dir}
                        rows={4}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddUpdate(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button 
                      onClick={handleAddUpdate} 
                      disabled={!newUpdateText.trim() || isSubmitting}
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                      {t('dashboard.admin.helps.updates.addBtn')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Case Modal */}
      <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
        <DialogContent className="max-w-lg" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.helps.reviewCase')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.helps.reviewDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.admin.helps.form.applicantName')}:</strong> {selectedRequest.applicantName}</p>
                <p><strong>{t('dashboard.admin.helps.form.condition')}:</strong> {selectedRequest.medicalCondition}</p>
                <p className="inline-flex items-center gap-1"><strong>{t('dashboard.admin.helps.form.cost')}:</strong> {getNetMedicalCost(selectedRequest).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="socialStatus"
                    checked={reviewData.socialStatusFits}
                    onCheckedChange={(checked) => setReviewData(prev => ({ ...prev, socialStatusFits: !!checked }))}
                  />
                  <Label htmlFor="socialStatus" className="cursor-pointer">
                    {t('dashboard.admin.helps.reviewForm.socialStatusFits')}
                  </Label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="medicalCondition"
                    checked={reviewData.medicalConditionFits}
                    onCheckedChange={(checked) => setReviewData(prev => ({ ...prev, medicalConditionFits: !!checked }))}
                  />
                  <Label htmlFor="medicalCondition" className="cursor-pointer">
                    {t('dashboard.admin.helps.reviewForm.medicalConditionFits')}
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewNotes">{t('dashboard.admin.helps.reviewForm.notes')}</Label>
                <Textarea
                  id="reviewNotes"
                  value={reviewData.coordinatorNotes}
                  onChange={(e) => setReviewData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                  dir={dir}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsReviewOpen(false)} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleReviewCase} 
                  disabled={isSubmitting}
                  className={reviewData.socialStatusFits && reviewData.medicalConditionFits 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'bg-red-600 hover:bg-red-700'
                  }
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {reviewData.socialStatusFits && reviewData.medicalConditionFits 
                    ? t('dashboard.admin.helps.reviewForm.sendForApproval')
                    : t('dashboard.admin.helps.reviewForm.reject')
                  }
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={isApprovalOpen} onOpenChange={setIsApprovalOpen}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {approvalData.decision === 'approve' 
                ? t('dashboard.admin.helps.approveCase')
                : t('dashboard.admin.helps.rejectCase')
              }
            </DialogTitle>
            <DialogDescription>
              {approvalData.decision === 'approve' 
                ? t('dashboard.admin.helps.approveDescription')
                : t('dashboard.admin.helps.rejectDescription')
              }
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.admin.helps.form.applicantName')}:</strong> {selectedRequest.applicantName}</p>
                <p><strong>{t('dashboard.admin.helps.form.condition')}:</strong> {selectedRequest.medicalCondition}</p>
                <p className="inline-flex items-center gap-1"><strong>{t('dashboard.admin.helps.form.cost')}:</strong> {getNetMedicalCost(selectedRequest).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></p>
              </div>

              {approvalData.decision === 'approve' && (
                <div className="space-y-2">
                  <Label htmlFor="approvedAmount">{t('dashboard.admin.helps.approvedAmount')} *</Label>
              <Input
                    id="approvedAmount"
                    type="number"
                    min="0"
                    value={approvalData.approvedAmount}
                    onChange={(e) => setApprovalData(prev => ({ ...prev, approvedAmount: e.target.value }))}
                    required
                    dir="ltr"
              />
            </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="adminNotes">{t('dashboard.admin.helps.adminNotes')}</Label>
              <Textarea
                  id="adminNotes"
                  value={approvalData.adminNotes}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, adminNotes: e.target.value }))}
                  dir={dir}
                  rows={3}
              />
            </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsApprovalOpen(false)} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={selectedRequest.status === 'pending' && approvalData.decision === 'approve' ? handleDirectApprove : handleApprovalDecision}
                  disabled={isSubmitting || (approvalData.decision === 'approve' && !approvalData.approvedAmount)}
                  className={approvalData.decision === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                  }
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {approvalData.decision === 'approve' 
                    ? t('dashboard.admin.helps.approve')
                    : t('dashboard.admin.helps.reject')
                  }
                </Button>
            </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* File Details/Edit Dialog */}
      <Dialog open={editingFileIndex !== null} onOpenChange={(open) => { if (!open) { setEditingFileIndex(null); setEditingFileType(null); setIsEditingInvoice(false); setIsEditingReport(false); setIsEditingProgressReport(false); } }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{isEditingInvoice ? t('dashboard.admin.helps.editInvoice') : (isEditingReport || isEditingProgressReport) ? t('dashboard.admin.helps.editReport') : t('common.fileDetails')}</DialogTitle>
          </DialogHeader>
          {selectedRequest && editingFileIndex !== null && editingFileType === 'report' && (() => {
            const report = selectedRequest.medicalReports?.[editingFileIndex];
            if (!report) return null;

            if (isEditingReport) {
              const selectedProvider = activeProviders.find(p => p.id === editingReportData.providerId);
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('dashboard.admin.helps.provider')} *</Label>
                      <Select
                        value={editingReportData.providerId}
                        onValueChange={(val) => {
                          const prov = activeProviders.find(p => p.id === val);
                          setEditingReportData(prev => ({ ...prev, providerId: val, department: '' }));
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.selectProvider')} /></SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedProvider && selectedProvider.departments.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-sm">{t('common.department')}</Label>
                        <Select
                          value={editingReportData.department}
                          onValueChange={(val) => setEditingReportData(prev => ({ ...prev, department: val }))}
                        >
                          <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.selectDepartment')} /></SelectTrigger>
                          <SelectContent>
                            {selectedProvider.departments.map((dept) => (
                              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.reportDate')}</Label>
                      <Input
                        type="date"
                        value={editingReportData.reportDate}
                        onChange={(e) => setEditingReportData(prev => ({ ...prev, reportDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <Button size="sm" onClick={handleUpdateReportMetadata} disabled={isSubmitting || !editingReportData.providerId}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Check className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingReport(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{report.name}</span>
                  </div>
                  {(report.providerName || report.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === report.providerId);
                        return language === 'ar'
                          ? (p?.nameAr || report.providerName)
                          : (report.providerName || p?.name);
                      })()}</span>
                    </div>
                  )}
                  {report.department && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.department')}</span>
                      <span className="text-sm font-medium">{report.department}</span>
                    </div>
                  )}
                  {report.reportDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.reportDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.reportDate))}</span>
                    </div>
                  )}
                  {report.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.uploadedAt))}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <a
                      href={report.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.download')}
                    </a>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dateStr = report.reportDate ? (() => { const d = new Date(report.reportDate); return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]; })() : '';
                        setEditingReportData({
                          providerId: report.providerId || '',
                          department: report.department || '',
                          reportDate: dateStr,
                        });
                        setIsEditingReport(true);
                      }}
                    >
                      <Pencil className="h-4 w-4 me-2" />
                      {t('common.edit')}
                    </Button>
                  </div>
                  {selectedRequest.status !== 'completed' && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        handleDeleteReport(editingFileIndex);
                        setEditingFileIndex(null);
                        setEditingFileType(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t('common.deleteFile')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          {selectedRequest && editingFileIndex !== null && editingFileType === 'progressReport' && (() => {
            const report = selectedRequest.progressReports?.[editingFileIndex];
            if (!report) return null;

            if (isEditingProgressReport) {
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('dashboard.admin.helps.provider')} *</Label>
                      <Select
                        value={editingProgressReportData.providerId}
                        onValueChange={(val) => setEditingProgressReportData(prev => ({ ...prev, providerId: val }))}
                      >
                        <SelectTrigger><SelectValue placeholder={t('dashboard.admin.helps.selectProvider')} /></SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.department')}</Label>
                      <Input
                        value={editingProgressReportData.department}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, department: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.reportDate')}</Label>
                      <Input
                        type="date"
                        value={editingProgressReportData.reportDate}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, reportDate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.progressReports.modal.notes')}</Label>
                      <Textarea
                        rows={3}
                        value={editingProgressReportData.notes}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <Button size="sm" onClick={handleUpdateProgressReportMetadata} disabled={isSubmitting || !editingProgressReportData.providerId}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Check className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingProgressReport(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{report.name}</span>
                  </div>
                  {(report.providerName || report.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === report.providerId);
                        return language === 'ar'
                          ? (p?.nameAr || report.providerName)
                          : (report.providerName || p?.name);
                      })()}</span>
                    </div>
                  )}
                  {report.department && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.department')}</span>
                      <span className="text-sm font-medium">{report.department}</span>
                    </div>
                  )}
                  {report.reportDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.reportDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.reportDate))}</span>
                    </div>
                  )}
                  {report.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.uploadedAt))}</span>
                    </div>
                  )}
                  {report.notes && (
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm text-muted-foreground">{t('common.progressReports.modal.notes')}</span>
                      <span className="text-sm font-medium text-end whitespace-pre-wrap">{report.notes}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <a
                      href={report.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.download')}
                    </a>
                    {['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const dateStr = report.reportDate ? (() => { const d = new Date(report.reportDate); return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]; })() : '';
                          setEditingProgressReportData({
                            providerId: report.providerId || '',
                            department: report.department || '',
                            reportDate: dateStr,
                            notes: report.notes || '',
                          });
                          setIsEditingProgressReport(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 me-2" />
                        {t('common.edit')}
                      </Button>
                    )}
                  </div>
                  {['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        handleDeleteProgressReport(editingFileIndex);
                        setEditingFileIndex(null);
                        setEditingFileType(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t('common.deleteFile')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          {selectedRequest && editingFileIndex !== null && editingFileType === 'invoice' && (() => {
            const invoice = selectedRequest.invoices?.[editingFileIndex];
            if (!invoice) return null;

            if (isEditingInvoice) {
              const nationalId = selectedRequest.idNumber || selectedRequest.nationalId;
              const vatApplies = nationalId ? !nationalId.startsWith('1') : false;
              const VAT_RATE = 0.15;
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>{t('dashboard.admin.helps.provider')} *</Label>
                      <Select
                        value={editingInvoiceData.providerId}
                        onValueChange={(val) => {
                          const provider = activeProviders.find(p => p.id === val);
                          setEditingInvoiceData(prev => ({
                            ...prev,
                            providerId: val,
                            providerName: provider?.name || '',
                            vatNumber: provider?.vatNumber || '',
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('dashboard.admin.helps.selectProvider')} />
                        </SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>{t('dashboard.admin.helps.vatNumber')}</Label>
                      <Input value={editingInvoiceData.vatNumber} readOnly disabled className="bg-muted" dir="ltr" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label>{t('common.invoiceDate')} *</Label>
                        <Input
                          type="date"
                          value={editingInvoiceData.invoiceDate}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>{t('common.invoiceNumber')} *</Label>
                        <Input
                          value={editingInvoiceData.invoiceNumber}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label>{t('common.totalBeforeDiscount')} *</Label>
                        <Input
                          type="number"
                          value={editingInvoiceData.totalBeforeDiscount}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, totalBeforeDiscount: e.target.value }))}
                          placeholder="0.00"
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>{t('common.totalAfterDiscount')} *</Label>
                        <Input
                          type="number"
                          value={editingInvoiceData.totalAfterDiscount}
                          onChange={(e) => {
                            const newTotal = e.target.value;
                            const totalN = Number(newTotal) || 0;
                            const clampedNonTax = Math.min(Number(editingInvoiceData.nonTaxableAmount) || 0, totalN);
                            const newTaxable = String(Math.max(0, totalN - clampedNonTax));
                            const tax = vatApplies ? ((Number(newTaxable) || 0) * VAT_RATE).toFixed(2) : '0';
                            setEditingInvoiceData(prev => ({ ...prev, totalAfterDiscount: newTotal, nonTaxableAmount: String(clampedNonTax), taxableAmount: newTaxable, taxAmount: tax }));
                          }}
                          placeholder="0.00"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    {vatApplies && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label>{t('common.nonTaxableAmount')}</Label>
                          <Input
                            type="number"
                            value={editingInvoiceData.nonTaxableAmount}
                            onChange={(e) => {
                              const total = Number(editingInvoiceData.totalAfterDiscount) || 0;
                              const clamped = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                              const newTaxable = String(Math.max(0, total - clamped));
                              const tax = ((Number(newTaxable) || 0) * VAT_RATE).toFixed(2);
                              setEditingInvoiceData(prev => ({ ...prev, nonTaxableAmount: String(clamped), taxableAmount: newTaxable, taxAmount: tax }));
                            }}
                            placeholder="0.00"
                            dir="ltr"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('common.taxableAmount')}</Label>
                          <Input
                            type="number"
                            value={editingInvoiceData.taxableAmount}
                            onChange={(e) => {
                              const total = Number(editingInvoiceData.totalAfterDiscount) || 0;
                              const clamped = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                              const newNonTax = String(Math.max(0, total - clamped));
                              const tax = (clamped * VAT_RATE).toFixed(2);
                              setEditingInvoiceData(prev => ({ ...prev, taxableAmount: String(clamped), nonTaxableAmount: newNonTax, taxAmount: tax }));
                            }}
                            placeholder="0.00"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label>{t('common.taxAmount')} {vatApplies ? '(15%)' : ''}</Label>
                      <Input type="number" value={editingInvoiceData.taxAmount} readOnly disabled className="bg-muted" dir="ltr" />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('common.grandTotal')}</Label>
                      <Input
                        type="number"
                        value={((Number(editingInvoiceData.totalAfterDiscount) || 0) + (Number(editingInvoiceData.taxAmount) || 0)).toFixed(2)}
                        readOnly
                        disabled
                        className="bg-muted font-medium"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsEditingInvoice(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleUpdateInvoiceMetadata}
                      disabled={isSubmitting || !editingInvoiceData.providerId || !editingInvoiceData.invoiceDate || !editingInvoiceData.invoiceNumber || !editingInvoiceData.totalBeforeDiscount || !editingInvoiceData.totalAfterDiscount}
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <CheckCircle className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.invoiceNumber')}</span>
                    <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{invoice.name}</span>
                  </div>
                  {(invoice.providerName || invoice.providerNameAr || invoice.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === invoice.providerId);
                        return language === 'ar'
                          ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                          : (invoice.providerName || p?.name || invoice.providerNameAr);
                      })()}</span>
                    </div>
                  )}
                  {invoice.invoiceDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.invoiceDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(invoice.invoiceDate))}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.totalBeforeDiscount')}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.totalBeforeDiscount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.totalAfterDiscount')}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.totalAfterDiscount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                  </div>
                  {invoice.taxableAmount != null && invoice.taxableAmount !== invoice.totalAfterDiscount && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.taxableAmount')}</span>
                      <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.taxableAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                    </div>
                  )}
                  {invoice.taxAmount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.taxAmount')}</span>
                      <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.taxAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                    </div>
                  )}
                  {invoice.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(invoice.uploadedAt))}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <a
                      href={invoice.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.download')}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Edit / Delete are always available so an admin can
                        correct invoice figures even after the case is
                        completed. */}
                    {true && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const dateStr = invoice.invoiceDate
                              ? new Date(invoice.invoiceDate).toISOString().split('T')[0]
                              : '';
                            setEditingInvoiceData({
                              providerId: invoice.providerId || '',
                              providerName: invoice.providerName || '',
                              vatNumber: invoice.vatNumber || '',
                              invoiceDate: dateStr,
                              invoiceNumber: invoice.invoiceNumber || '',
                              totalBeforeDiscount: String(invoice.totalBeforeDiscount || ''),
                              totalAfterDiscount: String(invoice.totalAfterDiscount || ''),
                              taxableAmount: String(invoice.taxableAmount ?? invoice.totalAfterDiscount ?? ''),
                              nonTaxableAmount: String((invoice as any).nonTaxableAmount ?? Math.max(0, (invoice.totalAfterDiscount || 0) - (invoice.taxableAmount ?? invoice.totalAfterDiscount ?? 0))),
                              taxAmount: String(invoice.taxAmount || ''),
                            });
                            setIsEditingInvoice(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 me-2" />
                          {t('common.edit')}
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            handleDeleteInvoice(editingFileIndex);
                            setEditingFileIndex(null);
                            setEditingFileType(null);
                          }}
                        >
                          <Trash2 className="h-4 w-4 me-2" />
                          {t('common.deleteFile')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Modal */}
      <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
        <DialogContent className="w-[95vw] max-w-2xl" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.helps.uploadInvoices')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.helps.uploadInvoicesDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.admin.helps.form.nameArabic')}:</strong> {selectedRequest.nameArabic || selectedRequest.applicantName}</p>
                <p className="inline-flex items-center gap-1"><strong>{t('dashboard.admin.helps.approvedAmount')}:</strong> {(selectedRequest.approvedAmount || selectedRequest.cost)?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></p>
            </div>

              <div className="space-y-2">
                <Label>{t('dashboard.admin.helps.treatmentInvoices')}</Label>
                <InvoiceUploader
                  invoices={pendingInvoices}
                  onInvoicesChange={setPendingInvoices}
                  translationPrefix="dashboard.admin.helps"
                  nationalId={selectedRequest?.nationalId}
                />
                </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setIsInvoiceOpen(false); setPendingInvoices([]); }} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleUploadInvoices} 
                  disabled={isSubmitting || pendingInvoices.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {t('dashboard.admin.helps.uploadInvoicesBtn')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Slips Upload Modal */}
      <Dialog open={isPaymentSlipOpen} onOpenChange={setIsPaymentSlipOpen}>
        <DialogContent className="max-w-lg" dir={dir}>
          <DialogHeader dir={dir} className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            <DialogTitle>{t('dashboard.admin.helps.uploadPaymentSlipsTitle')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.helps.uploadPaymentSlipsDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedRequest.applicantName}</p>
                <p className="text-sm text-muted-foreground">{selectedRequest.nationalId}</p>
              </div>
              
              <div className="space-y-2">
                <Label>{t('dashboard.admin.helps.selectPaymentSlipsLabel')}</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => setPaymentSlipFiles(Array.from(e.target.files || []))}
                />
                {paymentSlipFiles.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {paymentSlipFiles.length} {t('dashboard.admin.helps.filesSelected')}
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsPaymentSlipOpen(false)} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleUploadPaymentSlips} 
                  disabled={isSubmitting || paymentSlipFiles.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {t('dashboard.admin.helps.uploadPaymentSlipsBtn')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Case Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" dir={dir}>
          <div className="px-6 pt-6 pb-4 border-b shrink-0 bg-gray-50">
            <DialogHeader>
              <DialogTitle>{t('dashboard.admin.helps.editCase')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.admin.helps.editCaseDescription')}
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleEditCase} className="flex flex-col min-h-0 flex-1">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Card 1 - Personal Info */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.personalInfo')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-nameArabic">{t('dashboard.admin.helps.form.nameArabic')}</Label>
                    <Input id="edit-nameArabic" value={formData.nameArabic} onChange={(e) => setFormData(prev => ({ ...prev, nameArabic: e.target.value }))} dir="rtl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-nameEnglish">{t('dashboard.admin.helps.form.nameEnglish')}</Label>
                    <Input id="edit-nameEnglish" value={formData.nameEnglish} onChange={(e) => setFormData(prev => ({ ...prev, nameEnglish: e.target.value }))} dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-idNumber">{t('dashboard.admin.helps.form.idNumber')} *</Label>
                    <Input id="edit-idNumber" value={formData.idNumber} onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))} required dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-nationality">{t('dashboard.admin.helps.form.nationality')}</Label>
                    <Input id="edit-nationality" value={formData.nationality} onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))} dir={dir} />
                  </div>
                </div>
              </div>

              {/* Card 2 - Contact */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.contact')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-phoneNumber">{t('dashboard.admin.helps.form.phoneNumber')} *</Label>
                    <Input id="edit-phoneNumber" value={formData.phoneNumber} onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))} placeholder="05XXXXXXXX" required dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-relativeMobileNumber">{t('dashboard.admin.helps.form.relativeMobileNumber')}</Label>
                    <Input id="edit-relativeMobileNumber" value={formData.relativeMobileNumber} onChange={(e) => setFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))} placeholder="05XXXXXXXX" dir="ltr" />
                  </div>
                </div>
              </div>

              {/* Card 3 - Medical */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.medical')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-medicalCondition">{t('dashboard.admin.helps.form.condition')}</Label>
                    <Textarea id="edit-medicalCondition" value={formData.medicalCondition} onChange={(e) => setFormData(prev => ({ ...prev, medicalCondition: e.target.value }))} dir={dir} rows={3} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-requiredTreatment">{t('dashboard.admin.helps.form.treatment')}</Label>
                    <Textarea id="edit-requiredTreatment" value={formData.requiredTreatment} onChange={(e) => setFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))} dir={dir} rows={3} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-cost">{t('dashboard.admin.helps.form.cost')}</Label>
                    <Input id="edit-cost" type="number" min="0" value={formData.cost} onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))} dir="ltr" />
                  </div>
                </div>
              </div>

              {/* Card 4 - Reports */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('dashboard.admin.helps.existingReports')}</h4>
                {selectedRequest && (selectedRequest.medicalReports?.length || selectedRequest.medicalReportUrl) && (
                  <div className="space-y-1">
                    {selectedRequest.medicalReports?.map((report, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <a href={report.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 text-sm">
                          <FileText className="h-3 w-3" />
                          {report.name}
                        </a>
                        <a
                          href={report.url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md border text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </div>
                    ))}
                    {selectedRequest.medicalReportUrl && !selectedRequest.medicalReports?.length && (
                      <div className="flex items-center justify-between gap-2">
                        <a href={selectedRequest.medicalReportUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 text-sm">
                          <FileText className="h-3 w-3" />
                          {t('dashboard.admin.helps.viewReport')}
                        </a>
                        <a
                          href={selectedRequest.medicalReportUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md border text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.addNewReport')}</Label>
                  <MedicalReportUploader reports={pendingReports} onReportsChange={setPendingReports} translationPrefix="dashboard.admin.helps.form" />
                </div>
              </div>

              {/* Card 5 - Notes */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.caseStatus')}</h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-coordinatorNotes">{t('dashboard.admin.helps.form.notes')}</Label>
                  <Textarea id="edit-coordinatorNotes" value={formData.coordinatorNotes} onChange={(e) => setFormData(prev => ({ ...prev, coordinatorNotes: e.target.value }))} dir={dir} rows={2} />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0 bg-gray-50">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('dashboard.admin.helps.saveChanges')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Reports Modal */}
      <Dialog open={isAddReportsOpen} onOpenChange={setIsAddReportsOpen}>
        <DialogContent className="max-w-lg" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.helps.addReportsTitle')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.helps.addReportsDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.admin.helps.form.applicantName')}:</strong> {selectedRequest.applicantName}</p>
                <p><strong>{t('dashboard.admin.helps.form.nationalId')}:</strong> {selectedRequest.nationalId}</p>
              </div>

              {/* Existing reports */}
              {(selectedRequest.medicalReports?.length || selectedRequest.medicalReportUrl) && (
                <div className="space-y-2">
                  <Label>{t('dashboard.admin.helps.existingReports')}</Label>
                  <div className="space-y-1 p-3 bg-muted/50 rounded-md">
                    {selectedRequest.medicalReports?.map((report, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <a href={report.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 text-sm min-w-0">
                          <FileText className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{report.name}</span>
                          {report.uploadedAt && (
                            <span className="text-muted-foreground text-xs ml-2 flex-shrink-0">
                              ({formatLocalDate(new Date(report.uploadedAt))})
                            </span>
                          )}
                        </a>
                        <a
                          href={report.url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md border text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </div>
                    ))}
                    {selectedRequest.medicalReportUrl && !selectedRequest.medicalReports?.length && (
                      <div className="flex items-center justify-between gap-2">
                        <a href={selectedRequest.medicalReportUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 text-sm">
                          <FileText className="h-3 w-3" />
                          {t('dashboard.admin.helps.viewReport')}
                        </a>
                        <a
                          href={selectedRequest.medicalReportUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md border text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="newReports">{t('dashboard.admin.helps.selectNewReports')}</Label>
                <div className="flex flex-col gap-2">
                  <Input
                    id="newReports"
                    type="file"
                    multiple
                    onChange={(e) => setNewReportFiles(Array.from(e.target.files || []))}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('newReports')?.click()}
                  >
                    <Upload className="h-4 w-4 me-2" />
                    {t('common.chooseFiles')}
                  </Button>
                  {newReportFiles.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-sm text-muted-foreground">
                        {newReportFiles.length} {t('common.filesSelected')}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {newReportFiles.map((file, i) => (
                          <Badge key={i} variant="secondary">
                            <FileText className="h-3 w-3 me-1" />
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setIsAddReportsOpen(false); setNewReportFiles([]); }} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleAddReports} 
                  disabled={isSubmitting || newReportFiles.length === 0}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus className="mr-2 h-4 w-4" />}
                  {t('dashboard.admin.helps.uploadReports')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Monthly Cost Report Dialog */}
      <Dialog
        open={isMonthlyCostOpen}
        onOpenChange={(open) => {
          setIsMonthlyCostOpen(open);
          if (open && monthlyReportYears.length > 0 && !monthlyReportYears.includes(monthlyReportYear)) {
            setMonthlyReportYear(monthlyReportYears[monthlyReportYears.length - 1]);
          }
        }}
      >
        <DialogContent className="max-w-2xl" dir={dir}>
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>{t('dashboard.admin.helps.monthlyCostReport.title')}</DialogTitle>
              {monthlyCostRows.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setMonthlyReportYear((y) => y - 1)}
                    disabled={monthlyReportYears.length === 0 || monthlyReportYear <= monthlyReportYears[0]}
                    aria-label="previous year"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-mono text-base font-semibold tabular-nums w-14 text-center">
                    {monthlyReportYear}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setMonthlyReportYear((y) => y + 1)}
                    disabled={monthlyReportYears.length === 0 || monthlyReportYear >= monthlyReportYears[monthlyReportYears.length - 1]}
                    aria-label="next year"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <DialogDescription>{t('dashboard.admin.helps.monthlyCostReport.description')}</DialogDescription>
          </DialogHeader>
          {monthlyCostRows.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {t('dashboard.admin.helps.monthlyCostReport.noCases')}
            </p>
          ) : (
            <>
              <div className="max-h-[69vh] overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.admin.helps.monthlyCostReport.monthColumn')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.monthlyCostReport.caseCountColumn')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.monthlyCostReport.totalCostColumn')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.monthlyCostReport.invoicesColumn')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMonthlyRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                          {t('dashboard.admin.helps.monthlyCostReport.noCases')}
                        </TableCell>
                      </TableRow>
                    ) : filteredMonthlyRows.map(row => (
                      <TableRow key={row.key}>
                        <TableCell className="font-medium">{row.label}</TableCell>
                        <TableCell className="text-end font-mono">{row.count.toLocaleString('en-US')}</TableCell>
                        <TableCell className="text-end font-mono">{row.total.toLocaleString('en-US')}</TableCell>
                        <TableCell className="text-end font-mono">{row.invoices.toLocaleString('en-US')}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/40 font-semibold">
                      <TableCell>{t('dashboard.admin.helps.monthlyCostReport.grandTotal')}</TableCell>
                      <TableCell className="text-end font-mono">{filteredMonthlyTotalCount.toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-end font-mono">{filteredMonthlyTotalCost.toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-end font-mono">{filteredMonthlyTotalInvoices.toLocaleString('en-US')}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader dir={dir} className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('dashboard.admin.helps.settings.title')}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={settingsTab} onValueChange={setSettingsTab} className="w-full">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-4">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t('dashboard.admin.helps.settings.generalTab')}
              </TabsTrigger>
              <TabsTrigger value="classifications" className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {t('dashboard.admin.helps.settings.classificationsTab')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="mt-0">
              <div className="space-y-6 py-4">
                {settingsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">
                        {t('dashboard.admin.helps.settings.autoApprove')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.admin.helps.settings.autoApproveDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={autoApproveEnabled}
                      onCheckedChange={handleAutoApproveToggle}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">
                        {t('dashboard.admin.helps.settings.coordCanDeleteAssignments')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.admin.helps.settings.coordCanDeleteAssignmentsDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={coordCanDeleteAssignments}
                      onCheckedChange={handleCoordCanDeleteAssignmentsToggle}
                    />
                  </div>

                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">
                        {t('dashboard.admin.helps.settings.marketingFormula')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.admin.helps.settings.marketingFormulaDesc')}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground pt-1" dir="ltr">
                        S = S + (S × X / Y)
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="marketingX">{t('dashboard.admin.helps.settings.marketingX')}</Label>
                        <Input
                          id="marketingX"
                          type="number"
                          step="0.01"
                          min="0"
                          value={marketingX}
                          onChange={(e) => setMarketingX(parseFloat(e.target.value) || 0)}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="marketingY">{t('dashboard.admin.helps.settings.marketingY')}</Label>
                        <Input
                          id="marketingY"
                          type="number"
                          step="0.01"
                          min="0"
                          value={marketingY}
                          onChange={(e) => setMarketingY(parseFloat(e.target.value) || 0)}
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.admin.helps.settings.marketingPreview')}: <span className="font-semibold text-foreground" dir="ltr">100 → {calcCostWithMarketing(100).toLocaleString('en-US')}</span>
                      </p>
                      <Button size="sm" onClick={() => handleMarketingSave(marketingX, marketingY)}>
                        {t('common.save')}
                      </Button>
                    </div>
                  </div>
                  {/* Data Migration Section */}
                  <div className="p-4 border rounded-lg border-orange-200 bg-orange-50/50 space-y-4">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">Data Migration</Label>
                      <p className="text-sm text-muted-foreground">
                        Verify and fix link data: ensure all holder entries have actualReceived and case entries have amount fields.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isMigrating}
                        onClick={async () => {
                          setIsMigrating(true);
                          setMigrationLog([]);
                          try {
                            const result = await verifyLinkData();
                            const logs: string[] = [];
                            logs.push(`Documents: ${result.holderLinks.length} holder links, ${result.caseEntries.length} case entries`);
                            logs.push(`Total received (actualReceived || platformCost): ${result.totalReceived.toLocaleString()} SAR`);
                            logs.push(`Excel reference: 1,644,151.28 SAR (147 links)`);
                            logs.push(`Difference: ${(result.totalReceived - 1644151.28).toLocaleString()} SAR | Links diff: ${result.holderLinks.length - 147}`);
                            logs.push('---');
                            for (const [p, data] of Object.entries(result.byPlatform)) {
                              logs.push(`${p}: ${data.count} links, ${data.total.toLocaleString()} SAR`);
                            }
                            logs.push('---');
                            logs.push(`Holder entries missing actualReceived: ${result.missingActualReceived.length}`);
                            logs.push(`Case entries missing amount: ${result.missingAmount.length}`);
                            if (result.missingActualReceived.length > 0) {
                              for (const l of result.missingActualReceived.slice(0, 5)) {
                                logs.push(`  → ${l.platform}/${l.caseNumber}: platformCost=${l.platformCost}`);
                              }
                            }
                            setMigrationLog(logs);
                          } catch (err: any) {
                            setMigrationLog([`Error: ${err.message}`]);
                          }
                          setIsMigrating(false);
                        }}
                      >
                        {isMigrating ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                        Verify Data
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isMigrating}
                        onClick={async () => {
                          setIsMigrating(true);
                          setMigrationLog(prev => [...prev, '--- DRY RUN ---']);
                          try {
                            const result = await migrateLinkData(true);
                            setMigrationLog(prev => [...prev, `Would update ${result.updatedDocs} docs, ${result.updatedEntries} entries`]);
                          } catch (err: any) {
                            setMigrationLog(prev => [...prev, `Error: ${err.message}`]);
                          }
                          setIsMigrating(false);
                        }}
                      >
                        Dry Run
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={isMigrating}
                        onClick={async () => {
                          if (!confirm('This will update Firestore data. Continue?')) return;
                          setIsMigrating(true);
                          setMigrationLog(prev => [...prev, '--- MIGRATING (LIVE) ---']);
                          try {
                            const result = await migrateLinkData(false);
                            setMigrationLog(prev => [...prev, `Updated ${result.updatedDocs} docs, ${result.updatedEntries} entries`]);
                            toast.success(`Migration complete: ${result.updatedDocs} docs updated`);
                          } catch (err: any) {
                            setMigrationLog(prev => [...prev, `Error: ${err.message}`]);
                            toast.error('Migration failed');
                          }
                          setIsMigrating(false);
                        }}
                      >
                        Migrate (Live)
                      </Button>
                    </div>
                    {migrationLog.length > 0 && (
                      <pre className="text-xs bg-black text-green-400 p-3 rounded-md overflow-auto max-h-64 font-mono" dir="ltr">
                        {migrationLog.join('\n')}
                      </pre>
                    )}
                  </div>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="classifications" className="mt-0">
              <div className="space-y-4">
                <div className="flex justify-start">
                  <Button onClick={openAddCategoryDialog}>
                    <Plus className="h-4 w-4 me-2" />
                    {t('dashboard.admin.helps.settings.categories.addCategory')}
                  </Button>
                </div>

                {categories.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    {t('dashboard.admin.helps.settings.categories.noCategories')}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table dir={dir}>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-start">{t('dashboard.admin.helps.settings.categories.tableName')}</TableHead>
                          <TableHead className="text-start">{t('dashboard.admin.helps.settings.categories.description')}</TableHead>
                          <TableHead className="text-start">{t('dashboard.admin.helps.settings.categories.status')}</TableHead>
                          <TableHead className="text-end">{t('dashboard.admin.helps.settings.categories.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell className="text-start">
                              <div className="font-medium">
                                {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                              </div>
                            </TableCell>
                            <TableCell className="text-start max-w-xs truncate">
                              {category.description || '-'}
                            </TableCell>
                            <TableCell className="text-start">
                              <Badge variant={category.isActive ? 'default' : 'secondary'}>
                                {category.isActive 
                                  ? t('dashboard.admin.helps.settings.categories.active') 
                                  : t('dashboard.admin.helps.settings.categories.inactive')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-end">
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="ghost" size="icon" onClick={() => openEditCategoryDialog(category)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(category)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={isCategoryEditOpen} onOpenChange={(open) => { setIsCategoryEditOpen(open); if (!open) resetCategoryForm(); }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle>
              {editingCategory 
                ? t('dashboard.admin.helps.settings.categories.editCategory') 
                : t('dashboard.admin.helps.settings.categories.addCategory')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="adminCategoryName">{t('dashboard.admin.helps.settings.categories.name')} *</Label>
                <Input
                  id="adminCategoryName"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('dashboard.admin.helps.settings.categories.namePlaceholder')}
                  required
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adminCategoryNameAr">{t('dashboard.admin.helps.settings.categories.nameAr')} *</Label>
                <Input
                  id="adminCategoryNameAr"
                  value={categoryFormData.nameAr}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, nameAr: e.target.value }))}
                  placeholder={t('dashboard.admin.helps.settings.categories.nameArPlaceholder')}
                  required
                  dir="rtl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminCategoryDescription">{t('dashboard.admin.helps.settings.categories.description')}</Label>
              <Textarea
                id="adminCategoryDescription"
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard.admin.helps.settings.categories.descriptionPlaceholder')}
                rows={2}
                dir={dir}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="adminCategoryIsActive"
                checked={categoryFormData.isActive}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="adminCategoryIsActive" className="cursor-pointer">
                {t('dashboard.admin.helps.settings.categories.isActive')}
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCategoryEditOpen(false)} disabled={isCategorySubmitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isCategorySubmitting}>
                {isCategorySubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {editingCategory ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fullscreen ID Document Preview */}
      {idPreviewUrl && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90"
          style={{ pointerEvents: 'auto', zIndex: 99999, isolation: 'isolate' }}
          onClick={isCropping ? undefined : () => { setIdPreviewUrl(null); setIdPreviewRotation(0); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { if (isCropping) { setIsCropping(false); setCropRect(null); } else { setIdPreviewUrl(null); setIdPreviewRotation(0); } } }}
          tabIndex={0}
          ref={(el) => { if (el && !el.dataset.focused) { el.dataset.focused = 'true'; el.focus(); } }}
        >
          <button
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); if (isCropping) { setIsCropping(false); setCropRect(null); } else { setIdPreviewUrl(null); setIdPreviewRotation(0); } }}
          >
            <X className="w-6 h-6" />
          </button>
          {!readOnly && !isPdfUrl(idPreviewUrl) && (
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
              {idPreviewRotation !== 0 && !isCropping && (
                <button
                  className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
                  onClick={(e) => { e.stopPropagation(); handleSaveRotation(); }}
                  disabled={isSavingRotation}
                >
                  {isSavingRotation ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {t('common.saveRotation')}
                </button>
              )}
              {isCropping && cropRect && cropRect.w > 1 && cropRect.h > 1 && (
                <>
                  <button
                    className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    onClick={(e) => { e.stopPropagation(); handleSaveCrop(); }}
                    disabled={isSavingRotation}
                  >
                    {isSavingRotation ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {t('common.saveCrop')}
                  </button>
                  <button
                    className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsCropping(false); setCropRect(null); }}
                  >
                    <XCircle className="w-4 h-4" />
                    {t('common.cancelCrop')}
                  </button>
                </>
              )}
              {!isCropping && (
                <>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIdPreviewRotation(prev => (prev + 90) % 360); }}
                    disabled={isSavingRotation}
                    title={t('common.rotateRight')}
                  >
                    <RotateCw className="w-5 h-5" />
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIdPreviewRotation(prev => (prev + 270) % 360); }}
                    disabled={isSavingRotation}
                    title={t('common.rotateLeft')}
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-white/20 mx-1" />
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsCropping(true); setIdPreviewRotation(0); }}
                    disabled={isSavingRotation}
                    title={t('common.crop')}
                  >
                    <Crop className="w-5 h-5" />
                  </button>
                </>
              )}
              {isCropping && !cropRect && (
                <span className="text-white/70 text-sm select-none">{t('common.crop')}: drag to select</span>
              )}
            </div>
          )}
          {!isCropping && (
            <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
              <a
                href={idPreviewUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                onClick={(e) => e.stopPropagation()}
                title={t('common.download')}
              >
                <Download className="w-5 h-5" />
              </a>
              <a
                href={idPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                onClick={(e) => e.stopPropagation()}
                title={t('common.openInNewTab')}
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          )}
          {isPdfUrl(idPreviewUrl) ? (
            <iframe
              src={idPreviewUrl}
              className="w-[90vw] h-[90vh] rounded-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          ) : isCropping ? (
            <div
              ref={cropContainerRef}
              className="relative select-none"
              style={{ maxWidth: '95vw', maxHeight: '95vh' }}
              onPointerDown={onCropPointerDown}
              onPointerMove={onCropPointerMove}
              onPointerUp={onCropPointerUp}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={idPreviewUrl} className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl" draggable={false} />
              {cropRect && (
                <>
                  <div className="absolute left-0 right-0 top-0 bg-black/60 pointer-events-none" style={{ height: `${cropRect.y}%` }} />
                  <div className="absolute left-0 right-0 bottom-0 bg-black/60 pointer-events-none" style={{ height: `${100 - cropRect.y - cropRect.h}%` }} />
                  <div className="absolute left-0 bg-black/60 pointer-events-none" style={{ top: `${cropRect.y}%`, height: `${cropRect.h}%`, width: `${cropRect.x}%` }} />
                  <div className="absolute right-0 bg-black/60 pointer-events-none" style={{ top: `${cropRect.y}%`, height: `${cropRect.h}%`, width: `${100 - cropRect.x - cropRect.w}%` }} />
                  <div className="absolute border-2 border-white/90 pointer-events-none" style={{ left: `${cropRect.x}%`, top: `${cropRect.y}%`, width: `${cropRect.w}%`, height: `${cropRect.h}%` }}>
                    <div className="absolute inset-0">
                      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                      <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                    </div>
                  </div>
                  {['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'].map(h => (
                    <div key={h} className="bg-white rounded-full shadow-md border border-gray-300 z-10" style={getCropHandleStyle(h, cropRect)} onPointerDown={(e) => onCropHandleDown(h, e)} />
                  ))}
                </>
              )}
            </div>
          ) : (
            <img
              src={idPreviewUrl}
              className="max-w-[95vw] max-h-[95vh] object-contain select-none rounded-lg shadow-2xl transition-transform duration-300"
              style={{ transform: `rotate(${idPreviewRotation}deg)` }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>,
        document.body
      )}
      {/* AI Allocation Dialog */}
      <Dialog open={showAiAllocation} onOpenChange={setShowAiAllocation}>
        <DialogContent className="max-w-5xl max-h-[80vh] flex flex-col overflow-hidden" dir={dir}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('dashboard.admin.helps.aiAllocate')}
            </DialogTitle>
            <DialogDescription>
              {isAiLoading
                ? t('dashboard.admin.helps.aiAllocateLoading')
                : aiAllocations.length > 0
                  ? `${aiAllocations.length} ${t('dashboard.admin.helps.aiAllocateFound')} — ${Math.round(aiAllocations.reduce((s, a) => s + a.amount, 0)).toLocaleString('en-US')} ${t('dashboard.admin.helps.currency')}`
                  : t('dashboard.admin.helps.aiAllocateNone')}
            </DialogDescription>
          </DialogHeader>
          {isAiLoading && (
            <div className="flex-1 min-h-0 flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {!isAiLoading && aiAllocations.length > 0 && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-end">#</TableHead>
                    <TableHead className="text-end">{t('dashboard.admin.helps.aiTable.link')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.admin.helps.aiTable.category')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.admin.helps.aiTable.amount')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.admin.helps.aiTable.case')}</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiAllocations.map((alloc, idx) => {
                    const link = allLinks.find(l => l._key === alloc.linkKey);
                    const caseReq = requests.find(r => r.id === alloc.caseId);
                    const caseCost = caseReq ? getNetMedicalCost(caseReq) : 0;
                    const caseAge = (() => {
                      if (!caseReq) return null;
                      const dob = (caseReq as any).dateOfBirth;
                      if (!dob) return (caseReq as any).age ?? null;
                      const d = dob instanceof Date ? dob : dob.toDate ? dob.toDate() : new Date(dob);
                      if (isNaN(d.getTime())) return (caseReq as any).age ?? null;
                      const now = new Date();
                      let a = now.getFullYear() - d.getFullYear();
                      if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a--;
                      return a;
                    })();
                    return (
                    <TableRow key={idx}>
                      <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="text-end text-xs">
                        <div>{alloc.linkTitle}</div>
                        {link && <div className="text-muted-foreground">({t('dashboard.admin.helps.linksView.remaining')}: {link.remaining.toLocaleString('en-US')})</div>}
                      </TableCell>
                      <TableCell className="text-end text-xs">{(() => { const cat = activeCategories.find(c => c.id === alloc.treatmentCategoryId); return cat ? (dir === 'rtl' ? cat.nameAr : cat.name) : '—'; })()}</TableCell>
                      <TableCell className="text-end font-medium">{Math.round(alloc.amount).toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-end text-xs">
                        <div>{alloc.caseName}{caseAge !== null ? ` (${caseAge})` : ''}</div>
                        {caseReq && <div className="text-muted-foreground">({t('dashboard.admin.helps.totalInvoices')}: {caseCost.toLocaleString('en-US')})</div>}
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => setAiAllocations(prev => prev.filter((_, i) => i !== idx))}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="outline" onClick={() => setShowAiAllocation(false)}>
              {t('dashboard.admin.helps.cancel')}
            </Button>
            <Button variant="outline" onClick={handleAiAllocate} disabled={isAiLoading} className="gap-1.5">
              {isAiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t('dashboard.admin.helps.aiAllocateRetry')}
            </Button>
            {aiAllocations.length > 0 && (
              <Button onClick={handleApplyAiAllocations} disabled={isApplyingAi} className="gap-1.5">
                {isApplyingAi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {t('dashboard.admin.helps.aiAllocateApply')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start-treatment confirm prompt (shown after the first progress report upload) */}
      <Dialog open={!!startPromptRequest} onOpenChange={(open) => { if (!open) setStartPromptRequest(null); }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.helps.startTreatmentPromptTitle')}</DialogTitle>
            <DialogDescription>{t('dashboard.admin.helps.startTreatmentPromptDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartPromptRequest(null)}>
              {t('dashboard.admin.helps.startTreatmentPromptCancel')}
            </Button>
            <Button onClick={async () => {
              const req = startPromptRequest;
              setStartPromptRequest(null);
              if (req) await handleStartTreatment(req);
            }}>
              {t('dashboard.admin.helps.startTreatmentPromptConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
